// ============================================================================
// Entity Screening (Saved Work) ticket report — PDF builder
// ----------------------------------------------------------------------------
// Generates a clean, presentable report for a saved entity-screening ticket
// using PDFKit (browser standalone build), styled after the ACE Analyzer
// findings report (see automations/src/utils/AnalyzerUtils/BitePdfBuilder.ts).
//
// Sections: cover, Risk Evaluation & AI Results (incl. saved Beneficial
// Ownership evaluation), Top 25 Matches by match score, and Top 25 Ownership
// matches by match score.
//
// We embed Open Sans (Regular + Bold) so non-Latin names (Cyrillic, Czech
// diacritics, etc.) render correctly. fontkit (bundled in the standalone build)
// measures every glyph, so column text wraps within its cell instead of being
// mismeasured by the built-in Helvetica and bleeding into the next column.
// ============================================================================

import PDFDocument from 'pdfkit/js/pdfkit.standalone';
import biteHeaderDark from '../../resources/img/biteHeaderDark.png';
import { getEntityMatchScore } from 'utils/nameMatchScore';
import { reflowIntoParagraphs } from 'utils/reflowParagraphs';
import sanitizeModelText from 'utils/sanitizeModelText';
import { formatLicenseExceptions } from 'utils/formatLicenseExceptions';
import {
  ASSESSMENT_PHASE_LABELS,
  ExportAssessmentData,
  NOT_ASSESSED_LABEL,
  NOT_ASSESSED_NOTICE,
  combinedNuclearHeadline,
  isPhaseNotAssessed,
  isPhaseNotRequested,
  notAssessedPhases,
  notAssessedSummaryText,
} from 'utils/exportAssessment';
import type { ExportAssessmentPhase } from 'interfaces/iStreamedResponse';
import {
  dispositionLabel,
  jurisdictionNeedsManualReview,
  coverageStatusLabel,
  coverageStatusPlain,
} from 'components/Classify/JurisdictionDispositionBadge';
import type { IOwnershipGraph } from 'utils/beneficialOwnershipGraph';
import { sameNameNote } from 'utils/beneficialOwnershipGraph';
import { buildBeneficialOwnershipPdfTree } from 'utils/beneficialOwnershipPdfTree';
import type { EntityRiskReport } from 'components/BiteDB/Components/entityRiskReport';
import type {
  IBeneficialOwnershipPdfTreeGroup,
  IBeneficialOwnershipPdfTreeRow,
} from 'utils/beneficialOwnershipPdfTree';
// Type-only: DesignTicketPayload is already the exact flattened shape the Design Assistant
// ticket renders from (components/ExportDesign/exportDesignTicketPayload.ts's readDesignTicket),
// so it's reused directly as this report's data type below instead of adding a parallel
// interface + buildXReportData() mapper like every other docType needs. `import type` keeps
// this a compile-time-only reference — ExportDesignTicket.tsx importing the PDF function back
// would otherwise be a runtime circular import.
import type { DesignTicketPayload } from 'components/ExportDesign/ExportDesignTicket';
import { EEI_VALUE_EXEMPTION_NOTE, showEeiValueExemptionNote } from 'utils/eeiValueNote';
import { buildScopedReviewRows, NOT_REQUESTED_SECTION_TEXT } from 'utils/scopedReviewCaveat';
import { scheduleBCodeRows } from 'utils/scheduleBCodeResult';
import type { ImportBan, BanGateAnswer } from 'utils/importBans';
import {
  importBanGateAnswer,
  importBanResultRow,
  findActiveImportBan,
} from 'utils/importBans';
import type { AesRunDetails, AesLine, AesFinding } from 'api/AesAnalyzerAPI';
import { usmlDisplay } from 'components/Analyzers/aesFormat';

const COLOR = {
  primary: '#4895EF',
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#64748B',
  divider: '#E2E8F0',
  cardBg: '#F8FAFC',
  cardBorder: '#E2E8F0',
  tableHeaderBg: '#4895EF',
  tableHeaderText: '#FFFFFF',
  riskHigh: '#F04438',
  riskMedium: '#FDB022',
  riskLow: '#12B76A',
};

const PAGE_MARGIN = 56;
const MAX_ROWS = 25;
const CLASSIFICATION_DISCLAIMER =
  'The 10-digit HTS classification provided is for general, educational and planning purposes only and not for purposes of filing and entry. Unless you are the importer of record, the specific tariff classification to be applied to a specific entry of merchandise is to be determined, supervised and controlled by the licensed customs broker. Users other than the importer of record must consult a licensed customs broker as classification for Customs entry purposes can only be determined by a licensed customs broker.';
const TARIFF_DISCLAIMER =
  'Users are responsible for manually verifying product scope, effective dates, list numbers, applicable exemptions, and FTAs. BITE outputs are an unofficial opinion of the BITE Data Corporation. It is intended to assist in identifying potential tariff implications and should be used alongside a comprehensive compliance review. Reliance upon this advice does not constitute reasonable care under the Customs Modernization Act. In order to exercise the highest degree of reasonable care, BITE Data Corporation recommends that users perform a complete review, which may include application for a binding ruling from U.S. Customs and Border Protection. Should you need further assistance in this matter, please contact us at info@bitedata.io, where we can direct you to additional resources for your trade compliance needs.';
const ECCN_DISCLAIMER =
  'This tool provides automated determinations of potential export controls based on ECCN and country inputs. While efforts are made to reflect applicable regulations, no guarantee is made as to the accuracy, completeness, or current applicability of the results. Export control requirements may vary based on additional factors not captured by this tool, including end use, end user, and specific transaction details. Users are solely responsible for validating all outputs and ensuring compliance with applicable export control laws and regulations, including the U.S. Export Administration Regulations (EAR).';
const AES_DISCLAIMER =
  'This report flags potential discrepancies in the uploaded Automated Export System (AES) filing data based on automated rule checks and is not a substitute for a complete compliance review. Checks that could not fully run on this file (see Audit Summary) were not evaluated and may hide additional issues. Users are solely responsible for validating all findings and ensuring filings comply with the Foreign Trade Regulations (15 CFR Part 30) and applicable export control laws, including the U.S. Export Administration Regulations (EAR) and International Traffic in Arms Regulations (ITAR).';
// Verbatim copy of the disclaimer already shown on the ticket page itself
// (ExportDesign/ExportDesignTicket.tsx's da-ticket-disclaimer paragraph).
const DESIGN_ASSESSMENT_DISCLAIMER =
  'This is a research tool, not a formal ruling — please consult counsel for a final determination (or request a CJ from State/DDTC, or a CCATS classification from BIS).';

// Resolved per build: the embedded Open Sans names if they registered cleanly,
// otherwise PDFKit's built-in Helvetica.
let FONT_REGULAR = 'Helvetica';
let FONT_BOLD = 'Helvetica-Bold';

export interface IEntityTicketReportData {
  ticketName: string;
  entity?: { primary?: string; country?: string; address?: string };
  aiSums?: any[];
  beneficialOwnership?: any;
  adverseMedia?: any;
  // Raw OpenSearch _source documents for each tab.
  entityDocs: any[];
  ownershipDocs: any[];
  entityTotal?: number;
  ownershipTotal?: number;
  // Name of the user generating the report (for the cover "Prepared by" row).
  preparedBy?: string;
}

export interface IProductTicketReportData {
  ticketName: string;
  docType?: string;
  product?: {
    name?: string;
    htsCode?: string;
    country?: string;
    description?: string;
    region?: string;
  };
  aiSums?: any[];
  productDocs: any[];
  productTotal?: number;
  tariffSummary?: {
    dutyCost?: string;
    totalCost?: string;
    baseDutyRate?: string;
    additionalDutyRate?: string;
  };
  preparedBy?: string;
}

export interface ITariffTicketReportData {
  ticketName: string;
  docType?: string;
  tariffResults?: any;
  preparedBy?: string;
}

export interface IClassificationTicketReportData {
  ticketName: string;
  docType?: string;
  isLegacyTicket?: boolean;
  classificationValue?: string;
  commodity?: {
    name?: string;
    codeLabel?: string;
    code?: string;
    material?: string;
    intendedUse?: string;
    physicalState?: string;
  };
  followUps?: Array<{ question?: string; answer?: string }>;
  chatHistory?: Array<{ role?: string; text?: string }>;
  crossRulings?: Array<{
    title?: string;
    url?: string;
    relevance_percent?: number | null;
    relevance_note?: string | null;
  }>;
  chapterNotes?: string;
  finalReport?: any;
  aiStream?: string[];
  preparedBy?: string;
}

export interface IBeneficialOwnershipTicketReportData {
  ticketName: string;
  entityName?: string;
  country?: string;
  secondLayer?: boolean;
  sanctionsFlag?: string;
  sanctionsSummary?: string;
  executiveSummary?: string;
  summary?: string;
  owners?: any[];
  keyFindings?: any[];
  associatedEntities?: any[];
  supportingLinks?: any[];
  ownershipGraph?: IOwnershipGraph;
  preparedBy?: string;
}

export interface IUnifiedTicketReportData {
  ticketName: string;
  docType: 'Import Unified' | 'Export Unified' | string;
  status?: string;
  frequency?: string;
  lastScreen?: string | Date;
  preparedBy?: string;
  classification: IClassificationTicketReportData;
  workflowData?: any;
  tariffResults?: any;
  aiSums?: any[];
  product?: IProductTicketReportData['product'];
  productDocs?: any[];
  productTotal?: number;
}

export interface IEccnTicketReportData {
  ticketName: string;
  docType?: string;
  classificationValue?: string;
  commodity?: {
    name?: string;
    eccn?: string;
    destinationCountry?: string;
  };
  eccnResults?: any[];
  status?: string;
  frequency?: string;
  lastScreen?: string | Date;
  preparedBy?: string;
}

// Import Hub product page — pre-formatted currency/percentage strings are
// built by the caller (mirroring IProductTicketReportData.tariffSummary)
// so this file stays free of Import Hub-specific number formatting.
export interface IImportProductTicketReportData {
  productName: string;
  sku?: string;
  htsCode?: string;
  countryOfOrigin?: string;
  databaseName?: string;
  declaredValue?: string;
  preparedBy?: string;
  compliance: {
    assessed: boolean;
    score: number;
    deductions: Array<{ reason: string; points: number; category: string }>;
  };
  systemFlags: Array<{
    label: string;
    detail?: string;
    severity: string;
    cleared?: boolean;
  }>;
  risk: {
    status: 'available' | 'unavailable' | 'error';
    level?: string;
    confidence?: string;
    narrative?: string;
    keyFactors?: string[];
  };
  classification: {
    name?: string;
    sku?: string;
    hierarchyType?: string;
    category?: string;
    manufacturer?: string;
    vendor?: string;
    htsCode?: string;
    countryOfOrigin?: string;
    unitOfMeasure?: string;
    database?: string;
    description?: string;
    traces?: string;
    specifications?: string;
    additionalHtsCodes: Array<{ htsCode: string; countryOfOrigin: string }>;
    additionalColumns: Array<{ key: string; value: string }>;
  };
  tariff: {
    declaredValue?: string;
    materialInputs: Array<{ label: string; value: string }>;
    riskFlags: Array<{ label: string; detail?: string }>;
    baseDutyRate?: string;
    baseDutyCost?: string;
    additionalDutyRate?: string;
    additionalDutyCost?: string;
    totalDutyCost?: string;
    totalCost?: string;
    dutyLines: Array<{
      subCode?: string;
      label: string;
      ratePct: string;
      cost: string;
    }>;
    ftas: Array<{ program: string; status: string; note?: string }>;
    exclusions: Array<{
      subCode?: string;
      label: string;
      ratePct: string;
      applicability: string;
    }>;
  };
  programCodes: {
    appliedFlagCodes: string[];
    lensSummary: Array<{ name: string; total: number; newCount: number }>;
    matches: Array<{
      lens: string;
      entity: string;
      type: string;
      country: string;
      list: string;
      score: string;
    }>;
  };
  crossRulings: Array<{ title: string; url?: string }>;
  changelog: Array<{
    date: string;
    heading: string;
    previousValue?: string;
    currentValue?: string;
  }>;
}

interface ReportMatch {
  name: string;
  entityType: string;
  relatedEntity: string;
  country: string;
  listName: string;
  score: number | null;
}

interface ProductReportMatch {
  product: string;
  htsCode: string;
  country: string;
  listName: string;
  agency: string;
  lens: string;
}

// ---------------------------------------------------------------------------
// Field helpers (mirror EntityScreeningResultsTable so the report matches the
// on-screen table values/score).
// ---------------------------------------------------------------------------
// Strip non-printable control characters that can appear in dirty source data
// (they render as stray glyphs) and collapse tabs/newlines to a space so a
// value stays on its intended line within a table cell.
const cleanText = (input: any): string =>
  String(input ?? '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/[\t\n\r]+/g, ' ')
    .trim();

const cleanMultilineText = (input: any): string =>
  String(input ?? '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/\r\n?/g, '\n')
    .replace(/\t+/g, ' ')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');

// The BO agent's general_description/sanctions_summary come back as one
// continuous narrative with no embedded line breaks at all — confirmed
// against how the live workflow chat renders the same field (plain <p>, no
// white-space CSS, so even a value that DID have \n\n would collapse the
// same way there). This reflows the wall of text into paragraph-sized
// chunks for readability; shared with the ticket results page (see
// utils/reflowParagraphs.ts) so both present the same summary the same way.
const reflowIntoPdfParagraphs = (
  input: any,
  sentencesPerParagraph = 3,
): string => reflowIntoParagraphs(input, sentencesPerParagraph).join('\n\n');

const cleanPdfInlineMarkdown = (input: any): string =>
  cleanText(input)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 — $2')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#{1,6}\s*/, '')
    .replace(/^[-*]\s+/, '• ');

const cleanClassificationPdfText = (input: any): string =>
  cleanMultilineText(input)
    .split('\n')
    .map(cleanPdfInlineMarkdown)
    .filter(Boolean)
    .join('\n');

const getValue = (source: any, keys: string[], fallback = '—'): string => {
  const value = keys.map((key) => source?.[key]).find(Boolean);
  return value ? cleanText(value) : fallback;
};

const getEntityType = (source: any): string => {
  const explicit = getValue(source, ['Entity Type', 'Type'], '');
  if (explicit) return explicit;
  return source?.['Date of Birth'] ? 'Individual' : 'Entity';
};

const decorateAndRank = (docs: any[], searchTerm: string): ReportMatch[] => {
  const rows: ReportMatch[] = (docs || []).map((source: any) => ({
    name: getValue(source, ['Name of Entity']),
    entityType: getEntityType(source),
    relatedEntity: getValue(source, ['Related Entity']),
    country: getValue(source, ['Entity Country', 'Country']),
    listName: getValue(source, ['List Name', 'Agency']),
    score: getEntityMatchScore(searchTerm, source),
  }));
  // Sort by match score descending; rows without a score sort last.
  rows.sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
  return rows.slice(0, MAX_ROWS);
};

const riskColor = (label: string): string => {
  const lower = String(label || '').toLowerCase();
  if (lower.includes('high')) return COLOR.riskHigh;
  if (lower.includes('low')) return COLOR.riskLow;
  if (lower.includes('medium') || lower.includes('moderate'))
    return COLOR.riskMedium;
  return COLOR.textSecondary;
};

const stripEntityNameQuotes = (
  text: string | undefined,
  entityName: string | undefined,
): string => {
  if (!text) return '';
  const name = (entityName || '').trim();
  if (!name) return text;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(
    new RegExp(`['"“‘’”\`]\\s*(${escaped})\\s*['"“‘’”\`]`, 'gi'),
    '$1',
  );
};

const fmtCount = (n: number): string =>
  new Intl.NumberFormat('en-US').format(n || 0);

const fmtDateTimeLong = (d: Date): string => {
  const date = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date} · ${time}`;
};

const aiTopMatchNames = (summary: any): string[] =>
  Array.isArray(summary?.top_matches)
    ? summary.top_matches
        .map((m: any) => (typeof m === 'string' ? m : m?.name || m?.id))
        .filter(Boolean)
        .map((m: any) => cleanText(m))
    : [];

const getProductValue = (source: any, keys: string[], fallback = '—'): string =>
  getValue(source, keys, fallback);

const getProductName = (source: any): string =>
  getProductValue(source, [
    'Product Name',
    'Product',
    'Commodity Name',
    'Description',
    'description',
    'name',
  ]);

const getProductHts = (source: any): string =>
  getProductValue(source, [
    'HTS Code',
    'HTS',
    'HS Code',
    'HS CODE',
    'Harmonized Code',
    'harmonizedScheduleCode',
    'htsCode',
    'hsCode',
    'code',
  ]);

const decorateProductRows = (docs: any[]): ProductReportMatch[] =>
  (docs || []).slice(0, MAX_ROWS).map((source: any) => ({
    product: getProductName(source),
    htsCode: getProductHts(source),
    country: getProductValue(source, [
      'Country',
      'Source Country',
      'Origin Country',
      'Entity Country',
      'country',
    ]),
    listName: getProductValue(source, ['List Name', 'List', 'Dataset']),
    agency: getProductValue(source, ['Agency', 'Source', 'Regulator']),
    lens: getProductValue(source, ['BITE Lens', 'Lens', 'Category']),
  }));

const productSummaryText = (data: IProductTicketReportData): string => {
  const summary = data.aiSums?.[0] || {};
  return cleanText(
    summary.explanation ||
      summary.summary ||
      summary.analysis ||
      summary.response ||
      summary.message ||
      'No AI product-risk summary was saved with this screen.',
  );
};

const formatReportMoney = (value: any): string => {
  if (value === undefined || value === null || value === '') return '—';
  const normalized =
    typeof value === 'string' ? value.replace(/[$,\s]/g, '') : value;
  const numeric = Number(normalized);

  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(numeric);
};

// Matches the AES Analyzer run page's `usd(value, true)` KPI formatting
// ("$974K") — used for report KPI cards where the full precise figure
// (formatReportMoney) is too wide for the card and would wrap/clip.
const formatCompactMoney = (value: any): string => {
  if (value === undefined || value === null || value === '') return '—';
  const normalized =
    typeof value === 'string' ? value.replace(/[$,\s]/g, '') : value;
  const numeric = Number(normalized);

  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    // Without an explicit minimum, some Intl implementations clamp it up to
    // match maximumFractionDigits instead of defaulting to 0, forcing a
    // trailing ".0" on round numbers (e.g. "$974.0K" instead of "$974K").
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(numeric);
};

const formatReportDate = (value: any): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return cleanText(value);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatReportRate = (value: any): string => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'string') return value;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return cleanText(value);
  return `${numeric
    .toFixed(2)
    .replace(/\.00$/, '')
    .replace(/(\.\d)0$/, '$1')}%`;
};

const tariffSnapshotData = (data: ITariffTicketReportData): any => {
  const snapshot = data.tariffResults || {};
  return snapshot?.data || snapshot;
};

const tariffLines = (snapshot: any): any[] => {
  const results = snapshot?.results || {};
  if (Array.isArray(results.lines) && results.lines.length) {
    return results.lines;
  }

  const summaryRows = Array.isArray(results.summaryRows)
    ? results.summaryRows
    : [];
  return summaryRows.length
    ? [
        {
          id: 'line1',
          label: 'Line 1',
          value: snapshot?.inputs?.productValue,
          duty:
            snapshot?.math?.additionalDutyDollars ?? snapshot?.math?.dutyCost,
          rows: summaryRows,
        },
      ]
    : [];
};

const truthyKeys = (record: any): string[] =>
  record && typeof record === 'object'
    ? Object.entries(record)
        .filter(([, value]) => Boolean(value))
        .map(([key]) => cleanText(key))
    : [];

type TariffExclusionQuestion = {
  id: string;
  prompt: string;
  helperText: string;
  options: Array<{
    id: string;
    label: string;
    enablesExclusions: string[];
  }>;
};

const buildTariffExclusionQuestions = (
  optionalExclusions: any[],
): TariffExclusionQuestion[] => {
  const groups: Record<
    string,
    Array<{ id: string; label: string; enablesExclusions: string[] }>
  > = {};

  optionalExclusions.forEach((candidate: any) => {
    const question = candidate?.bd_232_question;
    const answer = candidate?.bd_232_answer;
    const code =
      candidate?.codeDigits || candidate?.codePretty || candidate?.code;

    if (!question || !answer || !code) return;

    if (!groups[question]) {
      groups[question] = [];
    }

    const answerId = String(answer).toLowerCase();
    const answerLabel = answerId.charAt(0).toUpperCase() + answerId.slice(1);
    let option = groups[question].find((item) => item.id === answerId);

    if (!option) {
      option = {
        id: answerId,
        label: answerLabel,
        enablesExclusions: [],
      };
      groups[question].push(option);
    }

    if (!option.enablesExclusions.includes(code)) {
      option.enablesExclusions.push(code);
    }
  });

  Object.values(groups).forEach((options) => {
    const hasYes = options.some((option) => option.id === 'yes');
    const hasNo = options.some((option) => option.id === 'no');

    if (hasYes && !hasNo) {
      options.push({ id: 'no', label: 'No', enablesExclusions: [] });
    } else if (hasNo && !hasYes) {
      options.push({ id: 'yes', label: 'Yes', enablesExclusions: [] });
    }
  });

  return Object.entries(groups).map(([prompt, options], index) => ({
    id: `generated-q-${index}`,
    prompt: prompt,
    helperText:
      'Select the option that describes your product to apply the correct tariff or exclusion.',
    options: options.sort((a, b) => {
      if (a.id === 'yes') return -1;
      if (b.id === 'yes') return 1;
      return a.label.localeCompare(b.label);
    }),
  }));
};

const selectedTariffQuestionOption = (
  question: TariffExclusionQuestion,
  enabledExclusions: Record<string, boolean>,
) => {
  // Mirrors TariffToolInsightsPanel's selectedAnswers: the selected option is the
  // one whose codes EXACTLY match this question's active codes. An option that
  // enables no codes (e.g. "Yes, it's an auto part" when only "No" carries the
  // exclusion) is therefore selected when none of the question's codes are on,
  // which is the answer the saved duty was calculated with.
  const questionCodes = new Set(
    question.options.flatMap((option) => option.enablesExclusions),
  );
  const activeCodes = new Set(
    [...questionCodes].filter((code) => enabledExclusions[code]),
  );
  return question.options.find(
    (option) =>
      option.enablesExclusions.length === activeCodes.size &&
      option.enablesExclusions.every((code) => activeCodes.has(code)),
  );
};

type TariffBanQuestion = {
  id: string;
  prompt: string;
  helperText: string;
  options: Array<{ id: 'yes' | 'no'; label: string }>;
  selected?: 'yes' | 'no';
};

// Mirrors TariffPage.tsx's importBanQuestions memo: one question per unique
// gate id across every matched ban (the importation-date gate's question
// text is identical across bans, so gate.id — already the dedup key there —
// collapses it to a single entry here too), each resolved through the same
// importBanGateAnswer fallback (filer's answer, else the gate's "keep the
// ban on" default) used to decide which ban is active in the first place.
const buildImportBanQuestions = (
  importBans: ImportBan[],
  banGateAnswers: Record<string, BanGateAnswer>,
): TariffBanQuestion[] => {
  const questions: TariffBanQuestion[] = [];
  const seen = new Set<string>();
  importBans.forEach((ban) => {
    (ban.gates || []).forEach((gate) => {
      if (gate.keepOnAnswer === null || seen.has(gate.id)) return;
      seen.add(gate.id);
      questions.push({
        id: gate.id,
        prompt: gate.question,
        helperText: `${ban.measure}. The goods are excluded from importation unless this answer lifts the ban.`,
        options: [
          { id: 'yes', label: 'Yes' },
          { id: 'no', label: 'No' },
        ],
        selected: importBanGateAnswer(gate, banGateAnswers) ?? undefined,
      });
    });
  });
  return questions;
};

// Shared by the Exclusion Applicability and Import Ban Applicability question
// loops in renderTariffApplicability — same helper/prompt/radio-card layout,
// just a different question source and selection rule.
const renderTariffQuestionCard = (
  doc: any,
  helperText: string,
  prompt: string,
  options: Array<{ id: string; label: string }>,
  selectedId: string | undefined,
): void => {
  if (doc.y > doc.page.height - 130) {
    doc.addPage();
    doc.y = 70;
  }

  doc
    .font(FONT_REGULAR)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text(helperText, PAGE_MARGIN, doc.y, {
      width: doc.page.width - 2 * PAGE_MARGIN,
      lineGap: 2,
    });
  doc.moveDown(0.35);
  doc
    .font(FONT_BOLD)
    .fontSize(10)
    .fillColor(COLOR.textPrimary)
    .text(prompt, PAGE_MARGIN, doc.y, {
      width: doc.page.width - 2 * PAGE_MARGIN,
      lineGap: 2,
    });
  doc.moveDown(0.4);

  options.forEach((option) => {
    const isSelected = selectedId === option.id;
    const y = doc.y;
    doc.save();
    doc
      .roundedRect(PAGE_MARGIN, y, doc.page.width - 2 * PAGE_MARGIN, 28, 4)
      .fillAndStroke(isSelected ? '#EFF6FF' : '#FFFFFF', COLOR.cardBorder);
    // Draw the radio as vector circles: the embedded Open Sans has no ● / ○
    // glyphs, so text radios render as empty .notdef boxes.
    const radioX = PAGE_MARGIN + 15;
    const radioY = y + 14;
    doc
      .lineWidth(1)
      .circle(radioX, radioY, 4.5)
      .stroke(isSelected ? COLOR.primary : COLOR.cardBorder);
    if (isSelected) {
      doc.circle(radioX, radioY, 2.5).fill(COLOR.primary);
    }
    doc.restore();
    doc
      .font(isSelected ? FONT_BOLD : FONT_REGULAR)
      .fontSize(10)
      .fillColor(isSelected ? COLOR.primary : COLOR.textPrimary)
      .text(option.label, PAGE_MARGIN + 28, y + 8, {
        width: doc.page.width - 2 * PAGE_MARGIN - 38,
        lineBreak: false,
      });
    doc.y = y + 34;
  });

  doc.moveDown(0.45);
};

const classificationStreamText = (streamData?: string[]): string => {
  if (!Array.isArray(streamData)) return '';

  return streamData
    .map((chunk) => {
      try {
        const parsed = JSON.parse(chunk);
        return parsed?.message || '';
      } catch {
        return '';
      }
    })
    .filter(Boolean)
    .join('\n\n')
    .replace(/<\/?i>/g, '');
};

// ---------------------------------------------------------------------------
// Font loading (Open Sans from /public so we get Cyrillic + Latin Extended)
// ---------------------------------------------------------------------------
export async function fetchFont(file: string): Promise<ArrayBuffer | null> {
  try {
    // eslint-disable-next-line no-undef
    const base = process.env.PUBLIC_URL || '';
    const res = await fetch(`${base}/resources/fonts/Open_Sans/${file}`);
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

function registerFonts(
  doc: any,
  regular: ArrayBuffer | null,
  bold: ArrayBuffer | null,
): void {
  FONT_REGULAR = 'Helvetica';
  FONT_BOLD = 'Helvetica-Bold';
  if (!regular || !bold) return;
  try {
    doc.registerFont('Body', new Uint8Array(regular));
    doc.registerFont('BodyBold', new Uint8Array(bold));
    // Force-resolve now so any parsing error is caught here (PDFKit opens
    // embedded fonts lazily); fall back to Helvetica if it throws.
    doc.font('Body');
    doc.font('BodyBold');
    FONT_REGULAR = 'Body';
    FONT_BOLD = 'BodyBold';
  } catch {
    FONT_REGULAR = 'Helvetica';
    FONT_BOLD = 'Helvetica-Bold';
  }
}

// ---------------------------------------------------------------------------
// PDFKit chrome / layout primitives (ported from BitePdfBuilder)
// ---------------------------------------------------------------------------
function attachChrome(
  doc: any,
  labels: {
    header?: string;
    headerRight?: string;
    footerFirstPage?: string;
    footerRest?: string;
  } = {},
): void {
  let pageNumber = 0;
  const headerLabel = labels.header || 'BITE · ENTITY SCREENING';
  const headerRightLabel = labels.headerRight || 'Saved Work Report';
  const firstPageFooter = labels.footerFirstPage || 'Entity Screening Report';

  const drawHeader = () => {
    pageNumber++;
    if (pageNumber === 1) return;
    const oldTop = doc.page.margins.top;
    const oldX = doc.x;
    const oldY = doc.y;
    doc.page.margins.top = 0;
    doc.save();
    doc
      .font(FONT_BOLD)
      .fontSize(8)
      .fillColor(COLOR.primary)
      .text(headerLabel, PAGE_MARGIN, 28, {
        characterSpacing: 0.6,
        lineBreak: false,
      });
    doc
      .font(FONT_REGULAR)
      .fontSize(8)
      .fillColor(COLOR.textMuted)
      .text(headerRightLabel, 0, 28, {
        align: 'right',
        width: doc.page.width - PAGE_MARGIN,
        lineBreak: false,
      });
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.5)
      .moveTo(PAGE_MARGIN, 44)
      .lineTo(doc.page.width - PAGE_MARGIN, 44)
      .stroke();
    doc.restore();
    doc.page.margins.top = oldTop;
    doc.x = oldX;
    doc.y = oldY;
  };

  const drawFooter = () => {
    const y = doc.page.height - 32;
    const oldBottom = doc.page.margins.bottom;
    const oldX = doc.x;
    const oldY = doc.y;
    doc.page.margins.bottom = 0;
    doc.save();
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.5)
      .moveTo(PAGE_MARGIN, y - 10)
      .lineTo(doc.page.width - PAGE_MARGIN, y - 10)
      .stroke();
    const rightLabel =
      pageNumber === 1 ? firstPageFooter : `Page ${pageNumber}`;
    doc
      .font(FONT_REGULAR)
      .fontSize(8)
      .fillColor(COLOR.textMuted)
      .text(rightLabel, 0, y, {
        align: 'right',
        width: doc.page.width - PAGE_MARGIN,
        lineBreak: false,
      });
    doc.restore();
    doc.page.margins.bottom = oldBottom;
    doc.x = oldX;
    doc.y = oldY;
  };

  doc.on('pageAdded', () => {
    drawHeader();
    drawFooter();
  });

  drawHeader();
  drawFooter();
}

// After a cover's disclaimer (renderFirstPageDisclaimer, via its `minY`
// param) gets pushed down far enough by long cover content that it spills a
// few lines onto a fresh page on its own, the section that follows should
// continue right there instead of forcing yet another near-empty page.
// Starts a fresh page only when doc.y is NOT already near the top of one —
// i.e. when the disclaimer finished within the cover page as usual.
const continueOrAddPage = (doc: any): void => {
  if (doc.y < doc.page.height - 150) {
    doc.y += 24;
  } else {
    doc.addPage();
    doc.y = 70;
  }
};

function renderSectionHeader(doc: any, num: string, title: string): void {
  const y = doc.y;
  doc
    .font(FONT_BOLD)
    .fontSize(15)
    .fillColor(COLOR.primary)
    .text(`${num} `, PAGE_MARGIN, y, { continued: true })
    .fillColor(COLOR.textPrimary)
    .text(title);
  const underY = doc.y + 2;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(1.2)
    .moveTo(PAGE_MARGIN, underY)
    .lineTo(doc.page.width - PAGE_MARGIN, underY)
    .stroke();
  doc.moveDown(0.6);
}

function renderKpiCards(
  doc: any,
  cards: Array<{ value: string; label: string; valueColor?: string }>,
): void {
  const startY = doc.y;
  const usable = doc.page.width - 2 * PAGE_MARGIN;
  const gap = 10;
  const w = (usable - gap * (cards.length - 1)) / cards.length;
  const h = 64;

  cards.forEach((c, i) => {
    const x = PAGE_MARGIN + i * (w + gap);
    doc.save();
    doc
      .roundedRect(x, startY, w, h, 4)
      .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
    doc.rect(x, startY, w, 3).fill(COLOR.primary);
    doc.restore();

    doc
      .fillColor(c.valueColor || COLOR.primary)
      .font(FONT_BOLD)
      .fontSize(18)
      .text(c.value, x + 10, startY + 12, { width: w - 20, lineBreak: false });
    doc
      .fillColor(COLOR.textSecondary)
      .font(FONT_BOLD)
      .fontSize(8)
      .text(c.label, x + 10, startY + 42, {
        width: w - 20,
        characterSpacing: 0.6,
      });
  });

  doc.y = startY + h + 18;
}

function renderLabeledBlock(doc: any, label: string, body: string): void {
  if (doc.y > doc.page.height - 120) {
    doc.addPage();
    doc.y = 70;
  }
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text(label, PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.2);
  doc
    .font(FONT_REGULAR)
    .fontSize(10)
    .fillColor(COLOR.textPrimary)
    .text(body, PAGE_MARGIN, doc.y, {
      width: doc.page.width - 2 * PAGE_MARGIN,
      lineGap: 2,
    });
  doc.moveDown(0.6);
}

// PDFKit can add a page in the middle of a long text call. Our pageAdded
// handler draws the report chrome, which changes PDFKit's active text style;
// PDF graphics-state save/restore does not restore PDFKit's JS font metadata.
// Render long copy in page-sized chunks so every continuation explicitly gets
// the same embedded font, size, and high-contrast colour.
function renderPaginatedBodyText(
  doc: any,
  body: string,
  options: {
    x?: number;
    width?: number;
    fontSize?: number;
    lineGap?: number;
    color?: string;
  } = {},
): void {
  const x = options.x ?? PAGE_MARGIN;
  const bodyWidth = options.width ?? doc.page.width - 2 * PAGE_MARGIN;
  const fontSize = options.fontSize ?? 10;
  const lineGap = options.lineGap ?? 2;
  const bodyOptions = { width: bodyWidth, lineGap: lineGap };
  const pageBottom = () => doc.page.height - 58;
  const applyBodyStyle = () =>
    doc
      .font(FONT_REGULAR)
      .fontSize(fontSize)
      .fillColor(options.color || COLOR.textPrimary);
  const addBodyPage = () => {
    doc.addPage();
    doc.y = 70;
    applyBodyStyle();
  };

  const paragraphs = body.split('\n');
  paragraphs.forEach((paragraph) => {
    let remaining = paragraph.trim();

    if (!remaining) {
      applyBodyStyle();
      doc.moveDown(0.4);
      return;
    }

    while (remaining) {
      applyBodyStyle();
      const minimumLineHeight = doc.heightOfString('Ag', bodyOptions);
      if (pageBottom() - doc.y < minimumLineHeight + 2) {
        addBodyPage();
      }

      const availableHeight = pageBottom() - doc.y - 2;
      if (doc.heightOfString(remaining, bodyOptions) <= availableHeight) {
        doc.text(remaining, x, doc.y, bodyOptions);
        remaining = '';
        continue;
      }

      // Find the largest prefix that fits. Prefer a word boundary but fall
      // back to a character boundary for long URLs or identifiers.
      let low = 1;
      let high = remaining.length;
      let fittingLength = 0;
      while (low <= high) {
        const midpoint = Math.floor((low + high) / 2);
        const candidate = remaining.slice(0, midpoint);
        if (doc.heightOfString(candidate, bodyOptions) <= availableHeight) {
          fittingLength = midpoint;
          low = midpoint + 1;
        } else {
          high = midpoint - 1;
        }
      }

      if (fittingLength === 0) {
        addBodyPage();
        continue;
      }

      const lastSpace = remaining.lastIndexOf(' ', fittingLength - 1);
      const splitAt = lastSpace > 0 ? lastSpace : fittingLength;
      const chunk = remaining.slice(0, splitAt).trimEnd();
      if (chunk) {
        applyBodyStyle();
        doc.text(chunk, x, doc.y, bodyOptions);
      }
      remaining = remaining.slice(splitAt).trimStart();
      if (remaining) addBodyPage();
    }
  });
}

function renderPaginatedLabeledBlock(
  doc: any,
  label: string,
  body: string,
): void {
  if (doc.y > doc.page.height - 120) {
    doc.addPage();
    doc.y = 70;
  }

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text(label, PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.2);

  renderPaginatedBodyText(doc, body);

  doc.moveDown(0.6);
}

function renderOrderedList(doc: any, items: string[]): void {
  items.forEach((item, i) => {
    if (doc.y > doc.page.height - 90) {
      doc.addPage();
      doc.y = 70;
    }
    const ny = doc.y;
    doc
      .font(FONT_BOLD)
      .fontSize(10)
      .fillColor(COLOR.primary)
      .text(`${i + 1}.`, PAGE_MARGIN + 2, ny);
    doc.y = ny;
    renderPaginatedBodyText(doc, item, {
      x: PAGE_MARGIN + 22,
      width: doc.page.width - 2 * PAGE_MARGIN - 24,
      fontSize: 10,
      lineGap: 2,
    });
    doc.moveDown(0.3);
  });
}

type Col = {
  header: string;
  width: number;
  align?: 'left' | 'right' | 'center';
  noWrap?: boolean;
};

export function renderTable(
  doc: any,
  cols: Col[],
  rows: any[],
  // Optional per-cell text colour (e.g. a verdict column); defaults to body text.
  cellColor?: (rowIndex: number, colIndex: number) => string | undefined,
  // Page options for overflow pages this table adds mid-render. A bare
  // `doc.addPage()` falls back to the *document's* original constructor
  // options (portrait), not the current page's — so a table opened on a
  // landscape page via `doc.addPage({ layout: 'landscape' })` must pass the
  // same options here, or a continuation page would silently snap back to
  // portrait mid-table.
  pageOptions?: Record<string, any>,
): void {
  const usable = doc.page.width - 2 * PAGE_MARGIN;
  const totalFraction = cols.reduce((s, c) => s + c.width, 0);
  const widths = cols.map((c) => (c.width / totalFraction) * usable);
  const xs: number[] = [];
  let cursor = PAGE_MARGIN;
  for (const w of widths) {
    xs.push(cursor);
    cursor += w;
  }

  const padX = 8;
  const rowGap = 7;
  const headerH = 22;

  const pageTop = 70;
  const pageBottom = () => doc.page.height - 60;
  // Less room than this left on a page isn't worth starting part of an
  // over-tall row in; it goes to the next page instead.
  const minPartHeight = 60;

  const drawHeaderRow = () => {
    const yy = doc.y;
    doc.save();
    doc.rect(PAGE_MARGIN, yy, usable, headerH).fill(COLOR.tableHeaderBg);
    cols.forEach((c, i) => {
      doc
        .fillColor(COLOR.tableHeaderText)
        .font(FONT_BOLD)
        .fontSize(9)
        .text(c.header, xs[i] + padX, yy + 7, {
          width: widths[i] - 2 * padX,
          align: c.align || 'left',
          lineBreak: false,
        });
    });
    doc.restore();
    doc.y = yy + headerH;
  };

  // A header drawn below the bottom margin makes pdfkit add a page for every
  // header cell, leaving one near-empty page per column. Start the table on a
  // fresh page instead when the header and the start of a row won't fit.
  if (doc.y + headerH + minPartHeight > pageBottom()) {
    doc.addPage(pageOptions);
    doc.y = pageTop;
  }
  drawHeaderRow();
  let y = doc.y;

  const cellHeight = (text: string, i: number) =>
    doc
      .font(FONT_REGULAR)
      .fontSize(9.5)
      .heightOfString(text, { width: widths[i] - 2 * padX });

  const measureRow = (texts: string[]) => {
    let tallest = 0;
    cols.forEach((c, i) => {
      if (c.noWrap) return;
      tallest = Math.max(tallest, cellHeight(texts[i], i));
    });
    return Math.max(tallest + rowGap * 2, 22);
  };

  // The part of `text` that fits in `maxHeight`, and the rest. Breaks between
  // lines where it can, otherwise between words, and always takes at least
  // one word so an over-tall row keeps moving forward.
  const splitCellText = (text: string, i: number, maxHeight: number) => {
    const fits = (s: string) => cellHeight(s, i) <= maxHeight;
    if (fits(text)) return [text, ''];
    const lines = text.split('\n');
    let n = 0;
    while (n < lines.length && fits(lines.slice(0, n + 1).join('\n'))) n++;
    if (n > 0) {
      return [lines.slice(0, n).join('\n'), lines.slice(n).join('\n')];
    }
    const words = lines[0].split(' ');
    let w = 1;
    while (w < words.length && fits(words.slice(0, w + 1).join(' '))) w++;
    const restOfLine = words.slice(w).join(' ');
    const rest = restOfLine ? [restOfLine, ...lines.slice(1)] : lines.slice(1);
    return [words.slice(0, w).join(' '), rest.join('\n')];
  };

  const startNewPage = () => {
    doc.addPage(pageOptions);
    doc.y = pageTop;
    drawHeaderRow();
    y = doc.y;
  };

  const drawRow = (texts: string[], rowIndex: number, rowH: number) => {
    cols.forEach((c, i) => {
      const cellText = texts[i];
      // Clip to the cell so nothing can bleed into the neighbouring column,
      // regardless of glyph widths in the underlying data.
      doc.save();
      doc.rect(xs[i], y, widths[i], rowH).clip();
      doc
        .fillColor(cellColor?.(rowIndex, i) || COLOR.textPrimary)
        .font(FONT_REGULAR)
        .fontSize(9.5)
        .text(cellText, xs[i] + padX, y + rowGap, {
          width: widths[i] - 2 * padX,
          // An explicit height makes pdfkit clip overflow instead of
          // silently auto-paginating: without one, a cell whose actual
          // rendered height exceeds this row's pre-measured `rowH` (e.g. a
          // rounding difference between this measurement and pdfkit's own
          // line-wrapping) triggers pdfkit's own `continueOnNewPage`, which
          // calls `doc.addPage()` with no arguments — silently reverting to
          // the document's original page options (portrait) regardless of
          // this table's `pageOptions`, and desyncing `xs`/`widths` from the
          // new page's actual width.
          //
          // Skip it for `noWrap` columns: passing both `height` and
          // `lineBreak: false` together makes pdfkit wrap to fill the height
          // box anyway (e.g. "$85,000.00" splitting into "$85,000.0" / "0"),
          // defeating the single-line intent. A noWrap cell is always one
          // line, so it can't trigger the auto-pagination this guards against.
          height: c.noWrap ? undefined : rowH - 2 * rowGap,
          align: c.align || 'left',
          lineBreak: !c.noWrap,
          ellipsis: c.noWrap ? true : false,
        });
      doc.restore();
    });

    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(PAGE_MARGIN, y + rowH)
      .lineTo(PAGE_MARGIN + usable, y + rowH)
      .stroke();
    y += rowH;
  };

  rows.forEach((row, rowIndex) => {
    let texts = cols.map((_, i) =>
      row[i] === null || row[i] === undefined ? '' : String(row[i]),
    );
    let rowH = measureRow(texts);
    const fitsOnAFreshPage = rowH <= pageBottom() - (pageTop + 22);
    if (
      y + rowH > pageBottom() &&
      (fitsOnAFreshPage || pageBottom() - y < minPartHeight)
    ) {
      startNewPage();
    }

    // A row taller than a whole page (e.g. an AES line with many findings in
    // one cell) is drawn in parts continued on the following pages. Drawn
    // whole, it ran past the bottom of the page and everything below it was
    // lost.
    let drawnInParts = false;
    while (y + rowH > pageBottom()) {
      const room = pageBottom() - y - 2 * rowGap;
      const parts = texts.map((text, i) =>
        cols[i].noWrap ? [text, ''] : splitCellText(text, i, room),
      );
      drawRow(
        parts.map((part) => part[0]),
        rowIndex,
        pageBottom() - y,
      );
      texts = parts.map((part) => part[1]);
      if (texts.every((text) => !text)) {
        drawnInParts = true;
        break;
      }
      startNewPage();
      rowH = measureRow(texts);
    }
    if (!drawnInParts) drawRow(texts, rowIndex, rowH);
  });

  doc.y = y + 6;
}

async function loadLogoBuffer(): Promise<ArrayBuffer | string | null> {
  try {
    if (
      typeof biteHeaderDark === 'string' &&
      biteHeaderDark.startsWith('data:')
    ) {
      return biteHeaderDark;
    }
    const res = await fetch(biteHeaderDark);
    if (res.ok) {
      return await res.arrayBuffer();
    }
  } catch {
    /* logo is optional */
  }
  return null;
}

function renderCover(
  doc: any,
  data: IEntityTicketReportData,
  logo: ArrayBuffer | string | null,
): void {
  // Left accent sidebar
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* ignore logo failures */
    }
  }

  const titleY = 230;
  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  // The title is static text that could change — measure it instead of
  // assuming a fixed single line, so a longer title that wraps doesn't push
  // its second line into the metadata ledger below.
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('BITE Entity Screening Report', {
      width: titleWidth,
      lineGap: 2,
    });
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text('BITE Entity Screening Report', PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const metaY = titleY + Math.max(titleHeight, 46) + 40;
  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 130;
  const valueW = doc.page.width - PAGE_MARGIN - valueX;

  const meta: Array<[string, string]> = [
    ['TICKET', cleanText(data.ticketName) || '—'],
    ['ENTITY', cleanText(data.entity?.primary) || '—'],
  ];
  if (data.entity?.country)
    meta.push(['COUNTRY', cleanText(data.entity.country)]);
  if (data.entity?.address)
    meta.push(['ADDRESS', cleanText(data.entity.address)]);
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push([
    'PREPARED BY',
    cleanText(data.preparedBy) || 'BITE Entity Screening',
  ]);

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, metaY - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, metaY - 6)
    .stroke();

  let y = metaY;
  meta.forEach((pair) => {
    // A long value (e.g. a saved ticket's free-text name) must wrap within
    // its own row rather than overflowing a fixed row height into the next
    // row — the row grows to fit, capped and ellipsis-truncated beyond ~3
    // lines.
    const measuredHeight = doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .heightOfString(pair[1], { width: valueW, lineGap: 2 });
    const rowH = Math.max(26, Math.min(measuredHeight, 44) + 14);

    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text(pair[0], labelX, y + 6, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(pair[1], valueX, y + 5, {
        width: valueW,
        height: rowH - 10,
        ellipsis: true,
      });
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(labelX, y + rowH - 4)
      .lineTo(doc.page.width - PAGE_MARGIN, y + rowH - 4)
      .stroke();
    y += rowH;
  });

  const accentY = y + 18;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(2.5)
    .moveTo(PAGE_MARGIN, accentY)
    .lineTo(PAGE_MARGIN + 60, accentY)
    .stroke();
  doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text(
      'A record of the saved risk evaluation, AI results, and the top matches ' +
        'and ownership matches (ranked by match score) for this entity screen.',
      PAGE_MARGIN,
      accentY + 18,
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );
}

function renderProductCover(
  doc: any,
  data: IProductTicketReportData,
  logo: ArrayBuffer | string | null,
): void {
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* ignore logo failures */
    }
  }

  const titleY = 230;
  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('BITE Product Screening Report', {
      width: titleWidth,
      lineGap: 2,
    });
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text('BITE Product Screening Report', PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const metaY = titleY + Math.max(titleHeight, 46) + 40;
  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 130;
  const valueW = doc.page.width - PAGE_MARGIN - valueX;

  const meta: Array<[string, string]> = [
    ['TICKET', cleanText(data.ticketName) || '—'],
    ['TYPE', cleanText(data.docType) || 'Product Screen'],
  ];
  if (data.product?.region)
    meta.push(['IMPORT REGION', cleanText(data.product.region)]);
  if (data.product?.htsCode)
    meta.push(['HTS / HS CODE', cleanText(data.product.htsCode)]);
  meta.push(['DESCRIPTION', cleanText(data.product?.description)]);
  if (data.product?.country)
    meta.push(['COUNTRY', cleanText(data.product.country)]);
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push([
    'PREPARED BY',
    cleanText(data.preparedBy) || 'BITE Product Screening',
  ]);

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, metaY - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, metaY - 6)
    .stroke();

  let y = metaY;
  meta.forEach((pair) => {
    // A long value (e.g. a saved ticket's free-text name) must wrap within
    // its own row rather than overflowing a fixed row height into the next
    // row — the row grows to fit, capped and ellipsis-truncated beyond ~3
    // lines.
    const measuredHeight = doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .heightOfString(pair[1], { width: valueW, lineGap: 2 });
    const rowH = Math.max(26, Math.min(measuredHeight, 44) + 14);

    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text(pair[0], labelX, y + 6, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(pair[1], valueX, y + 5, {
        width: valueW,
        height: rowH - 10,
        ellipsis: true,
      });
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(labelX, y + rowH - 4)
      .lineTo(doc.page.width - PAGE_MARGIN, y + rowH - 4)
      .stroke();
    y += rowH;
  });

  const accentY = y + 18;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(2.5)
    .moveTo(PAGE_MARGIN, accentY)
    .lineTo(PAGE_MARGIN + 60, accentY)
    .stroke();
  doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text(
      'A record of the saved product risk evaluation, AI results, tariff context, ' +
        'and the top product-screening matches for this ticket.',
      PAGE_MARGIN,
      accentY + 18,
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );
}

function renderFirstPageDisclaimer(
  doc: any,
  disclaimer: string,
  preferredFontSize?: number,
  // Y the cover's own content (meta table + closing paragraph) actually
  // ended at. The disclaimer normally sits flush against the bottom of the
  // page, but a long free-text value above it (e.g. a long HTS/product
  // description) can push that content down far enough to collide with this
  // fixed bottom anchor — passing `minY` (typically the caller's `doc.y`
  // right after its closing paragraph) makes the disclaimer start below the
  // content instead of overlapping it, at the cost of sitting higher than
  // the usual bottom-flush position in that case.
  minY?: number,
): void {
  const width = doc.page.width - 2 * PAGE_MARGIN;
  const fontSize = preferredFontSize ?? (disclaimer.length > 500 ? 7.2 : 7.8);
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(8.5)
    .heightOfString('Disclaimer:', { width: width });
  const bodyHeight = doc
    .font(FONT_REGULAR)
    .fontSize(fontSize)
    .heightOfString(disclaimer, {
      width: width,
      lineGap: 1.4,
    });
  const blockHeight = titleHeight + bodyHeight + 8;
  const bottom = doc.page.height - 48;
  let y = Math.max(bottom - blockHeight, minY != null ? minY + 24 : 0);
  // Cover content long enough to leave no room for the disclaimer: start it
  // at the top of the next page rather than let PDFKit break it mid-text,
  // where the continuation loses its font and colour (see
  // renderPaginatedBodyText). The section after a cover continues on that
  // page through continueOrAddPage.
  if (y + blockHeight > bottom) {
    doc.addPage();
    y = 80;
  }

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(PAGE_MARGIN, y - 10)
    .lineTo(doc.page.width - PAGE_MARGIN, y - 10)
    .stroke();
  doc
    .font(FONT_BOLD)
    .fontSize(8.5)
    .fillColor(COLOR.textPrimary)
    .text('Disclaimer:', PAGE_MARGIN, y, { width: width });
  doc
    .font(FONT_REGULAR)
    .fontSize(fontSize)
    .fillColor(COLOR.textSecondary)
    .text(disclaimer, PAGE_MARGIN, y + titleHeight + 4, {
      width: width,
      lineGap: 1.4,
    });
}

function renderTariffCover(
  doc: any,
  data: ITariffTicketReportData,
  logo: ArrayBuffer | string | null,
): void {
  const snapshot = tariffSnapshotData(data);
  const inputs = snapshot.inputs || {};
  const base = snapshot.base || {};

  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* ignore logo failures */
    }
  }

  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('BITE Tariff Simulation Report', {
      width: titleWidth,
      lineGap: 2,
    });
  // The title wraps to two lines; lift it by the extra line so the meta
  // table, closing paragraph and disclaimer keep their single-line positions
  // and the disclaimer still fits on the cover.
  const titleLineHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('B', { width: titleWidth, lineGap: 2 });
  const titleY = 230 - Math.max(0, titleHeight - titleLineHeight);
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text('BITE Tariff Simulation Report', PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const metaY = titleY + Math.max(titleHeight, 46) + 40;
  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 130;
  const valueW = doc.page.width - PAGE_MARGIN - valueX;
  const minRowH = 26;
  const meta: Array<[string, string]> = [
    ['TICKET', cleanText(data.ticketName) || '—'],
    ['HTS CODE', cleanText(inputs.htsCode) || '—'],
    ['ORIGIN', cleanText(inputs.originCountry || inputs.originIso2) || '—'],
  ];
  if (base.productDesc) {
    meta.push(['DESCRIPTION', cleanText(base.productDesc)]);
  }
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push(['PREPARED BY', cleanText(data.preparedBy) || 'BITE Tariff']);

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, metaY - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, metaY - 6)
    .stroke();

  let y = metaY;
  meta.forEach((pair) => {
    // DESCRIPTION is free-text and can run arbitrarily long — capped and
    // ellipsis-truncated the same way renderClassificationCover already
    // handles its own DESCRIPTION row, so it can never push `y` down far
    // enough to collide with renderFirstPageDisclaimer's fixed
    // bottom-anchored block below.
    const isDescription = pair[0] === 'DESCRIPTION';
    const valueFontSize = isDescription && pair[1].length > 220 ? 10 : 11;
    const maxValueHeight = isDescription ? 66 : 34;
    const measuredValueHeight = doc
      .font(FONT_REGULAR)
      .fontSize(valueFontSize)
      .heightOfString(pair[1], {
        width: valueW,
        lineGap: 2,
      });
    const valueHeight = Math.min(measuredValueHeight, maxValueHeight);
    const rowH = Math.max(minRowH, valueHeight + 14);
    const valueOptions: Record<string, any> = {
      width: valueW,
      height: valueHeight + 2,
      lineGap: 2,
    };
    if (measuredValueHeight > maxValueHeight) {
      valueOptions.ellipsis = true;
    }

    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text(pair[0], labelX, y + 6, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(valueFontSize)
      .fillColor(COLOR.textPrimary)
      .text(pair[1], valueX, y + 5, valueOptions);
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(labelX, y + rowH - 4)
      .lineTo(doc.page.width - PAGE_MARGIN, y + rowH - 4)
      .stroke();
    y += rowH;
  });

  const accentY = y + 18;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(2.5)
    .moveTo(PAGE_MARGIN, accentY)
    .lineTo(PAGE_MARGIN + 60, accentY)
    .stroke();
  doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text(
      'A record of the saved tariff inputs, duty summary, estimated fees, and total landed-cost calculation for this ticket.',
      PAGE_MARGIN,
      accentY + 18,
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );

  renderFirstPageDisclaimer(doc, TARIFF_DISCLAIMER, undefined, doc.y);
}

function renderClassificationCover(
  doc: any,
  data: IClassificationTicketReportData,
  logo: ArrayBuffer | string | null,
): void {
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* ignore logo failures */
    }
  }

  // EAR Classification tickets (docType 'ECCN') share this renderer with
  // HTS Classification; title them by type.
  const classificationTitle =
    data.docType === 'ECCN'
      ? 'BITE Export Classification Report'
      : 'BITE HTS Classification Report';
  const titleY = 230;
  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString(classificationTitle, {
      width: titleWidth,
      lineGap: 2,
    });
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text(classificationTitle, PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const metaY = titleY + Math.max(titleHeight, 46) + 40;
  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 130;
  const valueW = doc.page.width - PAGE_MARGIN - valueX;
  const minRowH = 28;
  const codeLabel = cleanText(data.commodity?.codeLabel) || 'CLASSIFICATION';

  const meta: Array<[string, string]> = [
    ['TICKET', cleanText(data.ticketName) || '—'],
    ['TYPE', cleanText(data.docType) || 'Classification'],
  ];
  if (data.commodity?.code)
    meta.push([codeLabel.toUpperCase(), cleanText(data.commodity.code)]);
  meta.push([
    'DESCRIPTION',
    cleanText(data.commodity?.name || data.classificationValue) || '—',
  ]);
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push([
    'PREPARED BY',
    cleanText(data.preparedBy) || 'BITE Classification',
  ]);

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, metaY - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, metaY - 6)
    .stroke();

  let y = metaY;
  meta.forEach((pair) => {
    const isDescription = pair[0] === 'DESCRIPTION';
    const valueFontSize = isDescription && pair[1].length > 220 ? 10 : 11;
    const maxValueHeight = isDescription ? 66 : 34;
    const measuredValueHeight = doc
      .font(FONT_REGULAR)
      .fontSize(valueFontSize)
      .heightOfString(pair[1], {
        width: valueW,
        lineGap: 2,
      });
    const valueHeight = Math.min(measuredValueHeight, maxValueHeight);
    const rowH = Math.max(minRowH, valueHeight + 14);
    const valueOptions: Record<string, any> = {
      width: valueW,
      height: valueHeight + 2,
      lineGap: 2,
    };
    if (measuredValueHeight > maxValueHeight) {
      valueOptions.ellipsis = true;
    }

    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text(pair[0], labelX, y + 6, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(valueFontSize)
      .fillColor(COLOR.textPrimary)
      .text(pair[1], valueX, y + 5, valueOptions);
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(labelX, y + rowH - 4)
      .lineTo(doc.page.width - PAGE_MARGIN, y + rowH - 4)
      .stroke();
    y += rowH;
  });

  const accentY = y + 18;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(2.5)
    .moveTo(PAGE_MARGIN, accentY)
    .lineTo(PAGE_MARGIN + 60, accentY)
    .stroke();
  doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text(
      'A record of the saved classification result, clarifying details, final report, CROSS rulings, and section or chapter notes for this ticket.',
      PAGE_MARGIN,
      accentY + 18,
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );

  renderFirstPageDisclaimer(doc, CLASSIFICATION_DISCLAIMER, 7.8, doc.y);
}

function renderRiskAndAi(doc: any, data: IEntityTicketReportData): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '01', 'Risk Evaluation & AI Results');

  const summary = data.aiSums?.[0];
  const riskLabel = summary?.risk || 'N/A';
  const entityName = data.entity?.primary || summary?.entity_name || '';
  const explanation =
    cleanText(stripEntityNameQuotes(summary?.explanation, entityName)) ||
    'No risk evaluation was saved with this screen.';

  renderKpiCards(doc, [
    {
      value: String(riskLabel),
      label: 'EVALUATED RISK LEVEL',
      valueColor: riskColor(String(riskLabel)),
    },
    {
      value: fmtCount(data.entityTotal ?? data.entityDocs.length),
      label: 'SCREENING MATCHES',
    },
    {
      value: fmtCount(data.ownershipTotal ?? data.ownershipDocs.length),
      label: 'OWNERSHIP MATCHES',
    },
  ]);

  renderLabeledBlock(doc, 'RISK EVALUATION SUMMARY', explanation);

  const aiMatches = aiTopMatchNames(summary);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('AI TOP MATCHES', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.4);
  if (aiMatches.length) {
    renderOrderedList(doc, aiMatches.slice(0, 25));
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No AI top matches were saved with this screen.',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(0.6);
  }

  // Saved Adverse Media evaluation (optional)
  const adverseMedia = data.adverseMedia;
  const adverseFindings = Array.isArray(adverseMedia?.findings)
    ? adverseMedia.findings
    : [];
  const hasAdverseMedia =
    adverseMedia && (adverseMedia.summary || adverseFindings.length);
  if (hasAdverseMedia) {
    doc.moveDown(0.4);
    if (doc.y > doc.page.height - 160) {
      doc.addPage();
      doc.y = 70;
    }
    doc
      .font(FONT_BOLD)
      .fontSize(12)
      .fillColor(COLOR.textPrimary)
      .text('Adverse Media Evaluation', PAGE_MARGIN, doc.y);
    doc.moveDown(0.4);
    renderLabeledBlock(
      doc,
      'ADVERSE MEDIA SUMMARY',
      cleanText(adverseMedia.summary || 'No summary was saved.'),
    );
    if (adverseFindings.length) {
      const findingLines = adverseFindings
        .slice(0, 5)
        .map((finding: any) =>
          cleanText(
            [
              finding.claim || finding.source || 'Finding',
              finding.date,
              finding.url,
            ]
              .filter(Boolean)
              .join(' - '),
          ),
        )
        .filter(Boolean);
      if (findingLines.length) {
        doc
          .font(FONT_BOLD)
          .fontSize(9)
          .fillColor(COLOR.textSecondary)
          .text('ADVERSE MEDIA FINDINGS', PAGE_MARGIN, doc.y, {
            characterSpacing: 0.6,
          });
        doc.moveDown(0.4);
        renderOrderedList(doc, findingLines);
      }
    }
  }

  // Saved Beneficial Ownership evaluation (optional)
  const boi = data.beneficialOwnership;
  const hasBoi =
    boi &&
    (boi.summary ||
      boi.beneficial_ownership_summary ||
      boi.entity_risk ||
      (boi.top_matches && boi.top_matches.length));
  if (hasBoi) {
    doc.moveDown(0.4);
    if (doc.y > doc.page.height - 160) {
      doc.addPage();
      doc.y = 70;
    }
    doc
      .font(FONT_BOLD)
      .fontSize(12)
      .fillColor(COLOR.textPrimary)
      .text('Beneficial Ownership Evaluation', PAGE_MARGIN, doc.y);
    doc.moveDown(0.4);
    if (boi.entity_risk) {
      renderLabeledBlock(doc, 'ENTITY RISK', cleanText(boi.entity_risk));
    }
    renderLabeledBlock(
      doc,
      'OWNERSHIP SUMMARY',
      cleanText(
        boi.summary ||
          boi.beneficial_ownership_summary ||
          'No summary was saved.',
      ),
    );
    const boiMatches = Array.isArray(boi.top_matches)
      ? boi.top_matches.filter(Boolean).map((m: any) => cleanText(m))
      : [];
    if (boiMatches.length) {
      doc
        .font(FONT_BOLD)
        .fontSize(9)
        .fillColor(COLOR.textSecondary)
        .text('OWNERSHIP TOP MATCHES', PAGE_MARGIN, doc.y, {
          characterSpacing: 0.6,
        });
      doc.moveDown(0.4);
      renderOrderedList(doc, boiMatches.slice(0, 25));
    }
  }
}

function renderProductRiskAndAi(
  doc: any,
  data: IProductTicketReportData,
  sectionNum = '01',
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, sectionNum, 'Risk Evaluation & AI Results');

  const summary = data.aiSums?.[0] || {};
  const riskLabel = summary.risk || summary.risk_level || 'N/A';
  const burdenLabel = summary.burden || summary.trade_burden || 'N/A';

  renderKpiCards(doc, [
    {
      value: String(riskLabel),
      label: 'EVALUATED RISK LEVEL',
      valueColor: riskColor(String(riskLabel)),
    },
    {
      value: String(burdenLabel),
      label: 'TRADE BURDEN',
    },
    {
      value: fmtCount(data.productTotal ?? data.productDocs.length),
      label: 'SCREENING MATCHES',
    },
  ]);

  renderLabeledBlock(doc, 'PRODUCT RISK SUMMARY', productSummaryText(data));

  if (data.product?.description) {
    renderLabeledBlock(
      doc,
      'PRODUCT DESCRIPTION',
      cleanText(data.product.description),
    );
  }

  if (data.tariffSummary) {
    const summaryLines = [
      data.tariffSummary.baseDutyRate
        ? `Base duty rate: ${data.tariffSummary.baseDutyRate}`
        : '',
      data.tariffSummary.additionalDutyRate
        ? `Additional duty rate: ${data.tariffSummary.additionalDutyRate}`
        : '',
      data.tariffSummary.dutyCost
        ? `Duty cost: ${data.tariffSummary.dutyCost}`
        : '',
      data.tariffSummary.totalCost
        ? `Total cost: ${data.tariffSummary.totalCost}`
        : '',
    ].filter(Boolean);

    if (summaryLines.length) {
      renderLabeledBlock(doc, 'TARIFF SUMMARY', summaryLines.join('\n'));
    }
  }
}

function renderTariffReadableResults(
  doc: any,
  data: ITariffTicketReportData,
  sectionNum = '02',
): void {
  const snapshot = tariffSnapshotData(data);
  const inputs = snapshot.inputs || {};
  const base = snapshot.base || {};
  const math = snapshot.math || {};
  const results = snapshot.results || {};
  // Mirrors TariffResults.tsx's `importBan` resolution: a barred product
  // shows an exclusion banner and blanked duty figures instead of a normal
  // calculation, and hides FTAs/exclusions entirely (irrelevant when the
  // goods can't be imported regardless). Derived from importBans +
  // banGateAnswers rather than read off a stored `activeImportBan` field —
  // like TariffPage.tsx's own `activeImportBan` state, which is always
  // recomputed this way rather than trusted from a snapshot, since a saved
  // `activeImportBan` would (a) not exist on older tickets and (b) can't
  // reliably stay in sync with the gate answers it was derived from.
  const importBansForBan: ImportBan[] = Array.isArray(results.importBans)
    ? results.importBans
    : [];
  const banGateAnswersForBan: Record<string, BanGateAnswer> =
    results.banGateAnswers || {};
  const activeImportBan: ImportBan | null = findActiveImportBan(
    importBansForBan,
    banGateAnswersForBan,
  );
  const lines = tariffLines(snapshot);
  const ftaOptions = Array.isArray(results.ftaOptions)
    ? results.ftaOptions
    : [];
  const optionalExclusions = Array.isArray(results.optionalExclusions)
    ? results.optionalExclusions
    : [];
  const enabledFtas = results.enabledFtas || {};
  const enabledExclusions = results.enabledExclusions || {};
  const leftX = PAGE_MARGIN;
  const gap = 22;
  const rightW = 162;
  const leftW = doc.page.width - 2 * PAGE_MARGIN - gap - rightW;
  const rightX = leftX + leftW + gap;
  const rowGap = 6;
  const baseDutyRate = base.baseDutyRate || math.baseDutyRate || '—';
  const additionalDutyRate =
    math.additionalDutyRateDisplay || formatReportRate(math.additionalDutyRate);
  const dutiesAndFees =
    Number(math.dutyCost || 0) + Number(math.mpf || 0) + Number(math.hmf || 0);

  const writeHeading = (text: string, x: number, y: number, width: number) => {
    doc
      .font(FONT_BOLD)
      .fontSize(10.5)
      .fillColor(COLOR.textPrimary)
      .text(text, x, y, { width: width });
    return doc.y + 6;
  };

  const writeBody = (text: string, x: number, y: number, width: number) => {
    doc
      .font(FONT_REGULAR)
      .fontSize(9.5)
      .fillColor(COLOR.textPrimary)
      .text(text || '—', x, y, { width: width, lineGap: 2 });
    return doc.y + 8;
  };

  const writeCostRow = (
    label: string,
    value: string,
    y: number,
    bold = false,
  ) => {
    doc
      .font(bold ? FONT_BOLD : FONT_REGULAR)
      .fontSize(9.5)
      .fillColor(COLOR.textPrimary)
      .text(label, rightX, y, { width: rightW * 0.62, lineBreak: false });
    doc
      .font(bold ? FONT_BOLD : FONT_REGULAR)
      .fontSize(9.5)
      .fillColor(COLOR.textPrimary)
      .text(value, rightX + rightW * 0.62, y, {
        width: rightW * 0.38,
        align: 'right',
        lineBreak: false,
      });
    return y + 22;
  };

  const measureText = (text: string, width: number, bold = false) =>
    doc
      .font(bold ? FONT_BOLD : FONT_REGULAR)
      .fontSize(9.3)
      .heightOfString(text || '—', {
        width: width,
        lineGap: 2,
      });

  const ensureLeftSpace = (height: number) => {
    if (leftY + height > doc.page.height - 58) {
      doc.addPage();
      doc.y = 70;
      leftY = doc.y;
    }
  };

  const writeThreeColumnRow = (
    values: [string, string, string],
    widths: [number, number, number],
    offsets: [number, number, number],
  ) => {
    const rowHeights = values.map((value, index) =>
      measureText(value, leftW * widths[index]),
    );
    const rowH = Math.max(18, Math.max(...rowHeights) + 4);
    ensureLeftSpace(rowH);

    values.forEach((value, index) => {
      doc
        .font(FONT_REGULAR)
        .fontSize(9.3)
        .fillColor(COLOR.textPrimary)
        .text(value || '—', leftX + leftW * offsets[index], leftY, {
          width: leftW * widths[index],
          align: index === 0 ? 'left' : 'right',
          lineGap: 2,
        });
    });

    leftY += rowH;
  };

  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, sectionNum, 'Tariff Results');

  const startY = doc.y + 4;
  let rightY = writeHeading('Cost Summary', rightX, startY, rightW);
  if (activeImportBan) {
    doc
      .font(FONT_REGULAR)
      .fontSize(8.5)
      .fillColor(COLOR.riskMedium)
      .text(
        'Excluded from importation, so no duty or fees apply.',
        rightX,
        rightY,
        { width: rightW, lineGap: 2 },
      );
    rightY = doc.y + 8;
    rightY = writeCostRow('Base Duty Rate:', '—', rightY);
    rightY = writeCostRow('Base Duty Cost:', '—', rightY);
    rightY = writeCostRow('Additional Duty Rate:', '—', rightY);
    rightY = writeCostRow('Additional Duty Cost:', '—', rightY);
    rightY = writeCostRow('Total Duty Cost:', '—', rightY, true);
    rightY = writeCostRow('Total Cost:', '—', rightY, true);
  } else {
    rightY = writeCostRow('Base Duty Rate:', cleanText(baseDutyRate), rightY);
    rightY = writeCostRow(
      'Base Duty Cost:',
      formatReportMoney(math.baseAmountDollars),
      rightY,
    );
    rightY = writeCostRow('Additional Duty Rate:', additionalDutyRate, rightY);
    rightY = writeCostRow(
      'Additional Duty Cost:',
      formatReportMoney(math.additionalDutyDollars),
      rightY,
    );
    rightY = writeCostRow(
      'Total Duty Cost:',
      formatReportMoney(math.dutyCost),
      rightY,
      true,
    );

    if (Number.isFinite(Number(math.mpf)) && Number(math.mpf) > 0) {
      rightY = writeCostRow('MPF:', formatReportMoney(math.mpf), rightY);
    }

    if (Number.isFinite(Number(math.hmf)) && Number(math.hmf) > 0) {
      rightY = writeCostRow('HMF:', formatReportMoney(math.hmf), rightY);
    }

    rightY = writeCostRow(
      'Duties and Fees:',
      formatReportMoney(dutiesAndFees),
      rightY,
      true,
    );
    rightY = writeCostRow(
      'Total Cost:',
      formatReportMoney(math.totalCost),
      rightY,
      true,
    );
  }

  let leftY = writeHeading('USHTS Product Description', leftX, startY, leftW);
  leftY = writeBody(cleanText(base.productDesc), leftX, leftY, leftW);

  leftY = writeHeading(
    activeImportBan
      ? 'Duty Summary'
      : lines.length > 1
      ? 'Duty Summary • Line Breakdown'
      : 'Duty Summary',
    leftX,
    leftY + 2,
    leftW,
  );

  if (activeImportBan) {
    leftY = writeBody(
      `${activeImportBan.narrative}. These goods can't be entered, so no duty applies.`,
      leftX,
      leftY,
      leftW,
    );
    const banRow = importBanResultRow(activeImportBan);
    writeThreeColumnRow(
      [cleanText(banRow.type), cleanText(banRow.code), cleanText(banRow.rate)],
      [0.5, 0.25, 0.25],
      [0, 0.52, 0.78],
    );
    leftY += 8;
    if (activeImportBan.description) {
      leftY = writeBody(
        cleanText(activeImportBan.description),
        leftX,
        leftY,
        leftW,
      );
    }
  } else if (lines.length) {
    lines.forEach((line: any, lineIndex: number) => {
      if (leftY > doc.page.height - 110) {
        doc.addPage();
        doc.y = 70;
        leftY = doc.y;
      }

      const label = cleanText(line?.label || `Line ${lineIndex + 1}`);
      const lineDuty =
        line?.duty !== undefined && line?.duty !== null
          ? formatReportMoney(line.duty)
          : lines.length === 1
          ? formatReportMoney(math.additionalDutyDollars ?? math.dutyCost)
          : '—';

      doc
        .font(FONT_BOLD)
        .fontSize(9.5)
        .fillColor(COLOR.textPrimary)
        .text(label, leftX, leftY, {
          width: leftW * 0.45,
          lineBreak: false,
        });
      doc
        .font(FONT_BOLD)
        .fontSize(9.5)
        .fillColor(COLOR.textPrimary)
        .text(
          `Value: ${formatReportMoney(
            line?.value ?? inputs.productValue,
          )} • Duty: ${lineDuty}`,
          leftX + leftW * 0.45,
          leftY,
          { width: leftW * 0.55, align: 'right' },
        );
      leftY = doc.y + 7;

      const rows = Array.isArray(line?.rows) ? line.rows : [];
      rows.forEach((row: any) => {
        writeThreeColumnRow(
          [
            cleanText(row.type),
            cleanText(row.code || row._codeDigits),
            cleanText(row.rate),
          ],
          [0.48, 0.25, 0.22],
          [0, 0.5, 0.78],
        );
      });

      leftY += lineIndex === lines.length - 1 ? 8 : 12;
    });
  } else {
    leftY = writeBody(
      'No duty summary rows were saved with this tariff ticket.',
      leftX,
      leftY,
      leftW,
    );
  }

  // FTAs and Section 232 exclusions don't matter when the goods can't be
  // imported at all, so both sections are skipped entirely — matching
  // TariffResults.tsx's `!importBan &&` guards around these same blocks.
  if (!activeImportBan) {
    leftY = writeHeading('Free Trade Agreements', leftX, leftY, leftW);
    if (ftaOptions.length) {
      ftaOptions.slice(0, 8).forEach((fta: any) => {
        const code = cleanText(fta.code || fta.id || fta.agreementId);
        writeThreeColumnRow(
          [
            cleanText(fta.name || fta.label || fta.description),
            code || '—',
            code && enabledFtas[code] ? 'Selected' : 'Available',
          ],
          [0.62, 0.16, 0.18],
          [0, 0.64, 0.82],
        );
      });
    } else {
      leftY = writeBody('No applicable FTAs', leftX, leftY, leftW);
    }

    leftY = writeHeading(
      'Potential Tariffs & Exclusions',
      leftX,
      leftY + 4,
      leftW,
    );
    if (optionalExclusions.length) {
      optionalExclusions.slice(0, 5).forEach((item: any) => {
        const code = cleanText(
          item.codePretty || item.codeDigits || item.code,
        );
        const key = item.codeDigits || item.codePretty || item.code;
        const label = cleanText(
          item.label || item.description || item.bd_232_question,
        );
        const rate = cleanText(item.rateDisplay || item.ratePct);
        const selection =
          key && enabledExclusions[key] ? 'Selected' : 'Available';

        writeThreeColumnRow(
          [label || code || '—', code || '—', rate || selection],
          [0.56, 0.18, 0.22],
          [0, 0.58, 0.78],
        );
      });

      if (optionalExclusions.length > 5) {
        leftY = writeBody(
          `${
            optionalExclusions.length - 5
          } more item(s) listed in Applicability & Risk Context.`,
          leftX,
          leftY,
          leftW,
        );
      }
    } else {
      leftY = writeBody(
        'No potential tariffs or exclusions saved for this transaction.',
        leftX,
        leftY,
        leftW,
      );
    }
  }

  doc.y = Math.max(leftY, rightY) + rowGap;
}

function renderTariffInputs(doc: any, data: ITariffTicketReportData): void {
  const snapshot = tariffSnapshotData(data);
  const inputs = snapshot.inputs || {};
  const details = [
    ['HTS Code', inputs.htsCode],
    ['Country of Origin', inputs.originCountry || inputs.originIso2],
    ['Product Value', formatReportMoney(inputs.productValue)],
    ['Entry Date', formatReportDate(inputs.entryDate || inputs.effectDate)],
    ['Loading Date', formatReportDate(inputs.loadingDate || inputs.loadDate)],
    [
      'Transport Method',
      inputs.transportMethod || snapshot.math?.transportMethod,
    ],
    [
      'Amount',
      inputs.amountValue
        ? `${inputs.amountValue} ${inputs.amountUnit || ''}`
        : '',
    ],
    [
      'Amount Inputs',
      inputs.amountInputs && typeof inputs.amountInputs === 'object'
        ? Object.entries(inputs.amountInputs)
            .map(([unit, value]) => `${value} ${unit}`)
            .join(', ')
        : '',
    ],
    ['Material Percentage', inputs.materialPct],
    [
      'Smelt / Pour Country',
      inputs.countryOfSmeltPour || inputs.loadingCountry,
    ],
    [
      'Hide IEEPA Tariffs',
      typeof inputs.hideIeepa === 'boolean'
        ? inputs.hideIeepa
          ? 'Yes'
          : 'No'
        : '',
    ],
  ].filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );

  if (!details.length) return;

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('SAVED INPUTS', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.45);

  const rows = details.map(([label, value]) => [label, cleanText(value)]);
  renderTable(
    doc,
    [
      { header: 'Input', width: 1.4 },
      { header: 'Saved Value', width: 3 },
    ],
    rows,
  );
}

function renderTariffSavedInputsPage(
  doc: any,
  data: ITariffTicketReportData,
  sectionNum = '01',
  sectionTitle = 'Saved Inputs',
  // When true, skip the fresh page if doc.y is already near the top of one —
  // e.g. right after the cover's disclaimer spilled a few lines onto a new
  // page on its own (a long HTS/product description can push the disclaimer
  // down far enough that it no longer fits on the cover page), continuing
  // here avoids an otherwise near-empty page between the disclaimer and this
  // section. Defaults to false so the Unified report (which embeds this same
  // function as one of several numbered sections and always wants one
  // section per page) keeps its original unconditional page break.
  continueIfNearTop = false,
): void {
  if (continueIfNearTop) {
    continueOrAddPage(doc);
  } else {
    doc.addPage();
    doc.y = 70;
  }
  renderSectionHeader(doc, sectionNum, sectionTitle);
  renderTariffInputs(doc, data);
}

function renderTariffApplicability(
  doc: any,
  data: ITariffTicketReportData,
  sectionNum = '03',
): void {
  const snapshot = tariffSnapshotData(data);
  const results = snapshot.results || {};
  const insights = snapshot.insights || {};
  const optionalExclusions = Array.isArray(results.optionalExclusions)
    ? results.optionalExclusions
    : [];
  const enabledExclusions = results.enabledExclusions || {};
  const selectedExclusions = truthyKeys(enabledExclusions);
  const importBans: ImportBan[] = Array.isArray(results.importBans)
    ? results.importBans
    : [];
  const banGateAnswers: Record<string, BanGateAnswer> =
    results.banGateAnswers || {};
  // Derived rather than read off a stored `activeImportBan` field — see the
  // matching comment in renderTariffReadableResults.
  const activeImportBan: ImportBan | null = findActiveImportBan(
    importBans,
    banGateAnswers,
  );
  const importBanQuestions = buildImportBanQuestions(
    importBans,
    banGateAnswers,
  );
  // Section 232 exclusions are moot once the goods can't be imported at all —
  // matches TariffPage.tsx passing `questions={activeImportBan ? [] : exclusionQuestions}`
  // into the same scope-question panel live.
  const exclusionQuestions = activeImportBan
    ? []
    : buildTariffExclusionQuestions(optionalExclusions);
  const riskFlags = Array.isArray(insights.riskFlags) ? insights.riskFlags : [];
  const history = Array.isArray(insights.history) ? insights.history : [];
  const adcvdResults = cleanMultilineText(
    results.adcvdResults || snapshot.adcvdResults,
  );

  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, sectionNum, 'Applicability & Risk Context');

  if (importBanQuestions.length) {
    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text('IMPORT BAN APPLICABILITY', PAGE_MARGIN, doc.y, {
        characterSpacing: 0.6,
      });
    doc.moveDown(0.45);
    importBanQuestions.forEach((question) => {
      renderTariffQuestionCard(
        doc,
        question.helperText,
        question.prompt,
        question.options,
        question.selected,
      );
    });
    doc.moveDown(0.2);
  }

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('EXCLUSION APPLICABILITY ASSISTANT', PAGE_MARGIN, doc.y, {
      characterSpacing: 0.6,
    });
  doc.moveDown(0.45);
  if (exclusionQuestions.length) {
    exclusionQuestions.forEach((question) => {
      const selectedOption = selectedTariffQuestionOption(
        question,
        enabledExclusions,
      );
      renderTariffQuestionCard(
        doc,
        question.helperText,
        question.prompt,
        question.options,
        selectedOption?.id,
      );
    });
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No exclusions applicable for assistance found for this transaction.',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(0.8);
  }

  if (selectedExclusions.length) {
    renderLabeledBlock(
      doc,
      'ENABLED CODES',
      selectedExclusions.length ? selectedExclusions.join(', ') : 'None',
    );
  } else if (exclusionQuestions.length) {
    renderLabeledBlock(doc, 'ENABLED CODES', 'None');
  }

  if (adcvdResults) {
    renderLabeledBlock(doc, 'AD/CVD RESULTS', adcvdResults);
  }

  doc.moveDown(0.25);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('RISK FLAGS', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.45);
  if (riskFlags.length) {
    riskFlags.forEach((flag: any) => {
      const title = cleanText(flag.title);
      const description = cleanText(flag.description);
      const severity = cleanText(flag.severity);
      const width = doc.page.width - 2 * PAGE_MARGIN;
      // Titles come from screening list/case names and can run long enough
      // to wrap — measure them instead of assuming a single line, the same
      // way the description below already is, so a wrapped title can't push
      // the description past the card's bottom edge.
      const titleHeight = Math.min(
        26,
        doc
          .font(FONT_BOLD)
          .fontSize(10)
          .heightOfString(title, { width: width - 24 }),
      );
      const descriptionOffset = 10 + titleHeight + 8;
      const descriptionHeight = doc
        .font(FONT_REGULAR)
        .fontSize(9.5)
        .heightOfString(description, {
          width: width - 24,
          lineGap: 2,
        });
      const cardHeight = Math.max(
        54,
        descriptionOffset + descriptionHeight + 14,
      );

      if (doc.y + cardHeight > doc.page.height - 58) {
        doc.addPage();
        doc.y = 70;
      }

      const y = doc.y;
      const fill =
        severity === 'high'
          ? '#FEF3F2'
          : severity === 'medium'
          ? '#FFFAEB'
          : '#EFF6FF';
      const stroke =
        severity === 'high'
          ? COLOR.riskHigh
          : severity === 'medium'
          ? COLOR.riskMedium
          : COLOR.primary;

      doc.save();
      doc
        .roundedRect(PAGE_MARGIN, y, width, cardHeight, 4)
        .fillAndStroke(fill, stroke);
      doc.restore();
      doc
        .font(FONT_BOLD)
        .fontSize(10)
        .fillColor(COLOR.textPrimary)
        .text(title, PAGE_MARGIN + 12, y + 10, {
          width: width - 24,
          height: titleHeight,
          ellipsis: true,
        });
      doc
        .font(FONT_REGULAR)
        .fontSize(9.5)
        .fillColor(COLOR.textSecondary)
        .text(description, PAGE_MARGIN + 12, y + descriptionOffset, {
          width: width - 24,
          lineGap: 2,
        });
      doc.y = y + cardHeight + 10;
    });
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No flags found for AD/CVD, Forced Labor, or PGA.',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(0.8);
  }

  doc.moveDown(0.25);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('HTS CODE HISTORY', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.45);
  if (history.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'Recent and upcoming changes to this HTS classification.',
        PAGE_MARGIN,
        doc.y,
        {
          width: doc.page.width - 2 * PAGE_MARGIN,
        },
      );
    doc.moveDown(0.5);
    history.forEach((item: any) => {
      renderLabeledBlock(
        doc,
        cleanText(item.date).toUpperCase(),
        cleanText(item.summary),
      );
    });
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No recent changes detected for this HTS code.',
        PAGE_MARGIN,
        doc.y,
      );
  }
}

function renderTariffExplanationAndDisclaimer(
  doc: any,
  data: ITariffTicketReportData,
  sectionNum = '04',
): void {
  const snapshot = tariffSnapshotData(data);
  const explanation = cleanMultilineText(snapshot.explanation?.text);

  if (!explanation) return;

  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, sectionNum, 'Notes');
  renderLabeledBlock(doc, 'EXPLANATION', explanation);
}

function renderClassificationChatHistory(
  doc: any,
  messages: Array<{ role?: string; text?: string }>,
): void {
  if (doc.y > doc.page.height - 130) {
    doc.addPage();
    doc.y = 70;
  }

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('CHAT HISTORY', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.45);

  const width = doc.page.width - 2 * PAGE_MARGIN;
  messages.forEach((message) => {
    const role = cleanText(message.role || 'Message');
    const text = cleanClassificationPdfText(message.text);
    if (!text) return;

    const isUser = role.toLowerCase().includes('user');
    const textHeight = doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .heightOfString(text, {
        width: width - 18,
        lineGap: 3,
      });
    const blockHeight = Math.max(42, textHeight + 28);

    if (blockHeight > doc.page.height - 128) {
      if (doc.y > doc.page.height - 120) {
        doc.addPage();
        doc.y = 70;
      }
      doc
        .font(FONT_BOLD)
        .fontSize(8.5)
        .fillColor(isUser ? COLOR.primary : COLOR.textSecondary)
        .text(role.toUpperCase(), PAGE_MARGIN, doc.y, {
          width: width,
          characterSpacing: 0.5,
        });
      doc.moveDown(0.2);
      renderPaginatedBodyText(doc, text, {
        width: width,
        fontSize: 10,
        lineGap: 3,
      });
      doc.moveDown(0.6);
      return;
    }

    if (doc.y + blockHeight > doc.page.height - 58) {
      doc.addPage();
      doc.y = 70;
    }

    const y = doc.y;
    doc.save();
    doc
      .roundedRect(PAGE_MARGIN, y, width, blockHeight, 4)
      .fillAndStroke(isUser ? '#EFF6FF' : COLOR.cardBg, COLOR.cardBorder);
    doc.restore();

    doc
      .font(FONT_BOLD)
      .fontSize(8.5)
      .fillColor(isUser ? COLOR.primary : COLOR.textSecondary)
      .text(role.toUpperCase(), PAGE_MARGIN + 9, y + 8, {
        width: width - 18,
        characterSpacing: 0.5,
      });
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(text, PAGE_MARGIN + 9, y + 22, {
        width: width - 18,
        lineGap: 3,
      });

    doc.y = y + blockHeight + 8;
  });
}

function getClassificationFinalReportRows(
  data: IClassificationTicketReportData,
): Array<{ label: string; value: string; list?: string[] }> {
  const payload = data.finalReport?.payload || {};
  const generated = data.finalReport?.generated || {};
  const rows: Array<{ label: string; value: string; list?: string[] }> = [
    {
      label: data.commodity?.codeLabel || 'Classification Code',
      value: payload.hts_code || generated.hts_code || data.commodity?.code,
    },
    {
      label: 'Generated Commodity Description',
      value: generated.cmdt_desc || data.commodity?.name,
    },
    {
      label: 'Summary',
      value: payload.summary_text || generated.summary_text,
    },
    {
      label: 'Legal Basis',
      value: payload.legal_basis_summary || generated.legal_basis_summary,
    },
    {
      label: 'Confidence Note',
      value: payload.confidence_note || generated.confidence_note,
    },
  ];

  if (
    Array.isArray(payload.reasoning_trace) &&
    payload.reasoning_trace.length
  ) {
    rows.push({
      label: 'Reasoning Trace',
      value: '',
      list: payload.reasoning_trace.map((item: any) => cleanText(item)),
    });
  }

  if (
    Array.isArray(payload.facts_relied_on) &&
    payload.facts_relied_on.length
  ) {
    rows.push({
      label: 'Facts Relied On',
      value: '',
      list: payload.facts_relied_on.map((item: any) => cleanText(item)),
    });
  }

  return rows.filter((row) => row.value || row.list?.length);
}

function renderLegacyClassificationResult(
  doc: any,
  data: IClassificationTicketReportData,
): void {
  renderPaginatedLabeledBlock(
    doc,
    'CLASSIFICATION DESCRIPTION',
    cleanText(data.classificationValue) ||
      'No classification description saved.',
  );

  // Material / Intended Use / Physical State are HTS-commodity attributes; the export
  // workflow never captures them. Only render this block when at least one is present
  // (so export tickets don't show a block of "N/A"s from another tool's design).
  const optionalDetailValues: Array<[string, string]> = [
    ['Material', cleanText(data.commodity?.material)],
    ['Intended Use', cleanText(data.commodity?.intendedUse)],
    ['Physical State', cleanText(data.commodity?.physicalState)],
  ];
  if (optionalDetailValues.some(([, v]) => v)) {
    renderPaginatedLabeledBlock(
      doc,
      'OPTIONAL DETAILS',
      optionalDetailValues.map(([k, v]) => `${k}: ${v || 'N/A'}`).join('\n'),
    );
  }

  const resultLines: string[] = [];
  // Avoid repeating the description when it already appears as the CLASSIFICATION
  // DESCRIPTION above (the export case, where both are the product description).
  const productName = cleanText(data.commodity?.name);
  if (productName && productName !== cleanText(data.classificationValue)) {
    resultLines.push(`Product Description: ${productName}`);
  }
  resultLines.push(
    `${cleanText(data.commodity?.codeLabel) || 'Classification Code'}: ${
      cleanText(data.commodity?.code) || 'N/A'
    }`,
  );
  renderPaginatedLabeledBlock(doc, 'RESULTS', resultLines.join('\n'));

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('CLARIFYING QUESTIONS', PAGE_MARGIN, doc.y, {
      characterSpacing: 0.6,
    });
  doc.moveDown(0.4);

  const followUps = (data.followUps || []).filter((item) =>
    cleanText(item.question || item.answer),
  );

  if (followUps.length) {
    const width = doc.page.width - 2 * PAGE_MARGIN;
    followUps.forEach((item, index) => {
      const question = cleanText(item.question || 'Question');
      const answer = cleanText(item.answer) || 'No answer saved';
      const questionHeight = doc
        .font(FONT_REGULAR)
        .fontSize(9.5)
        .heightOfString(question, {
          width: width - 28,
          lineGap: 2,
        });
      const answerHeight = doc
        .font(FONT_REGULAR)
        .fontSize(10)
        .heightOfString(answer, {
          width: width - 28,
          lineGap: 2,
        });
      const blockHeight = Math.max(70, questionHeight + answerHeight + 46);

      if (blockHeight > doc.page.height - 128) {
        if (doc.y > doc.page.height - 120) {
          doc.addPage();
          doc.y = 70;
        }
        doc
          .font(FONT_BOLD)
          .fontSize(8)
          .fillColor(COLOR.primary)
          .text(`QUESTION ${index + 1}`, PAGE_MARGIN, doc.y, {
            width: width,
            characterSpacing: 0.5,
          });
        doc.moveDown(0.2);
        renderPaginatedBodyText(doc, question, {
          width: width,
          fontSize: 9.5,
          lineGap: 2,
        });
        doc.moveDown(0.25);
        doc
          .font(FONT_BOLD)
          .fontSize(8)
          .fillColor(COLOR.textSecondary)
          .text('ANSWER GIVEN', PAGE_MARGIN, doc.y, {
            width: width,
            characterSpacing: 0.5,
          });
        doc.moveDown(0.2);
        renderPaginatedBodyText(doc, answer, {
          width: width,
          fontSize: 10,
          lineGap: 2,
        });
        doc.moveDown(0.6);
        return;
      }

      if (doc.y + blockHeight > doc.page.height - 58) {
        doc.addPage();
        doc.y = 70;
      }

      const y = doc.y;
      doc.save();
      doc
        .roundedRect(PAGE_MARGIN, y, width, blockHeight, 4)
        .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
      doc.restore();

      doc
        .font(FONT_BOLD)
        .fontSize(8)
        .fillColor(COLOR.primary)
        .text(`QUESTION ${index + 1}`, PAGE_MARGIN + 10, y + 10, {
          width: width - 20,
          characterSpacing: 0.5,
        });
      doc
        .font(FONT_REGULAR)
        .fontSize(9.5)
        .fillColor(COLOR.textPrimary)
        .text(question, PAGE_MARGIN + 10, y + 25, {
          width: width - 20,
          lineGap: 2,
        });
      doc
        .font(FONT_BOLD)
        .fontSize(8)
        .fillColor(COLOR.textSecondary)
        .text('ANSWER GIVEN', PAGE_MARGIN + 10, doc.y + 7, {
          width: width - 20,
          characterSpacing: 0.5,
        });
      doc
        .font(FONT_REGULAR)
        .fontSize(10)
        .fillColor(COLOR.textPrimary)
        .text(answer, PAGE_MARGIN + 10, doc.y + 3, {
          width: width - 20,
          lineGap: 2,
        });

      doc.y = y + blockHeight + 8;
    });
    doc.moveDown(0.4);
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No clarifying questions were saved with this ticket.',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(0.8);
  }
}

function parseLegacyClassificationOutput(
  output: string,
): Array<{ title: string; body: string[] }> {
  const blocks: Array<{ title: string; body: string[] }> = [];
  let current: { title: string; body: string[] } | null = null;

  cleanMultilineText(output)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const headingMatch = line.match(/^#{1,6}\s*(.+)$/);
      if (headingMatch) {
        current = {
          title: cleanPdfInlineMarkdown(headingMatch[1]),
          body: [],
        };
        blocks.push(current);
        return;
      }

      if (!current) {
        current = { title: '', body: [] };
        blocks.push(current);
      }

      current.body.push(cleanPdfInlineMarkdown(line));
    });

  return blocks.filter((block) => block.title || block.body.length);
}

function renderLegacyClassificationOutput(doc: any, output: string): void {
  const blocks = parseLegacyClassificationOutput(output);

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('CLASSIFICATION OUTPUT', PAGE_MARGIN, doc.y, {
      characterSpacing: 0.6,
    });
  doc.moveDown(0.45);

  if (!blocks.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No final report or classification output was saved with this ticket.',
        PAGE_MARGIN,
        doc.y,
        { width: doc.page.width - 2 * PAGE_MARGIN },
      );
    doc.moveDown(0.8);
    return;
  }

  const width = doc.page.width - 2 * PAGE_MARGIN;
  blocks.forEach((block) => {
    const titleHeight = block.title
      ? doc
          .font(FONT_BOLD)
          .fontSize(9.5)
          .heightOfString(block.title, {
            width: width - 20,
            lineGap: 2,
          })
      : 0;
    const body = cleanClassificationPdfText(block.body.join('\n'));
    const bodyHeight = body
      ? doc
          .font(FONT_REGULAR)
          .fontSize(10.3)
          .heightOfString(body, {
            width: width - 20,
            lineGap: 3.5,
          })
      : 0;
    const blockHeight = Math.max(42, titleHeight + bodyHeight + 24);

    // A card taller than one page cannot be drawn as a single rectangle, and
    // allowing its text call to auto-paginate causes the continuation to
    // inherit the report footer's muted 8pt style. Use the page-safe text
    // renderer for oversized classification output blocks.
    if (blockHeight > doc.page.height - 128) {
      if (doc.y > doc.page.height - 120) {
        doc.addPage();
        doc.y = 70;
      }

      if (block.title) {
        doc
          .font(FONT_BOLD)
          .fontSize(9.5)
          .fillColor(COLOR.textPrimary)
          .text(block.title, PAGE_MARGIN, doc.y, {
            width: width,
            lineGap: 2,
          });
        doc.moveDown(0.35);
      }
      if (body) {
        renderPaginatedBodyText(doc, body, {
          width: width,
          fontSize: 10.3,
          lineGap: 3.5,
        });
      }
      doc.moveDown(0.6);
      return;
    }

    if (doc.y + blockHeight > doc.page.height - 58) {
      doc.addPage();
      doc.y = 70;
    }

    const y = doc.y;
    doc.save();
    doc
      .roundedRect(PAGE_MARGIN, y, width, blockHeight, 4)
      .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
    doc.restore();

    let cursorY = y + 10;
    if (block.title) {
      doc
        .font(FONT_BOLD)
        .fontSize(9.5)
        .fillColor(COLOR.textPrimary)
        .text(block.title, PAGE_MARGIN + 10, cursorY, {
          width: width - 20,
          lineGap: 2,
        });
      cursorY = doc.y + 5;
    }

    if (body) {
      doc
        .font(FONT_REGULAR)
        .fontSize(10.3)
        .fillColor(COLOR.textPrimary)
        .text(body, PAGE_MARGIN + 10, cursorY, {
          width: width - 20,
          lineGap: 3.5,
        });
    }

    doc.y = y + blockHeight + 8;
  });

  doc.moveDown(0.2);
}

function renderClassificationFinalReport(
  doc: any,
  data: IClassificationTicketReportData,
  sectionNum = '01',
  // See the matching parameter on renderTariffSavedInputsPage: true for the
  // standalone Classification PDF's direct call (right after the cover, so a
  // disclaimer that spilled onto a fresh page can be continued on rather
  // than wasted), false (default) for the Unified report, which embeds this
  // as one of several numbered sections and always wants one per page.
  continueIfNearTop = false,
): void {
  if (continueIfNearTop) {
    continueOrAddPage(doc);
  } else {
    doc.addPage();
    doc.y = 70;
  }
  renderSectionHeader(doc, sectionNum, 'Classification Result');

  if (data.isLegacyTicket) {
    renderLegacyClassificationResult(doc, data);
    // Export-workflow tickets already present the EAR / CCL result in their dedicated
    // "EAR / CCL Check" section (renderUnifiedWorkflow), so the free-text CLASSIFICATION
    // OUTPUT block here is redundant — skip it for export. HTS / legacy classification
    // tickets (docType !== 'Export Unified') still render it as their primary output.
    if (data.docType !== 'Export Unified') {
      const streamText = cleanMultilineText(
        classificationStreamText(data.aiStream),
      );
      renderLegacyClassificationOutput(doc, streamText);
    }
    return;
  }

  const rows = getClassificationFinalReportRows(data);
  if (rows.length) {
    rows.forEach((row) => {
      if (row.list?.length) {
        doc
          .font(FONT_BOLD)
          .fontSize(9)
          .fillColor(COLOR.textSecondary)
          .text(row.label.toUpperCase(), PAGE_MARGIN, doc.y, {
            characterSpacing: 0.6,
          });
        doc.moveDown(0.4);
        renderOrderedList(doc, row.list);
      } else {
        renderPaginatedLabeledBlock(
          doc,
          row.label.toUpperCase(),
          cleanClassificationPdfText(row.value),
        );
      }
    });
    return;
  }

  const streamText = cleanMultilineText(
    classificationStreamText(data.aiStream),
  );
  if (streamText) {
    renderPaginatedLabeledBlock(doc, 'CLASSIFICATION OUTPUT', streamText);
    return;
  }

  renderPaginatedLabeledBlock(
    doc,
    'CLASSIFICATION OUTPUT',
    'No final report or classification output was saved with this ticket.',
  );
}

function normalizeClassificationNotes(notes: any): string[] {
  const lines = String(notes ?? '')
    .replace(/[\[\]"]/g, '')
    .replace(/,(\s)?$/gm, '')
    .replace(/^°/gm, '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => cleanText(line))
    .filter(Boolean)
    .filter(
      (line) =>
        !/^Harmonized Tariff Schedule/i.test(line) &&
        !/^Annotated for Statistical Reporting/i.test(line) &&
        !/^Purposes$/i.test(line) &&
        !/^[A-Z]+-\d+$/i.test(line) &&
        !/^Notes\s*\(con\.\)$/i.test(line),
    );

  const paragraphs: string[] = [];
  let current = '';

  lines.forEach((line) => {
    const startsNewClause =
      /^(\d+\.|\([a-z0-9]+\)|[IVX]+\.)\s+/i.test(line) ||
      /^[A-Z][A-Za-z\s]{2,}:$/.test(line);

    if (!current || startsNewClause) {
      if (current) paragraphs.push(current);
      current = line;
      return;
    }

    current = `${current} ${line}`;
  });

  if (current) paragraphs.push(current);
  return paragraphs;
}

function renderReadableNotesBlock(
  doc: any,
  label: string,
  paragraphs: string[],
): void {
  if (!paragraphs.length) return;

  if (doc.y > doc.page.height - 120) {
    doc.addPage();
    doc.y = 70;
  }

  doc
    .font(FONT_BOLD)
    .fontSize(10)
    .fillColor(COLOR.textSecondary)
    .text(label, PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.45);

  const width = doc.page.width - 2 * PAGE_MARGIN;
  paragraphs.forEach((paragraph) => {
    const textHeight = doc
      .font(FONT_REGULAR)
      .fontSize(10.8)
      .heightOfString(paragraph, {
        width: width,
        lineGap: 4,
      });

    if (doc.y + textHeight > doc.page.height - 58) {
      doc.addPage();
      doc.y = 70;
    }

    renderPaginatedBodyText(doc, paragraph, {
      width: width,
      fontSize: 10.8,
      lineGap: 4,
    });
    doc.moveDown(0.35);
  });
}

function renderClassificationReferences(
  doc: any,
  data: IClassificationTicketReportData,
  sectionNum = '02',
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, sectionNum, 'Reference Materials');

  const chatMessages = (data.chatHistory || []).filter((message) =>
    cleanText(message.text),
  );
  if (chatMessages.length) {
    renderClassificationChatHistory(doc, chatMessages);
    doc.moveDown(0.4);
  }

  const crossRulings = (data.crossRulings || []).filter((item) => item?.url);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('CROSS RULINGS', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.4);

  if (crossRulings.length) {
    const rows = crossRulings
      .slice(0, MAX_ROWS)
      .map((item, i) => [
        i + 1,
        cleanClassificationPdfText(item.title || item.url),
        typeof item.relevance_percent === 'number'
          ? `${item.relevance_percent}%`
          : '—',
        cleanClassificationPdfText(
          [item.relevance_note, item.url].filter(Boolean).join(' | '),
        ),
      ]);

    renderTable(
      doc,
      [
        { header: '#', width: 0.55, align: 'right', noWrap: true },
        { header: 'Ruling', width: 3 },
        { header: 'Relevance', width: 1, align: 'right', noWrap: true },
        { header: 'Note / URL', width: 3.4 },
      ],
      rows,
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No CROSS rulings were saved with this ticket.',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(0.8);
  }

  const chapterNotes = normalizeClassificationNotes(data.chapterNotes);
  if (chapterNotes.length) {
    doc.moveDown(0.4);
    renderReadableNotesBlock(doc, 'SECTION AND CHAPTER NOTES', chapterNotes);
  }
}

function renderProductMatchesTable(
  doc: any,
  data: IProductTicketReportData,
  sectionNum = '02',
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, sectionNum, 'Top Product Screening Matches');

  doc
    .font(FONT_REGULAR)
    .fontSize(10)
    .fillColor(COLOR.textPrimary)
    .text(
      'The highest-priority saved product-screening matches for this ticket, grouped into the same report design used by Person / Company tickets.',
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );
  doc.moveDown(0.8);

  const matches = decorateProductRows(data.productDocs);
  if (!matches.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text(
        'No product matches were saved with this screen.',
        PAGE_MARGIN,
        doc.y,
      );
    return;
  }

  const cols: Col[] = [
    { header: '#', width: 0.55, align: 'right', noWrap: true },
    { header: 'Product / Match', width: 2.6 },
    { header: 'HTS / HS', width: 1.4 },
    { header: 'Country', width: 1.3 },
    { header: 'BITE Lens', width: 1.9 },
    { header: 'List / Agency', width: 2.2 },
  ];

  const rows = matches.map((match, i) => [
    i + 1,
    match.product,
    match.htsCode,
    match.country,
    match.lens,
    [match.listName, match.agency]
      .filter((value) => value && value !== '—')
      .join(' / ') || '—',
  ]);

  renderTable(doc, cols, rows);
}

function renderMatchesTable(
  doc: any,
  sectionNum: string,
  title: string,
  intro: string,
  matches: ReportMatch[],
  includeRelatedEntity: boolean,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, sectionNum, title);

  doc
    .font(FONT_REGULAR)
    .fontSize(10)
    .fillColor(COLOR.textPrimary)
    .text(intro, { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 });
  doc.moveDown(0.8);

  if (!matches.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text('No matches were saved with this screen.', PAGE_MARGIN, doc.y);
    return;
  }

  const cols: Col[] = includeRelatedEntity
    ? [
        { header: '#', width: 0.7, align: 'right', noWrap: true },
        { header: 'Entity Name', width: 3 },
        { header: 'Type', width: 1.3 },
        { header: 'Related Entity', width: 2.4 },
        { header: 'Country', width: 1.6 },
        { header: 'Match', width: 1, align: 'right', noWrap: true },
      ]
    : [
        { header: '#', width: 0.7, align: 'right', noWrap: true },
        { header: 'Entity Name', width: 3 },
        { header: 'Type', width: 1.3 },
        { header: 'Country', width: 1.6 },
        { header: 'List Name', width: 2.4 },
        { header: 'Match', width: 1, align: 'right', noWrap: true },
      ];

  const rows = matches.map((m, i) => {
    const scoreText = m.score === null ? '—' : `${m.score}%`;
    return includeRelatedEntity
      ? [i + 1, m.name, m.entityType, m.relatedEntity, m.country, scoreText]
      : [i + 1, m.name, m.entityType, m.country, m.listName, scoreText];
  });

  renderTable(doc, cols, rows);
}

const boRiskColor = (label: string): string => {
  const lower = String(label || '').toLowerCase();
  if (
    lower.includes('confirm') ||
    lower.includes('sanction') ||
    lower.includes('high')
  ) {
    return COLOR.riskHigh;
  }
  if (lower.includes('low') || lower.includes('none') || lower.includes('no')) {
    return COLOR.riskLow;
  }
  if (lower.includes('medium') || lower.includes('review')) {
    return COLOR.riskMedium;
  }
  return COLOR.textSecondary;
};

const getBoFindingTitle = (finding: any, index: number): string =>
  typeof finding === 'string'
    ? `Finding ${index + 1}`
    : cleanText(finding?.title || finding?.risk_type || `Finding ${index + 1}`);

const getBoFindingText = (finding: any): string =>
  typeof finding === 'string'
    ? cleanText(finding)
    : cleanText(
        finding?.finding ||
          finding?.description ||
          finding?.summary ||
          finding?.title ||
          'Finding saved with this investigation.',
      );

function renderBeneficialOwnershipCover(
  doc: any,
  data: IBeneficialOwnershipTicketReportData,
  logo: ArrayBuffer | string | null,
): void {
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* ignore logo failures */
    }
  }

  const titleY = 220;
  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('BITE Beneficial Ownership Report', {
      width: titleWidth,
      lineGap: 2,
    });
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text('BITE Beneficial Ownership Report', PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const metaY = titleY + Math.max(titleHeight, 46) + 44;
  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 150;
  const valueW = doc.page.width - valueX - PAGE_MARGIN;
  const meta: Array<[string, string]> = [
    ['TICKET', cleanText(data.ticketName) || '-'],
    ['ENTITY', cleanText(data.entityName) || '-'],
  ];

  if (data.country) {
    meta.push(['COUNTRY', cleanText(data.country)]);
  }

  meta.push([
    'SECOND-LAYER SEARCH',
    data.secondLayer ? 'Enabled' : 'Disabled',
  ]);
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push([
    'PREPARED BY',
    cleanText(data.preparedBy) || 'BITE Beneficial Ownership',
  ]);

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, metaY - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, metaY - 6)
    .stroke();

  let y = metaY;
  meta.forEach(([label, value]) => {
    // A long value (e.g. a saved ticket's free-text name) must wrap within
    // its own row rather than overflowing a fixed row height into the next
    // row — the row grows to fit, capped and ellipsis-truncated beyond ~3
    // lines.
    const measuredHeight = doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .heightOfString(value, { width: valueW, lineGap: 2 });
    const rowH = Math.max(26, Math.min(measuredHeight, 40) + 14);

    doc
      .font(FONT_BOLD)
      .fontSize(8)
      .fillColor(COLOR.textSecondary)
      .text(label, labelX, y, { width: 120, characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(value, valueX, y - 1, {
        width: valueW,
        height: rowH - 8,
        ellipsis: true,
      });
    y += rowH;
  });

  const noteY = y + 34;
  doc
    .roundedRect(PAGE_MARGIN, noteY, doc.page.width - 2 * PAGE_MARGIN, 78, 5)
    .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
  doc
    .font(FONT_BOLD)
    .fontSize(10)
    .fillColor(COLOR.primary)
    .text('REPORT SCOPE', PAGE_MARGIN + 16, noteY + 14, {
      characterSpacing: 0.6,
    });
  doc
    .font(FONT_REGULAR)
    .fontSize(10)
    .fillColor(COLOR.textSecondary)
    .text(
      'This report summarizes the final saved Beneficial Ownership workflow result, including identified owners, the ownership tree, risk findings, connected entities, sanctions context, and supporting sources.',
      PAGE_MARGIN + 16,
      noteY + 34,
      { width: doc.page.width - 2 * PAGE_MARGIN - 32, lineGap: 2 },
    );
}

function renderBeneficialOwnershipOverview(
  doc: any,
  data: IBeneficialOwnershipTicketReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '01', 'Investigation Overview');

  renderKpiCards(doc, [
    {
      value: fmtCount(data.owners?.length || 0),
      label: 'OWNERS FOUND',
    },
    {
      value: fmtCount(data.keyFindings?.length || 0),
      label: 'KEY FINDINGS',
      valueColor: boRiskColor(data.sanctionsFlag || ''),
    },
    {
      value: fmtCount(data.associatedEntities?.length || 0),
      label: 'CONNECTED ENTITIES',
    },
    {
      value: fmtCount(data.supportingLinks?.length || 0),
      label: 'SOURCES',
    },
  ]);

  // Paginated (not plain renderLabeledBlock): these bodies are long enough to
  // overflow a page, and a plain doc.text() call left the overflow in the
  // header chrome's muted color instead of textPrimary (see the comment on
  // renderPaginatedBodyText) — the "faint text after a page break" bug.
  // The agent's own 3-6 sentence executive summary leads the report when the
  // run saved one; older runs predate the field and go straight to the full
  // narrative. Beneficial Ownership only — no other report type has it.
  if (cleanText(data.executiveSummary || '')) {
    renderPaginatedLabeledBlock(
      doc,
      'EXECUTIVE SUMMARY',
      reflowIntoPdfParagraphs(data.executiveSummary),
    );
  }
  renderPaginatedLabeledBlock(
    doc,
    'FINAL SUMMARY',
    reflowIntoPdfParagraphs(data.summary || 'No final summary was saved.'),
  );
  renderPaginatedLabeledBlock(
    doc,
    'SANCTIONS STATUS',
    cleanText(data.sanctionsFlag || 'Not available.'),
  );
  renderPaginatedLabeledBlock(
    doc,
    'SANCTIONS SUMMARY',
    reflowIntoPdfParagraphs(
      data.sanctionsSummary || 'No sanctions summary was saved.',
    ),
  );
}

function renderBeneficialOwnershipOwners(
  doc: any,
  data: IBeneficialOwnershipTicketReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '02', 'Ownership Found');

  const owners = Array.isArray(data.owners) ? data.owners : [];
  if (!owners.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text('No ownership records were saved with this investigation.');
    return;
  }

  renderTable(
    doc,
    [
      { header: '#', width: 0.6, align: 'right', noWrap: true },
      { header: 'Name', width: 2.6 },
      { header: 'Relationship', width: 2.2 },
      { header: 'Ownership', width: 1.1, align: 'right' },
    ],
    owners.map((owner: any, index: number) => [
      index + 1,
      cleanText(owner?.name || 'Unknown owner'),
      cleanText(
        owner?.relationship_to_subject ||
          owner?.relationship ||
          'Not specified',
      ),
      owner?.ownership_percentage
        ? cleanText(owner.ownership_percentage).includes('%')
          ? cleanText(owner.ownership_percentage)
          : `${cleanText(owner.ownership_percentage)}%`
        : cleanText(owner?.ownership || 'Not specified'),
    ]),
  );
}

const BO_PDF_TREE_MAX_DEPTH = 4;
const BO_PDF_TREE_MAX_ROWS = 250;
// How far a jurisdiction branch's bands sit in from its company card.
const BO_PDF_BRANCH_INDENT = 16;

const beneficialOwnershipTreeStatus = (
  row: IBeneficialOwnershipPdfTreeRow,
): string => {
  if (row.status === 'cycle') {
    return 'Cycle back to an entity already in this branch';
  }
  if (row.status === 'reference') {
    return 'Connections shown earlier in this tree';
  }
  if (row.status === 'depth-limit') {
    return `${fmtCount(row.childCount)} deeper connection${
      row.childCount === 1 ? '' : 's'
    } available in Saved Work`;
  }
  return row.childCount
    ? `${fmtCount(row.childCount)} connection${row.childCount === 1 ? '' : 's'}`
    : '';
};

function renderBeneficialOwnershipTree(
  doc: any,
  data: IBeneficialOwnershipTicketReportData,
  sectionNum = '03',
): void {
  const graph = data.ownershipGraph;
  if (!graph || graph.isEmpty || !graph.nodes[graph.subject]) {
    return;
  }

  const tree = buildBeneficialOwnershipPdfTree(
    graph,
    BO_PDF_TREE_MAX_DEPTH,
    BO_PDF_TREE_MAX_ROWS,
  );
  const pageBottom = () => doc.page.height - 62;

  const startPage = (continued = false) => {
    doc.addPage();
    doc.y = 70;
    renderSectionHeader(
      doc,
      sectionNum,
      continued ? 'Ownership Tree (continued)' : 'Ownership Tree',
    );
  };

  const renderGroupHeader = (
    group: IBeneficialOwnershipPdfTreeGroup,
    continued = false,
  ) => {
    const y = doc.y;
    const x = PAGE_MARGIN + (group.branch ? BO_PDF_BRANCH_INDENT : 0);
    const width = doc.page.width - PAGE_MARGIN - x;
    // Branched trees repeat the country in every band header, so a header
    // continued on a new page still says whose structure it is.
    const country = group.branch ? cleanText(group.branch.node.country) : '';
    doc
      .roundedRect(x, y, width, 24, 4)
      .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
    doc
      .font(FONT_BOLD)
      .fontSize(8.5)
      .fillColor(COLOR.primary)
      .text(
        `${country ? `${country.toUpperCase()} · ` : ''}${group.label.toUpperCase()} · ${fmtCount(
          group.rootCount,
        )}${continued ? ' · CONTINUED' : ''}`,
        x + 10,
        y + 8,
        { width: width - 20, characterSpacing: 0.45, lineBreak: false },
      );
    doc.y = y + 34;
  };

  // A jurisdiction branch's company: the card its band groups hang under.
  const renderBranchCard = (group: IBeneficialOwnershipPdfTreeGroup) => {
    const branch = group.branch;
    if (!branch) {
      return;
    }
    const width = doc.page.width - 2 * PAGE_MARGIN;
    const name = cleanText(branch.node.name || branch.key);
    const nameWidth = width - (branch.node.flagged ? 110 : 28);
    const nameHeight = Math.min(
      30,
      doc
        .font(FONT_BOLD)
        .fontSize(11.5)
        .heightOfString(name, { width: nameWidth, lineGap: 1 }),
    );
    const metadata = [
      cleanText(branch.node.type || ''),
      `${fmtCount(branch.connectionCount)} connection${
        branch.connectionCount === 1 ? '' : 's'
      }`,
    ]
      .filter(Boolean)
      .join(' · ');
    const cardHeight = 24 + nameHeight + 4 + 12 + 10;
    if (doc.y + cardHeight + 58 > pageBottom()) {
      startPage(true);
    }

    const y = doc.y;
    const border = branch.node.flagged ? COLOR.riskHigh : COLOR.primary;
    doc
      .roundedRect(PAGE_MARGIN, y, width, cardHeight, 6)
      .fillAndStroke(branch.node.flagged ? '#FEF3F2' : COLOR.cardBg, border);
    doc
      .font(FONT_BOLD)
      .fontSize(7.5)
      .fillColor(COLOR.primary)
      .text(
        (cleanText(branch.node.country) || 'Country not stated').toUpperCase(),
        PAGE_MARGIN + 14,
        y + 10,
        { width: width - 28, characterSpacing: 0.5, lineBreak: false },
      );
    if (branch.node.flagged) {
      doc
        .font(FONT_BOLD)
        .fontSize(7.5)
        .fillColor(COLOR.riskHigh)
        .text('SANCTIONED', PAGE_MARGIN + width - 94, y + 10, {
          width: 80,
          align: 'right',
          characterSpacing: 0.35,
          lineBreak: false,
        });
    }
    doc
      .font(FONT_BOLD)
      .fontSize(11.5)
      .fillColor(COLOR.textPrimary)
      .text(name, PAGE_MARGIN + 14, y + 24, {
        width: nameWidth,
        height: nameHeight,
        lineGap: 1,
        ellipsis: true,
      });
    doc
      .font(FONT_REGULAR)
      .fontSize(8.5)
      .fillColor(COLOR.textSecondary)
      .text(metadata, PAGE_MARGIN + 14, y + 28 + nameHeight, {
        width: width - 28,
        lineBreak: false,
        ellipsis: true,
      });
    doc.y = y + cardHeight + 12;
  };

  const renderRow = (
    row: IBeneficialOwnershipPdfTreeRow,
    group: IBeneficialOwnershipPdfTreeGroup,
  ) => {
    const indent =
      Math.max(0, row.depth - 1) * 18 +
      (group.branch ? BO_PDF_BRANCH_INDENT : 0);
    const x = PAGE_MARGIN + 26 + indent;
    const width = doc.page.width - PAGE_MARGIN - x;
    const nameWidth = width - (row.node.flagged ? 96 : 24);
    const nameHeight = Math.min(
      30,
      doc
        .font(FONT_BOLD)
        .fontSize(10.5)
        .heightOfString(cleanText(row.node.name || row.nodeKey), {
          width: nameWidth,
          lineGap: 1,
        }),
    );
    const metadata = [
      cleanText(row.edge.relation || 'connected'),
      cleanText(row.node.type || ''),
      cleanText(row.node.country || ''),
    ]
      .filter(Boolean)
      .join(' · ');
    const metadataHeight = Math.min(
      22,
      doc
        .font(FONT_REGULAR)
        .fontSize(8.5)
        .heightOfString(metadata || 'Connected entity', {
          width: width - 24,
          lineGap: 1,
        }),
    );
    const status = beneficialOwnershipTreeStatus(row);
    const cardHeight = Math.max(
      52,
      12 + nameHeight + 4 + metadataHeight + (status ? 14 : 0) + 9,
    );

    if (doc.y + cardHeight > pageBottom()) {
      startPage(true);
      renderGroupHeader(group, true);
    }

    const y = doc.y;
    const branchX = x - 12;
    const midY = y + cardHeight / 2;
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(1)
      .moveTo(branchX, y - 5)
      .lineTo(branchX, midY)
      .lineTo(x - 4, midY)
      .stroke();
    doc.circle(branchX, midY, 2.2).fill(COLOR.primary);

    doc
      .roundedRect(x, y, width, cardHeight, 5)
      .fillAndStroke(
        row.node.flagged ? '#FEF3F2' : COLOR.cardBg,
        row.node.flagged ? COLOR.riskHigh : COLOR.cardBorder,
      );
    doc
      .rect(x, y, 3, cardHeight)
      .fill(row.node.flagged ? COLOR.riskHigh : COLOR.primary);
    doc
      .font(FONT_BOLD)
      .fontSize(10.5)
      .fillColor(COLOR.textPrimary)
      .text(cleanText(row.node.name || row.nodeKey), x + 12, y + 10, {
        width: nameWidth,
        height: nameHeight,
        lineGap: 1,
        ellipsis: true,
      });
    if (row.node.flagged) {
      doc
        .font(FONT_BOLD)
        .fontSize(7.5)
        .fillColor(COLOR.riskHigh)
        .text('SANCTIONED', x + width - 84, y + 12, {
          width: 70,
          align: 'right',
          characterSpacing: 0.35,
          lineBreak: false,
        });
    }

    const metadataY = y + 12 + nameHeight;
    doc
      .font(FONT_REGULAR)
      .fontSize(8.5)
      .fillColor(COLOR.textSecondary)
      .text(metadata || 'Connected entity', x + 12, metadataY, {
        width: width - 24,
        height: metadataHeight,
        lineGap: 1,
        ellipsis: true,
      });
    if (status) {
      doc
        .font(FONT_REGULAR)
        .fontSize(7.8)
        .fillColor(COLOR.textMuted)
        .text(status, x + 12, metadataY + metadataHeight + 3, {
          width: width - 24,
          lineBreak: false,
          ellipsis: true,
        });
    }
    doc.y = y + cardHeight + 9;
  };

  startPage();
  doc
    .font(FONT_REGULAR)
    .fontSize(9.5)
    .fillColor(COLOR.textSecondary)
    .text(
      'The hierarchy below is generated from the same saved graph as the interactive Ownership Tree tab. Indentation shows each additional relationship level.',
      PAGE_MARGIN,
      doc.y,
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );
  doc.moveDown(0.8);

  const subject = graph.nodes[graph.subject];
  const subjectWidth = Math.min(360, doc.page.width - 2 * PAGE_MARGIN);
  const subjectX = (doc.page.width - subjectWidth) / 2;
  const subjectY = doc.y;
  const subjectNameHeight = Math.min(
    34,
    doc
      .font(FONT_BOLD)
      .fontSize(13)
      .heightOfString(cleanText(subject.name || graph.subject), {
        width: subjectWidth - 28,
        lineGap: 1,
      }),
  );
  const branchCount = (graph.branches || []).length;
  const subjectMetadataText = [
    branchCount ? `Found in ${fmtCount(branchCount)} jurisdictions` : subject.type,
    branchCount ? '' : subject.country,
    `${fmtCount(graph.edgeCount)} connection${
      graph.edgeCount === 1 ? '' : 's'
    }`,
  ]
    .map(cleanText)
    .filter(Boolean)
    .join(' · ');
  // Unlike the fixed-width KPI/label strings elsewhere in this file, this
  // line joins the subject's type, country, and connection count — data
  // long enough (e.g. "company (investment conglomerate/consortium) ·
  // Russian Federation · 22 connections") to wrap to a second line inside
  // the card's capped width, so its height must be measured and folded into
  // subjectHeight rather than assumed to fit in whatever space is left.
  const subjectMetadataHeight = Math.min(
    22,
    doc
      .font(FONT_REGULAR)
      .fontSize(8.5)
      .heightOfString(subjectMetadataText, {
        width: subjectWidth - 28,
        lineGap: 1,
      }),
  );
  const subjectHeight = 29 + subjectNameHeight + subjectMetadataHeight + 14;
  doc
    .roundedRect(subjectX, subjectY, subjectWidth, subjectHeight, 6)
    .fillAndStroke(COLOR.cardBg, COLOR.primary);
  doc
    .font(FONT_BOLD)
    .fontSize(7.5)
    .fillColor(COLOR.primary)
    .text('SUBJECT OF THIS INVESTIGATION', subjectX + 14, subjectY + 10, {
      width: subjectWidth - 28,
      characterSpacing: 0.5,
      lineBreak: false,
    });
  doc
    .font(FONT_BOLD)
    .fontSize(13)
    .fillColor(COLOR.textPrimary)
    .text(
      cleanText(subject.name || graph.subject),
      subjectX + 14,
      subjectY + 25,
      {
        width: subjectWidth - 28,
        height: subjectNameHeight,
        lineGap: 1,
        ellipsis: true,
      },
    );
  doc
    .font(FONT_REGULAR)
    .fontSize(8.5)
    .fillColor(COLOR.textSecondary)
    .text(
      subjectMetadataText,
      subjectX + 14,
      subjectY + 29 + subjectNameHeight,
      {
        width: subjectWidth - 28,
        height: subjectMetadataHeight,
        lineGap: 1,
        ellipsis: true,
      },
    );
  doc.y = subjectY + subjectHeight + 20;
  // Built for one of several same-name companies: say so under the card, the
  // same line the Ownership Tree tab shows.
  const note = sameNameNote(graph);
  if (note) {
    doc
      .font(FONT_REGULAR)
      .fontSize(8.5)
      .fillColor(COLOR.textMuted)
      .text(cleanText(note), subjectX, subjectY + subjectHeight + 8, {
        width: subjectWidth,
        align: 'center',
      });
    doc.y = Math.max(doc.y + 12, subjectY + subjectHeight + 20);
  }

  tree.groups.forEach((group) => {
    if (group.branchStart) {
      renderBranchCard(group);
    }
    if (!group.rows.length) {
      // A branch with nothing saved under it: its card, and a note.
      doc
        .font(FONT_REGULAR)
        .fontSize(8.5)
        .fillColor(COLOR.textMuted)
        .text(
          'No connections were saved for this company.',
          PAGE_MARGIN + BO_PDF_BRANCH_INDENT,
          doc.y,
          { width: doc.page.width - 2 * PAGE_MARGIN - BO_PDF_BRANCH_INDENT },
        );
      doc.moveDown(0.8);
      return;
    }
    if (doc.y + 58 > pageBottom()) {
      startPage(true);
    }
    renderGroupHeader(group);
    group.rows.forEach((row) => renderRow(row, group));
    doc.moveDown(0.25);
  });

  if (tree.truncated) {
    if (doc.y + 42 > pageBottom()) {
      startPage(true);
    }
    doc
      .roundedRect(PAGE_MARGIN, doc.y, doc.page.width - 2 * PAGE_MARGIN, 34, 4)
      .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
    doc
      .font(FONT_REGULAR)
      .fontSize(8.5)
      .fillColor(COLOR.textSecondary)
      .text(
        `This PDF shows the first ${fmtCount(
          BO_PDF_TREE_MAX_ROWS,
        )} relationships. Open the Saved Work Ownership Tree for the complete interactive graph.`,
        PAGE_MARGIN + 10,
        doc.y + 10,
        { width: doc.page.width - 2 * PAGE_MARGIN - 20 },
      );
  }
}

function renderBeneficialOwnershipFindings(
  doc: any,
  data: IBeneficialOwnershipTicketReportData,
  sectionNum = '03',
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, sectionNum, 'Risk Findings');

  const findings = Array.isArray(data.keyFindings) ? data.keyFindings : [];
  if (!findings.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text('No key findings were saved with this investigation.');
    return;
  }

  findings.forEach((finding: any, index: number) => {
    if (doc.y > doc.page.height - 130) {
      doc.addPage();
      doc.y = 70;
    }

    const title = getBoFindingTitle(finding, index);
    const riskLevel =
      typeof finding === 'string' ? '' : cleanText(finding?.risk_level || '');
    const body = getBoFindingText(finding);
    const contentWidth = doc.page.width - 2 * PAGE_MARGIN - 28;
    const titleHeight = doc
      .font(FONT_BOLD)
      .fontSize(11)
      .heightOfString(title, { width: contentWidth });
    const riskHeight = riskLevel ? 13 : 0;
    const bodyHeight = doc
      .font(FONT_REGULAR)
      .fontSize(9.5)
      .heightOfString(body, { width: contentWidth, lineGap: 2 });
    const cardHeight = Math.max(
      82,
      12 + titleHeight + riskHeight + bodyHeight + 24,
    );

    if (doc.y + cardHeight > doc.page.height - 60) {
      doc.addPage();
      doc.y = 70;
    }

    const cardY = doc.y;
    doc
      .roundedRect(
        PAGE_MARGIN,
        cardY,
        doc.page.width - 2 * PAGE_MARGIN,
        cardHeight,
        5,
      )
      .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
    doc
      .font(FONT_BOLD)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(title, PAGE_MARGIN + 14, cardY + 12, {
        width: doc.page.width - 2 * PAGE_MARGIN - 28,
      });
    if (riskLevel) {
      doc
        .font(FONT_BOLD)
        .fontSize(8)
        .fillColor(boRiskColor(riskLevel))
        .text(riskLevel.toUpperCase(), PAGE_MARGIN + 14, doc.y + 3, {
          characterSpacing: 0.6,
        });
    }
    doc
      .font(FONT_REGULAR)
      .fontSize(9.5)
      .fillColor(COLOR.textSecondary)
      .text(body, PAGE_MARGIN + 14, doc.y + 7, {
        width: contentWidth,
        lineGap: 2,
      });
    doc.y = cardY + cardHeight + 14;
  });
}

function renderBeneficialOwnershipEntitiesAndSources(
  doc: any,
  data: IBeneficialOwnershipTicketReportData,
  sectionNum = '04',
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, sectionNum, 'Connected Entities & Sources');

  const associatedEntities = Array.isArray(data.associatedEntities)
    ? data.associatedEntities
    : [];
  if (associatedEntities.length) {
    doc
      .font(FONT_BOLD)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text('Associated Entities', PAGE_MARGIN, doc.y);
    doc.moveDown(0.5);
    renderTable(
      doc,
      [
        { header: '#', width: 0.6, align: 'right', noWrap: true },
        { header: 'Entity', width: 2.6 },
        { header: 'Relationship', width: 2.4 },
        { header: 'Source URL', width: 2.2 },
      ],
      associatedEntities.map((entity: any, index: number) => [
        index + 1,
        cleanText(entity?.name || 'Unknown entity'),
        cleanText(entity?.relation || 'Not specified'),
        cleanText(entity?.source_url || '—'),
      ]),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text('No associated entities were saved with this investigation.');
    doc.moveDown(0.8);
  }

  const links = Array.isArray(data.supportingLinks) ? data.supportingLinks : [];
  if (doc.y > doc.page.height - 160) {
    doc.addPage();
    doc.y = 70;
  }
  doc
    .font(FONT_BOLD)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text('Supporting Links', PAGE_MARGIN, doc.y);
  doc.moveDown(0.5);

  if (!links.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text('No supporting links were saved with this investigation.');
    return;
  }

  renderTable(
    doc,
    [
      { header: '#', width: 0.6, align: 'right', noWrap: true },
      { header: 'Title', width: 2.2 },
      { header: 'Supports', width: 2.2 },
      { header: 'URL', width: 2.6 },
    ],
    links.map((link: any, index: number) => [
      index + 1,
      cleanText(link?.title || 'Source'),
      cleanText(link?.supports || '—'),
      cleanText(link?.url || '—'),
    ]),
  );
}

const orderedEccnResults = (results: any[]): any[] => {
  const preferredHeadings = [
    'Summary of Applicable Controls',
    'Control Check Outcome',
    'License Requirement Important Note!',
  ];
  const hasFinal = (results || []).some(
    (item) =>
      item?.section &&
      item.section !== 'progress' &&
      cleanText(item?.result?.heading),
  );

  return (results || [])
    .map((item, index) => ({ item: item, index: index }))
    .filter(({ item }) => !(hasFinal && item?.section === 'progress'))
    .filter(
      ({ item }) =>
        cleanText(item?.result?.heading) || cleanText(item?.result?.text),
    )
    .sort((a, b) => {
      const aIndex = preferredHeadings.indexOf(a.item?.result?.heading);
      const bIndex = preferredHeadings.indexOf(b.item?.result?.heading);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.index - b.index;
    })
    .map(({ item }) => item);
};

function renderEccnCover(
  doc: any,
  data: IEccnTicketReportData,
  logo: ArrayBuffer | string | null,
): void {
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* logo is optional */
    }
  }

  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('BITE ECCN Control Check Report', {
      width: titleWidth,
      lineGap: 2,
    });
  // The title wraps to two lines; lift it by the extra line so the meta
  // table, closing paragraph and disclaimer keep their single-line positions
  // and the disclaimer still fits on the cover.
  const titleLineHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('B', { width: titleWidth, lineGap: 2 });
  const titleY = 220 - Math.max(0, titleHeight - titleLineHeight);
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text('BITE ECCN Control Check Report', PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 130;
  const valueW = doc.page.width - PAGE_MARGIN - valueX;
  const meta: Array<[string, string]> = [
    ['TICKET', cleanText(data.ticketName) || '—'],
    ['ECCN CODE', cleanText(data.commodity?.eccn) || '—'],
    ['DESTINATION', cleanText(data.commodity?.destinationCountry) || '—'],
    [
      'PRODUCT',
      cleanText(data.commodity?.name || data.classificationValue) || '—',
    ],
  ];
  if (data.status) meta.push(['STATUS', cleanText(data.status)]);
  if (data.lastScreen)
    meta.push(['LAST SCREEN', formatReportDate(data.lastScreen)]);
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push([
    'PREPARED BY',
    cleanText(data.preparedBy) || 'BITE Export Controls',
  ]);

  let y = titleY + Math.max(titleHeight, 46) + 40;
  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, y - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, y - 6)
    .stroke();

  meta.forEach(([label, value]) => {
    const measuredHeight = doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .heightOfString(value, { width: valueW, lineGap: 2 });
    const rowH = Math.max(26, Math.min(measuredHeight, 44) + 14);

    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text(label, labelX, y + 6, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(value, valueX, y + 5, {
        width: valueW,
        height: rowH - 10,
        ellipsis: true,
      });
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(labelX, y + rowH - 4)
      .lineTo(doc.page.width - PAGE_MARGIN, y + rowH - 4)
      .stroke();
    y += rowH;
  });

  const accentY = y + 16;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(2.5)
    .moveTo(PAGE_MARGIN, accentY)
    .lineTo(PAGE_MARGIN + 60, accentY)
    .stroke();
  doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text(
      'A readable record of the saved ECCN and destination-country control check, including applicable controls and license guidance.',
      PAGE_MARGIN,
      accentY + 18,
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );

  renderFirstPageDisclaimer(doc, ECCN_DISCLAIMER, 7.8, doc.y);
}

function renderEccnResults(doc: any, data: IEccnTicketReportData): void {
  const results = orderedEccnResults(data.eccnResults || []);

  // Single caller (right after the cover), so always safe to continue on a
  // page the cover's disclaimer already spilled onto rather than force a
  // fresh, near-empty one.
  continueOrAddPage(doc);
  renderSectionHeader(doc, '01', 'Export Control Results');
  renderKpiCards(doc, [
    {
      value: cleanText(data.commodity?.eccn) || 'N/A',
      label: 'ECCN CODE',
    },
    {
      value: cleanText(data.commodity?.destinationCountry) || 'N/A',
      label: 'DESTINATION COUNTRY',
    },
    {
      value: fmtCount(results.length),
      label: 'SAVED RESULT SECTIONS',
    },
  ]);

  if (data.classificationValue || data.commodity?.name) {
    renderPaginatedLabeledBlock(
      doc,
      'PRODUCT DESCRIPTION',
      cleanText(data.commodity?.name || data.classificationValue),
    );
  }

  if (!results.length) {
    renderPaginatedLabeledBlock(
      doc,
      'CONTROL RESULTS',
      'No ECCN control results were saved with this ticket.',
    );
    return;
  }

  results.forEach((item) => {
    const heading =
      cleanText(item?.result?.heading) ||
      cleanText(item?.section)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase()) ||
      'Control Result';
    const body = cleanClassificationPdfText(
      item?.result?.text || item?.result?.message || item?.message,
    );

    renderPaginatedLabeledBlock(
      doc,
      heading.toUpperCase(),
      body || 'No result details were saved.',
    );
  });
}

const unifiedUiValue = (value: any): string => cleanText(value) || 'N/A';

// Combined NRC + DOE nuclear determination for the "Control Result" row — the SAME shared
// helper as the live NRC card (utils/exportAssessment), so a DOE-only match is
// never hidden behind a Part 110 "no clear match" and a refused / incomplete assessment can
// never read as "no match" (resilience plan Slice 6). Part 110 vs Part 810 specifics stay in
// the dedicated NRC/DOE rows.
const unifiedNuclearControlResult = (workflow: any): string =>
  cleanText(combinedNuclearHeadline(workflow)) || 'N/A';

const unifiedUiYesNo = (value: any): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'N/A';
};

const unifiedUiList = (value: any): string =>
  Array.isArray(value) && value.length
    ? value.map(cleanText).filter(Boolean).join(', ')
    : 'N/A';

const unifiedUiParts = (
  parts: Array<string | null | undefined>,
): string => parts.filter(Boolean).join(' ') || 'N/A';

const firstUnifiedObject = (value: any): any => {
  if (Array.isArray(value)) {
    return value.find((item) => item && typeof item === 'object') || {};
  }
  return value && typeof value === 'object' ? value : {};
};

const getUnifiedScreeningSummary = (workflow: any): any => {
  const exportScreening = workflow?.export_screening;
  const candidates = [
    exportScreening?.results,
    exportScreening?.result,
    exportScreening?.summary_result,
    exportScreening?.ai_summary,
    exportScreening?.aiSums,
    exportScreening?.threat_ui,
    exportScreening?.threat,
    exportScreening?.intake,
    exportScreening,
    workflow?.results,
    workflow?.ai_summary,
    workflow?.aiSums,
  ];

  const match = candidates.find((value) => {
    const candidate = firstUnifiedObject(value);
    return Boolean(
      candidate?.risk_level ||
        candidate?.explanation ||
        candidate?.summary ||
        candidate?.key_factors?.length,
    );
  });

  return firstUnifiedObject(match);
};

export const buildUnifiedCclRows = (earCcl: Record<string, any>): Array<[string, string]> => {
  const scope = earCcl?.scope_and_order_review;
  const seriesReview =
    earCcl?.review_9x515_600 || earCcl?.nine_x_515_or_600_series_review;
  const speciallyDesigned = earCcl?.specially_designed_analysis;
  const candidate = earCcl?.candidate_eccn_review;
  const license = earCcl?.license_review_summary;
  const endUse = earCcl?.end_use_end_user_review_summary;

  // Comparative transparency — the nearest ECCN (or EAR99) ruled out and why.
  const cclAlt = [earCcl?.closest_alternative_eccn, earCcl?.closest_alternative_reason]
    .filter(Boolean)
    .map((s: any) => cleanText(s))
    .join(' — ');

  // Slice 4 — international / multilateral overlay (EU dual-use + originating regimes). Supplementary
  // screening signal; the PDF row is emitted only when at least one field is present.
  const hasMultilateral = Boolean(
    (Array.isArray(earCcl?.regime_basis) && earCcl.regime_basis.length) ||
      earCcl?.eu_dual_use_entry ||
      (Array.isArray(earCcl?.foreign_equivalents) &&
        earCcl.foreign_equivalents.length) ||
      earCcl?.multilateral_notes,
  );

  return [
    ['Classification Result', unifiedUiValue(earCcl?.classification_result)],
    ['Reason', unifiedUiValue(earCcl?.reasoning)],
    ...(earCcl?.confidence
      ? ([['Confidence', unifiedUiValue(earCcl.confidence)]] as Array<[string, string]>)
      : []),
    ...(Array.isArray(earCcl?.candidate_eccns) && earCcl.candidate_eccns.length
      ? ([['Candidate ECCNs', unifiedUiList(earCcl.candidate_eccns)]] as Array<[string, string]>)
      : []),
    ...(cclAlt
      ? ([['Closest Alternative Considered', cclAlt]] as Array<[string, string]>)
      : []),
    ...(Array.isArray(earCcl?.assumptions) && earCcl.assumptions.length
      ? ([['Assumptions', unifiedUiParts(earCcl.assumptions)]] as Array<[string, string]>)
      : []),
    ['License requirement', unifiedUiValue(earCcl?.license_requirement)],
    [
      'Scope and order of review',
      unifiedUiParts([
        scope?.subject_to_ear_status
          ? `Subject to EAR status: ${scope.subject_to_ear_status}.`
          : null,
        scope?.subject_to_ear_basis
          ? `Basis: ${scope.subject_to_ear_basis}`
          : null,
        scope?.likely_categories?.length
          ? `Likely categories: ${unifiedUiList(scope.likely_categories)}.`
          : null,
        scope?.likely_product_groups?.length
          ? `Likely product groups: ${unifiedUiList(
              scope.likely_product_groups,
            )}.`
          : null,
        scope?.encryption_cross_cut_required !== undefined
          ? `Encryption cross-cut required: ${unifiedUiYesNo(
              scope.encryption_cross_cut_required,
            )}.`
          : null,
      ]),
    ],
    [
      '9x515 / 600-series review',
      unifiedUiParts([
        seriesReview?.review_required !== undefined
          ? `Review required: ${unifiedUiYesNo(
              seriesReview.review_required,
            )}.`
          : null,
        seriesReview?.likely_trumping_eccns?.length
          ? `Likely trumping ECCNs: ${unifiedUiList(
              seriesReview.likely_trumping_eccns,
            )}.`
          : null,
        seriesReview?.reasoning
          ? `Reasoning: ${seriesReview.reasoning}`
          : null,
        seriesReview?.specially_designed_relevant !== undefined
          ? `Specially designed relevant: ${unifiedUiYesNo(
              seriesReview.specially_designed_relevant,
            )}.`
          : null,
      ]),
    ],
    [
      'Specially designed review',
      unifiedUiParts([
        speciallyDesigned?.specially_designed_relevant !== undefined
          ? `Relevant: ${unifiedUiYesNo(
              speciallyDesigned.specially_designed_relevant,
            )}.`
          : null,
        speciallyDesigned?.catch_analysis
          ? `Catch analysis: ${speciallyDesigned.catch_analysis}`
          : null,
        speciallyDesigned?.release_analysis
          ? `Release analysis: ${speciallyDesigned.release_analysis}`
          : null,
        speciallyDesigned?.development_documentation_needed !== undefined
          ? `Development documentation needed: ${unifiedUiYesNo(
              speciallyDesigned.development_documentation_needed,
            )}.`
          : null,
        speciallyDesigned?.missing_technical_details?.length
          ? `Missing technical details: ${unifiedUiList(
              speciallyDesigned.missing_technical_details,
            )}.`
          : null,
      ]),
    ],
    [
      'Recommended ECCN Review',
      unifiedUiParts([
        candidate?.selected_eccn || earCcl?.eccn
          ? `Selected ECCN: ${candidate?.selected_eccn || earCcl?.eccn}${
              candidate?.selected_eccn_paragraph || earCcl?.eccn_paragraph
                ? ` (${
                    candidate?.selected_eccn_paragraph || earCcl?.eccn_paragraph
                  })`
                : ''
            }.`
          : null,
        candidate?.confidence || earCcl?.confidence
          ? `Confidence: ${candidate?.confidence || earCcl?.confidence}.`
          : null,
        candidate?.candidate_eccns?.length
          ? `Candidate ECCNs: ${unifiedUiList(candidate.candidate_eccns)}.`
          : null,
        candidate?.reasoning ? `Reasoning: ${candidate.reasoning}` : null,
        candidate?.matched_snippets?.length || earCcl?.matched_snippets?.length
          ? `Matched snippet: ${
              candidate?.matched_snippets?.[0] || earCcl?.matched_snippets?.[0]
            }`
          : null,
        // "Missing information" is now its OWN row (below) — parity with ITAR / NRC.
      ]),
    ],
    // Missing information — its own dedicated CCL row (parity with ITAR / NRC), no longer buried in
    // the Recommended ECCN Review text. Self-hides when empty.
    ...(candidate?.missing_technical_details?.length ||
    earCcl?.missing_technical_details?.length
      ? ([
          [
            'Missing information',
            unifiedUiList(
              candidate?.missing_technical_details ||
                earCcl?.missing_technical_details,
            ),
          ],
        ] as Array<[string, string]>)
      : []),
    [
      'License review',
      unifiedUiParts([
        (license || earCcl)?.reasons_for_control?.length
          ? `Reasons for control: ${unifiedUiList(
              (license || earCcl).reasons_for_control,
            )}.`
          : null,
        (license || earCcl)?.country_chart_columns?.length
          ? `Country chart columns: ${unifiedUiList(
              (license || earCcl).country_chart_columns,
            )}.`
          : null,
        (license || earCcl)?.license_required !== undefined
          ? `License required: ${unifiedUiYesNo(
              (license || earCcl).license_required,
            )}.`
          : null,
        (license || earCcl)?.license_required_basis?.length
          ? `License basis: ${unifiedUiList(
              (license || earCcl).license_required_basis,
            )}.`
          : null,
        license?.license_exception_notes?.length
          ? `License exception notes: ${unifiedUiList(
              license.license_exception_notes,
            )}.`
          : null,
        (license || earCcl)?.embargo_or_special_controls?.length
          ? `Embargo or special controls: ${unifiedUiList(
              (license || earCcl).embargo_or_special_controls,
            )}.`
          : null,
      ]),
    ],
    [
      'End-use / end-user review',
      unifiedUiParts([
        (endUse || earCcl)?.part_744_review?.length
          ? `Part 744 review: ${unifiedUiList(
              (endUse || earCcl).part_744_review,
            )}.`
          : null,
        // CHIPS §744.23 — advanced-computing / SME end-use heads-up (self-hides when empty).
        earCcl?.advanced_computing_note || endUse?.advanced_computing_note
          ? `Advanced-computing (§744.23): ${
              earCcl?.advanced_computing_note || endUse?.advanced_computing_note
            }`
          : null,
        (endUse || earCcl)?.red_flag_notes?.length
          ? `Red flag notes: ${unifiedUiList(
              (endUse || earCcl).red_flag_notes,
            )}.`
          : null,
      ]),
    ],
    ...(hasMultilateral
      ? ([
          [
            'International / Multilateral',
            unifiedUiParts([
              earCcl?.regime_basis?.length
                ? `Regime basis: ${unifiedUiList(earCcl.regime_basis)}.`
                : null,
              earCcl?.eu_dual_use_entry
                ? `EU dual-use list: ${earCcl.eu_dual_use_entry}`
                : null,
              // Task #5 — deterministic foreign-control correlation (EU / UK / Canada). Per
              // jurisdiction: an exact-number match or a low-confidence "no direct equivalent"
              // advisory (the not-1:1 case). The PDF collapses newlines, so jurisdictions are
              // semicolon-separated on one line, prefixed with the indicative caveat.
              earCcl?.foreign_equivalents?.length
                ? `International equivalents (indicative — verify with each destination's authority): ${earCcl.foreign_equivalents
                    .map(
                      (fe: any) =>
                        `${fe?.jurisdiction || ''}: ${
                          fe?.confidence === 'low'
                            ? 'no direct equivalent — verify locally'
                            : `${fe?.control_number || ''}${
                                fe?.regime ? ` (${fe.regime})` : ''
                              }`
                        }`,
                    )
                    .join('; ')}.`
                : null,
              earCcl?.multilateral_notes || null,
            ]),
          ],
        ] as Array<[string, string]>)
      : []),
    // License Exception ENC (§740.17) — deterministic self-classification-report-vs-CCATS pathway for
    // Category 5 Part 2 encryption ECCNs (5A002 / 5A992.c alike). Self-hides on non-encryption items.
    ...(earCcl?.enc_pathway
      ? ([['License Exception ENC (§740.17)', String(earCcl.enc_pathway)]] as Array<[string, string]>)
      : []),
    // License Exceptions (EAR Part 740) — deterministic grounded determination as its OWN self-hiding
    // row (headline + candidate exceptions + the reason none apply). Empty on EAR99 / retrieval failure.
    ...((): Array<[string, string]> => {
      const lx = formatLicenseExceptions(earCcl?.license_exceptions);
      return lx ? [['License Exceptions (15 CFR Part 740)', lx]] : [];
    })(),
    // Task #6 — BIS guidance (US/BIS interpretive FAQs / advisories). Its OWN self-hiding row,
    // separate from the International / Multilateral block above (not part of hasMultilateral).
    ...(earCcl?.bis_guidance_notes
      ? ([['BIS guidance', String(earCcl.bis_guidance_notes)]] as Array<[string, string]>)
      : []),
  ];
};

// v2 conditional-routing — the PACKAGE-level jurisdiction disposition → PDF rows. Exported so
// the local render test can exercise it. Reuses the SAME disposition wording as the on-screen
// badge (dispositionLabel) so every surface reads identically. The section self-hides at the
// caller when `jurisdiction_routing` is absent (flag OFF / older tickets predating this feature).
export const buildJurisdictionRoutingRows = (
  routing: Record<string, any>,
): Array<[string, string]> => {
  const label = dispositionLabel(routing?.disposition);
  if (!label) return [];
  const rows: [string, string][] = [['Determination', label]];
  const reasons = Array.isArray(routing?.reasons)
    ? routing.reasons.map((r: any) => cleanText(r)).filter(Boolean)
    : [];
  if (reasons.length) {
    rows.push(['Why', reasons.join('\n')]);
  }
  if (jurisdictionNeedsManualReview(routing as any)) {
    rows.push([
      'Manual review',
      'Jurisdiction review is recommended — the automated result for the unresolved item is ' +
        'provisional; do not rely on it until confirmed by qualified review.',
    ]);
  }
  return rows;
};

// "Where to File" / PGA agency-routing summary (advisory) → PDF rows. Exported (like
// buildUnifiedCclRows) so the local render test can exercise it directly. The whole section
// self-hides at the caller when `pga_routing` is absent (older tickets predating this feature).
export const buildUnifiedPgaRows = (
  pga: Record<string, any>,
): Array<[string, string]> => {
  const pgaAgencyLabel = (a: any): string => {
    const name = a?.agency_display || a?.agency || 'Agency';
    const code = a?.aes_license_type_code;
    return code ? `${name} — AES ${code}` : name;
  };
  const rows: [string, string][] = [
    ['Determination', unifiedUiValue(pga?.headline)],
    ['Determination Status', unifiedUiValue(pga?.determination_status)],
  ];
  if (pga?.prose) {
    rows.push(['Summary', cleanText(pga.prose)]);
  }
  if (Array.isArray(pga?.agencies)) {
    pga.agencies.forEach((a: any) => {
      const val = [a?.why, a?.next_step, a?.aes_license_type_description]
        .filter(Boolean)
        .map((s: any) => cleanText(s))
        .join('\n');
      rows.push([pgaAgencyLabel(a), val || 'N/A']);
    });
  }
  if (Array.isArray(pga?.not_assessed) && pga.not_assessed.length) {
    rows.push([
      'Not Assessed',
      pga.not_assessed.map((n: any) => cleanText(n)).filter(Boolean).join('\n'),
    ]);
  }
  if (Array.isArray(pga?.not_requested) && pga.not_requested.length) {
    rows.push([
      'Not Requested (scoped review)',
      [...pga.not_requested.map((n: any) => cleanText(n)).filter(Boolean),
        'Not run — treat as open, not cleared.'].join('\n'),
    ]);
  }
  // (Appendix X niche-PGA requirements live in the Country and Control List Check's control-list
  // part — the export_screening section.)
  if (pga?.caveat) {
    rows.push(['Note', cleanText(pga.caveat)]);
  }
  return rows;
};

// Appendix X — niche PGA requirements by commodity code (AMS/ATF/DEA/EPA/FWS/NMFS). Rendered under
// the control lists (export_screening) of the Country and Control List Check. Reads from the export_screening stage, falling back
// to the old pga_routing location for tickets saved before the move. Returns the rows, or a single
// explanatory note when no commodity code was classified (status 'no_code'); [] otherwise.
export const buildAppendixXRows = (
  stage: any,
  workflow: any,
): Array<[string, string]> => {
  const niche = Array.isArray(stage?.additional_pga_requirements)
    ? stage.additional_pga_requirements
    : Array.isArray(workflow?.pga_routing?.additional_pga_requirements)
      ? workflow.pga_routing.additional_pga_requirements
      : [];
  const caveat =
    stage?.additional_pga_caveat ?? workflow?.pga_routing?.additional_pga_caveat;
  const rows: [string, string][] = [];
  if (niche.length) {
    if (caveat) {
      rows.push([
        'Other agency requirements (by commodity code)',
        cleanText(caveat),
      ]);
    }
    niche.forEach((x: any) => {
      const label =
        x?.pga_agency && x?.agency_label
          ? `${x.pga_agency} — ${x.agency_label}`
          : x?.agency_label || x?.pga_agency || 'Agency';
      const verify = x?.advisory
        ? 'Code is not unique to this agency — verify whether a permit/notification applies.'
        : 'This commodity code carries this agency’s export requirement.';
      const val = [x?.program, x?.permit_forms, x?.description, verify]
        .filter(Boolean)
        .map((s: any) => cleanText(s))
        .join('\n');
      rows.push([label, val || 'N/A']);
    });
  } else if (stage?.additional_pga_status === 'no_code') {
    rows.push([
      'Other agency requirements (by commodity code)',
      'Commodity-code PGA requirements (Appendix X) could not be determined — no Schedule B/HTS code was classified.',
    ]);
  }
  return rows;
};

// Generic per-phase assessment quality → PDF (resilience plan Slice 6). The package summary
// lists every check whose automated assessment could not be completed; each affected
// non-jurisdiction phase also leads with an "Assessment status" row. Both self-hide when every
// phase is assessed. Exported so the local render test can exercise them. Same shared wording
// as the live card (utils/exportAssessment).
export const buildAssessmentSummaryRows = (
  workflow: ExportAssessmentData,
): Array<[string, string]> => {
  const phases = notAssessedPhases(workflow);
  if (!phases.length) return [];
  return [
    ['Status', NOT_ASSESSED_LABEL],
    ['Checks affected', phases.map((p) => ASSESSMENT_PHASE_LABELS[p]).join('\n')],
    ['What this means', notAssessedSummaryText(phases)],
  ];
};

// PDF section key → assessment phase, for the non-jurisdiction phases (NRC/ITAR use the
// reused jurisdiction coverage wording above instead).
const PDF_SECTION_ASSESSMENT_PHASE: Record<string, ExportAssessmentPhase> = {
  eei: 'profile',
  schedule_b: 'schedule_b',
  country_screening: 'country',
  export_screening: 'export_screening',
  ear_ccl: 'ccl',
};

export const withAssessmentStatusRow = (
  key: string,
  rows: Array<[string, string]>,
  workflow: ExportAssessmentData,
): Array<[string, string]> => {
  // Scoped review: a check the user didn't request never ran — one explicit status row instead
  // of a table of bare "N/A" values that could be misread (NRC / ITAR included here).
  const scopedPhase = PDF_SECTION_ASSESSMENT_PHASE[key] || (key === 'nrc' || key === 'itar' ? key : null);
  if (scopedPhase && isPhaseNotRequested(workflow, scopedPhase as ExportAssessmentPhase)) {
    return [['Status', NOT_REQUESTED_SECTION_TEXT]];
  }
  const phase = PDF_SECTION_ASSESSMENT_PHASE[key];
  if (!phase || !rows.length || !isPhaseNotAssessed(workflow, phase)) return rows;
  return [['Assessment status', `${NOT_ASSESSED_LABEL}\n${NOT_ASSESSED_NOTICE}`], ...rows];
};

const buildUnifiedUiRows = (
  key: string,
  stage: any,
  workflow: any,
): Array<[string, string]> => {
  switch (key) {
    case 'phase_assessments':
      return buildAssessmentSummaryRows(workflow);
    case 'review_scope':
      return buildScopedReviewRows(workflow);
    case 'jurisdiction_routing':
      return buildJurisdictionRoutingRows(stage);
    case 'pga_routing':
      return buildUnifiedPgaRows(stage);
    case 'eei': {
      const eeiRows: [string, string][] = [
        ['Filing Determination', unifiedUiValue(stage?.filing_determination)],
        ['Determination Status', unifiedUiValue(stage?.determination_status)],
        ['Basis', unifiedUiValue(stage?.basis)],
        ['Regulatory Citation', unifiedUiValue(stage?.citation)],
        ['Transaction Type', unifiedUiValue(stage?.transaction_type)],
        ['Destination Category', unifiedUiValue(stage?.destination_class_display)],
        ['Value Exemption', unifiedUiValue(stage?.value_exemption)],
        ...(showEeiValueExemptionNote(stage)
          ? ([['Value Exemption Note', EEI_VALUE_EXEMPTION_NOTE]] as [string, string][])
          : []),
        [
          'Does a license requirement force filing?',
          unifiedUiValue(stage?.license_override),
        ],
        ['AES Exemption Legend', unifiedUiValue(stage?.aes_exemption_legend)],
        ['Next Step', unifiedUiValue(stage?.next_step)],
      ];
      if (Array.isArray(stage?.notes) && stage.notes.length) {
        eeiRows.push([
          'Notes',
          stage.notes.map((n: any) => cleanText(n)).filter(Boolean).join('\n'),
        ]);
      }
      return eeiRows;
    }
    case 'schedule_b': {
      // Slice B: how the user's own code was checked (rows absent when classified).
      const codeRows = scheduleBCodeRows(stage);
      const scheduleBRows: [string, string][] = [
        ['Primary Candidate - Product', unifiedUiValue(stage?.product_name)],
        codeRows.exportCode
          ? ['Export code', codeRows.exportCode]
          : [
              'Primary Candidate - Schedule B Code',
              unifiedUiValue(stage?.schedule_b_code),
            ],
        ['Primary Candidate - Section', unifiedUiValue(stage?.section)],
        ['Primary Candidate - Chapter', unifiedUiValue(stage?.chapter)],
      ];
      if (codeRows.aesCheck) {
        scheduleBRows.push(['AES Export Check', codeRows.aesCheck]);
      }
      if (codeRows.codeSource) {
        scheduleBRows.push(['Where the Code Came From', codeRows.codeSource]);
      }
      if (stage?.selection_rationale) {
        scheduleBRows.push([
          'Selection Rationale',
          cleanText(stage.selection_rationale),
        ]);
      }
      const scheduleBAlt = [
        stage?.closest_alternative_hs_code,
        stage?.closest_alternative_reason,
      ]
        .filter(Boolean)
        .join(' — ');
      if (scheduleBAlt) {
        scheduleBRows.push([
          'Closest Alternative Considered',
          cleanText(scheduleBAlt),
        ]);
      }
      scheduleBRows.push([
        'Needs Clarification',
        stage?.needs_clarification || stage?.needs_clarification === false
          ? String(stage.needs_clarification)
          : 'N/A',
      ]);
      return scheduleBRows;
    }
    case 'country_screening':
      return [
        [
          'Input Destination Country',
          unifiedUiValue(stage?.normalized_country_name),
        ],
        ['Normalized Country', unifiedUiValue(stage?.normalized_country_name)],
        ['ISO2', unifiedUiValue(stage?.country_iso2)],
        ['Is this Country Embargoed', unifiedUiYesNo(stage?.embargoed_countries)],
        // How the embargo determination was reached (nature + country-level writeup).
        ...(stage?.embargoed_countries && stage?.embargo_nature
          ? ([['Embargo Nature', unifiedUiValue(stage.embargo_nature)]] as Array<
              [string, string]
            >)
          : []),
        ...(stage?.embargoed_countries && stage?.embargo_detailed_summary
          ? ([
              ['Embargo Detailed Summary', unifiedUiValue(stage.embargo_detailed_summary)],
            ] as Array<[string, string]>)
          : []),
        [
          'Is this Country OFAC-Sanctioned',
          unifiedUiYesNo(stage?.sanctions_programs_and_country_info),
        ],
        [
          'Is this a BIS Russia / Belarus Transshipment Point',
          unifiedUiYesNo(stage?.transhipment_points_russia_belarus),
        ],
        [
          'Is this a BIS China Transshipment Point',
          unifiedUiYesNo(stage?.transhipment_points_china),
        ],
      ];
    case 'export_screening': {
      const summary = getUnifiedScreeningSummary(workflow);
      const hsCode =
        summary?.hs_code ||
        workflow?.schedule_b?.hs_code ||
        workflow?.schedule_b?.schedule_b_code;
      const riskLevel = cleanText(summary?.risk_level);
      return [
        ['Schedule B / HTS Code', unifiedUiValue(hsCode)],
        [
          'Risk Level',
          riskLevel
            ? `${riskLevel.charAt(0).toUpperCase()}${riskLevel.slice(1).toLowerCase()}`
            : 'N/A',
        ],
        // sanitizeModelText backstops already-saved runs whose stored summary/explanation carry
        // leaked tool-call markup or internal field names (new runs are cleaned server-side).
        ['Summary', sanitizeModelText(unifiedUiValue(summary?.summary))],
        ['Explanation', sanitizeModelText(unifiedUiValue(summary?.explanation))],
        // Appendix X — niche PGA requirements by commodity code (moved here from Where to File).
        ...buildAppendixXRows(stage, workflow),
      ];
    }
    case 'nrc': {
      // List/alt joiners (parity with the ITAR case + the saved-work card).
      const nrcList = (items: any): string =>
        Array.isArray(items)
          ? items.map((s: any) => cleanText(s)).filter(Boolean).join('\n')
          : '';
      const nrcAlt = [stage?.closest_alternative, stage?.closest_alternative_reason]
        .filter(Boolean)
        .map((s: any) => cleanText(s))
        .join(' — ');
      const nrcDoeAlt = [
        stage?.doe_closest_alternative,
        stage?.doe_closest_alternative_reason,
      ]
        .filter(Boolean)
        .map((s: any) => cleanText(s))
        .join(' — ');
      const nrcRows: [string, string][] = [
        ['Control Result', unifiedNuclearControlResult(workflow)],
        ['Reason', unifiedUiValue(stage?.reason)],
      ];
      // v2 conditional-routing — nuclear jurisdiction coverage (spans NRC + DOE). Self-hides.
      // A not_assessed nuclear phase has no legal verdict → reuse the "Unresolved" wording
      // for display only (resilience plan Slice 6).
      const nrcCoverage =
        isPhaseNotAssessed(workflow, 'nrc') ? 'unresolved' : stage?.coverage_status || null;
      if (nrcCoverage) {
        nrcRows.push([
          'Nuclear jurisdiction coverage',
          [
            coverageStatusLabel(nrcCoverage),
            coverageStatusPlain(nrcCoverage, 'nuclear', workflow?.jurisdiction_routing),
          ]
            .filter(Boolean)
            .join('\n'),
        ]);
      }
      if (stage?.confidence) {
        nrcRows.push(['Confidence', unifiedUiValue(stage?.confidence)]);
      }
      nrcRows.push(['Notes', unifiedUiValue(stage?.notes)]);
      if (nrcAlt) {
        nrcRows.push(['Closest Alternative Considered', nrcAlt]);
      }
      // Missing information as its OWN row (parity with ITAR / CCL). Self-hides when empty.
      if (
        Array.isArray(stage?.missing_information) &&
        stage.missing_information.length
      ) {
        nrcRows.push(['Missing information', nrcList(stage.missing_information)]);
      }
      if (Array.isArray(stage?.assumptions) && stage.assumptions.length) {
        nrcRows.push(['Assumptions', nrcList(stage.assumptions)]);
      }
      // DOE 10 CFR Part 810 is folded into the nuclear phase — surface it when present
      // (absent on the OpenAI rollback path / older runs).
      if (stage?.doe_controlled) {
        nrcRows.push(['DOE Part 810 Result', unifiedUiValue(stage?.doe_controlled)]);
        nrcRows.push(['DOE Reason', unifiedUiValue(stage?.doe_reason)]);
        if (stage?.doe_confidence) {
          nrcRows.push(['DOE Confidence', unifiedUiValue(stage?.doe_confidence)]);
        }
        if (stage?.doe_notes) {
          nrcRows.push(['DOE Notes', unifiedUiValue(stage?.doe_notes)]);
        }
        if (nrcDoeAlt) {
          nrcRows.push(['DOE Closest Alternative Considered', nrcDoeAlt]);
        }
        if (
          Array.isArray(stage?.doe_missing_information) &&
          stage.doe_missing_information.length
        ) {
          nrcRows.push([
            'DOE Missing information',
            nrcList(stage.doe_missing_information),
          ]);
        }
        if (Array.isArray(stage?.doe_assumptions) && stage.doe_assumptions.length) {
          nrcRows.push(['DOE Assumptions', nrcList(stage.doe_assumptions)]);
        }
      }
      return nrcRows;
    }
    case 'itar': {
      const itarRows: [string, string][] = [
        ['Control Result', unifiedUiValue(stage?.controlled)],
        ['USML category', unifiedUiValue(stage?.usml_category)],
      ];
      // USML paragraph + SME / MT flags (parity with the saved-work card).
      if (stage?.usml_paragraph) {
        const flags = [
          stage?.sme ? 'SME' : null,
          stage?.mt_controlled ? 'MT' : null,
        ].filter(Boolean);
        itarRows.push([
          'USML paragraph',
          `${cleanText(stage.usml_paragraph)}${flags.length ? ` (${flags.join(', ')})` : ''}`,
        ]);
      }
      // v2 conditional-routing — ITAR jurisdiction coverage. Self-hides when absent.
      // A not_assessed ITAR phase has no legal verdict → reuse "Unresolved" (display only).
      const itarCoverage =
        isPhaseNotAssessed(workflow, 'itar') ? 'unresolved' : stage?.coverage_status || null;
      if (itarCoverage) {
        itarRows.push([
          'ITAR jurisdiction coverage',
          [
            coverageStatusLabel(itarCoverage),
            coverageStatusPlain(itarCoverage, 'itar', workflow?.jurisdiction_routing),
          ]
            .filter(Boolean)
            .join('\n'),
        ]);
      }
      itarRows.push(['Reason', unifiedUiValue(stage?.reason)]);
      if (stage?.confidence) {
        itarRows.push(['Confidence', unifiedUiValue(stage?.confidence)]);
      }
      // Comparative transparency — the competing USML category/jurisdiction ruled out.
      const itarAlt = [stage?.closest_alternative_category, stage?.closest_alternative_reason]
        .filter(Boolean)
        .map((s: any) => cleanText(s))
        .join(' — ');
      if (itarAlt) {
        itarRows.push(['Closest Alternative Considered', itarAlt]);
      }
      if (stage?.country_policy_note) {
        itarRows.push(['Destination policy', unifiedUiValue(stage?.country_policy_note)]);
      }
      if (stage?.license_note) {
        itarRows.push(['License note', unifiedUiValue(stage?.license_note)]);
      }
      // AUKUS §126.7 / FMS §126.6 exemption awareness — surfaced only when present.
      if (stage?.exemption_note) {
        itarRows.push(['Exemption outlook', unifiedUiValue(stage?.exemption_note)]);
      }
      if (stage?.fms_channel === true) {
        itarRows.push([
          'FMS channel (§126.6)',
          'Foreign Military Sales — follows the DoD/DSCA Letter of Offer and Acceptance rather than a standard DSP license.',
        ]);
      }
      // List fields — joined one-per-line, only when present (matches saved work).
      const itarList = (items: any): string =>
        Array.isArray(items)
          ? items.map((s: any) => cleanText(s)).filter(Boolean).join('\n')
          : '';
      if (Array.isArray(stage?.matched_snippets) && stage.matched_snippets.length) {
        itarRows.push(['Matched USML text', itarList(stage.matched_snippets)]);
      }
      if (Array.isArray(stage?.review_trace) && stage.review_trace.length) {
        itarRows.push(['How this was assessed', itarList(stage.review_trace)]);
      }
      if (Array.isArray(stage?.missing_information) && stage.missing_information.length) {
        itarRows.push(['Missing information', itarList(stage.missing_information)]);
      }
      if (Array.isArray(stage?.assumptions) && stage.assumptions.length) {
        itarRows.push(['Assumptions', itarList(stage.assumptions)]);
      }
      return itarRows;
    }
    case 'ear_ccl':
      return buildUnifiedCclRows(stage);
    default:
      return [];
  }
};

function renderUnifiedCover(
  doc: any,
  data: IUnifiedTicketReportData,
  logo: ArrayBuffer | string | null,
): void {
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* logo is optional */
    }
  }

  const isExport = data.docType === 'Export Unified';
  const classification = data.classification || ({} as any);

  const unifiedTitle = isExport
    ? 'BITE Export Workflow Report'
    : 'BITE Import Workflow Report';
  const titleY = 220;
  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString(unifiedTitle, { width: titleWidth, lineGap: 2 });
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text(unifiedTitle, PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 130;
  const valueW = doc.page.width - PAGE_MARGIN - valueX;
  const meta: Array<[string, string]> = [
    ['TICKET', cleanText(data.ticketName) || '—'],
    ['TYPE', cleanText(data.docType) || '—'],
  ];
  if (classification.commodity?.code) {
    meta.push([
      cleanText(
        classification.commodity.codeLabel ||
          (isExport ? 'Schedule B Code' : 'HTS Code'),
      ).toUpperCase(),
      cleanText(classification.commodity.code),
    ]);
  }
  if (classification.commodity?.name || classification.classificationValue) {
    meta.push([
      'PRODUCT',
      cleanText(
        classification.commodity?.name || classification.classificationValue,
      ),
    ]);
  }
  if (data.status) meta.push(['STATUS', cleanText(data.status)]);
  if (data.frequency) meta.push(['FREQUENCY', cleanText(data.frequency)]);
  if (data.lastScreen)
    meta.push(['LAST SCREEN', formatReportDate(data.lastScreen)]);
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push([
    'PREPARED BY',
    cleanText(data.preparedBy) || `BITE ${isExport ? 'Export' : 'Import'}`,
  ]);

  let y = titleY + Math.max(titleHeight, 46) + 40;
  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, y - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, y - 6)
    .stroke();

  meta.forEach(([label, value]) => {
    const measuredHeight = doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .heightOfString(value, { width: valueW, lineGap: 2 });
    const rowH = Math.max(26, Math.min(measuredHeight, 44) + 14);

    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text(label, labelX, y + 6, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(value, valueX, y + 5, {
        width: valueW,
        height: rowH - 10,
        ellipsis: true,
      });
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(labelX, y + rowH - 4)
      .lineTo(doc.page.width - PAGE_MARGIN, y + rowH - 4)
      .stroke();
    y += rowH;
  });

  const accentY = y + 16;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(2.5)
    .moveTo(PAGE_MARGIN, accentY)
    .lineTo(PAGE_MARGIN + 60, accentY)
    .stroke();
  doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text(
      isExport
        ? 'A readable record of the saved classification and export-control workflow results for this unified ticket.'
        : 'A readable record of the saved classification, tariff, and product-screening results for this unified ticket.',
      PAGE_MARGIN,
      accentY + 18,
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );
}

const unifiedScreeningDocs = (opensearchHits: any): any[] => {
  const hitCandidates = [
    opensearchHits?.result?.body?.hits?.hits,
    opensearchHits?.body?.hits?.hits,
    opensearchHits?.result?.hits?.hits,
    opensearchHits?.hits?.hits,
  ];
  const hits = hitCandidates.find(Array.isArray) || [];

  return hits
    .map((hit: any) => hit?._source || hit)
    .filter((source: any) => source && typeof source === 'object');
};

function renderUnifiedScreeningMatches(doc: any, opensearchHits: any): void {
  if (doc.y > doc.page.height - 150) {
    doc.addPage();
    doc.y = 70;
  }

  doc
    .font(FONT_BOLD)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text('Top Screening Matches', PAGE_MARGIN, doc.y);
  doc.moveDown(0.5);

  const matches = decorateProductRows(unifiedScreeningDocs(opensearchHits));
  if (!matches.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text('No screening matches were found.', PAGE_MARGIN, doc.y);
    return;
  }

  renderTable(
    doc,
    [
      { header: '#', width: 0.55, align: 'right', noWrap: true },
      { header: 'Product / Match', width: 2.6 },
      { header: 'HTS / HS', width: 1.4 },
      { header: 'Country', width: 1.3 },
      { header: 'BITE Lens', width: 1.9 },
      { header: 'List / Agency', width: 2.2 },
    ],
    matches.map((match, index) => [
      index + 1,
      match.product,
      match.htsCode,
      match.country,
      match.lens,
      [match.listName, match.agency]
        .filter((value) => value && value !== '—')
        .join(' / ') || '—',
    ]),
  );
}

function renderUnifiedWorkflow(
  doc: any,
  workflowData: any,
  startingSection = 3,
): void {
  const workflow = Array.isArray(workflowData)
    ? workflowData[0] || {}
    : workflowData || {};
  const labels: Record<string, string> = {
    phase_assessments: 'Manual Review Required',
    review_scope: 'Scoped Review',
    jurisdiction_routing: 'Jurisdiction Routing',
    pga_routing: 'Where to File',
    eei: 'Product Profile & EEI Check',
    schedule_b: 'Schedule B / HTS Classification',
    country_screening: 'Country and Control List Check',
    // Used only when the control-list part has no country part to continue (odd legacy data).
    export_screening: 'Country and Control List Check',
    nrc: 'Nuclear (NRC + DOE)',
    itar: 'ITAR Check',
    ear_ccl: 'EAR / CCL Check',
  };
  // Same order as the live Summary Results card and Saved Work: Manual Review (only when a phase
  // wasn't assessed) → Where to File (the "what to do next" output) → … → Country and Control
  // List Check → Jurisdiction Routing directly above Nuclear, where the jurisdiction checks begin.
  // The Country and Control List Check is ONE phase: its control-list part (export_screening)
  // continues the country section on the same page under a sub-heading.
  const subSections: Record<string, { parent: string; title: string }> = {
    export_screening: { parent: 'country_screening', title: 'Control lists for this product' },
  };
  const orderedKeys = [
    'phase_assessments',
    'review_scope',
    'pga_routing',
    'eei',
    'schedule_b',
    'country_screening',
    'export_screening',
    'jurisdiction_routing',
    'nrc',
    'itar',
    'ear_ccl',
  ];

  let section = startingSection;
  let lastRenderedKey = '';
  orderedKeys.forEach((key) => {
    // The Manual Review summary reads the whole workflow (so runs saved before
    // `phase_assessments` existed still get the conservative legacy inference).
    const stage =
      key === 'phase_assessments' || key === 'review_scope' ? workflow : workflow?.[key];
    if (stage === undefined || stage === null || stage === '') return;

    const rows = withAssessmentStatusRow(
      key,
      buildUnifiedUiRows(key, stage, workflow),
      workflow,
    );
    const hasScreeningResults =
      key === 'export_screening' && stage?.opensearch_hits;
    if (!rows.length && !hasScreeningResults) return;

    const isSubSection = subSections[key]?.parent === lastRenderedKey;
    // A scoped-out merged phase already says "Not requested" once, in its own section.
    if (isSubSection && isPhaseNotRequested(workflow, 'country')) return;
    if (isSubSection) {
      renderUnifiedSubsectionTitle(doc, subSections[key].title);
    } else {
      doc.addPage();
      doc.y = 70;
      renderSectionHeader(
        doc,
        String(section).padStart(2, '0'),
        labels[key],
      );
    }
    if (rows.length) {
      renderTable(
        doc,
        [
          { header: 'Field', width: 1.8 },
          { header: 'Saved Result', width: 4.2 },
        ],
        rows,
      );
    }
    if (key === 'export_screening') {
      renderUnifiedScreeningMatches(doc, stage?.opensearch_hits);
    }
    if (!isSubSection) {
      section += 1;
    }
    lastRenderedKey = key;
  });
}

// A titled sub-part inside a unified-workflow section (e.g. the control lists of the Country and
// Control List Check), on the same page when it fits.
function renderUnifiedSubsectionTitle(doc: any, title: string): void {
  if (doc.y > doc.page.height - 160) {
    doc.addPage();
    doc.y = 70;
  } else {
    doc.moveDown(1);
  }
  doc
    .font(FONT_BOLD)
    .fontSize(12)
    .fillColor(COLOR.textPrimary)
    .text(title, PAGE_MARGIN, doc.y);
  doc.moveDown(0.4);
}

export const eccnTicketReportPDF = async (
  data: IEccnTicketReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE ECCN Control Report — ${data.ticketName || ''}`,
          Author: 'BITE Export Controls',
          Subject: 'ECCN Control Check Saved Work Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · ECCN CONTROL CHECK',
        headerRight: 'ECCN Control Report',
        footerFirstPage: 'ECCN Control Report',
      });

      renderEccnCover(doc, data, logo);
      renderEccnResults(doc, data);

      doc.end();
    } catch (error) {
      reject(error);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = finalName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

export const unifiedTicketReportPDF = async (
  data: IUnifiedTicketReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const isExport = data.docType === 'Export Unified';
  const classification = data.classification || ({} as any);
  const hasReferences = Boolean(
    classification.chatHistory?.length ||
      classification.crossRulings?.length ||
      cleanText(classification.chapterNotes),
  );

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE ${isExport ? 'Export' : 'Import'} Unified Report — ${
            data.ticketName || ''
          }`,
          Author: `BITE ${isExport ? 'Export' : 'Import'} Unified`,
          Subject: `${
            isExport ? 'Export' : 'Import'
          } Unified Saved Work Report`,
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: `BITE · ${isExport ? 'EXPORT' : 'IMPORT'} UNIFIED`,
        headerRight: `${isExport ? 'Export' : 'Import'} Unified Report`,
        footerFirstPage: `${isExport ? 'Export' : 'Import'} Unified Report`,
      });

      renderUnifiedCover(doc, data, logo);

      let nextSection = 1;
      const section = () => String(nextSection++).padStart(2, '0');

      renderClassificationFinalReport(doc, classification, section());
      if (hasReferences) {
        renderClassificationReferences(doc, classification, section());
      }

      if (isExport) {
        renderUnifiedWorkflow(doc, data.workflowData, nextSection);
      } else {
        const tariffData: ITariffTicketReportData = {
          ticketName: data.ticketName,
          docType: data.docType,
          tariffResults: data.tariffResults || {},
          preparedBy: data.preparedBy,
        };
        const productData: IProductTicketReportData = {
          ticketName: data.ticketName,
          docType: data.docType,
          product: data.product,
          aiSums: data.aiSums || [],
          productDocs: data.productDocs || [],
          productTotal: data.productTotal,
          preparedBy: data.preparedBy,
        };

        if (data.tariffResults && Object.keys(data.tariffResults).length) {
          renderTariffSavedInputsPage(doc, tariffData, section(), 'Tariff Inputs');
          renderTariffReadableResults(doc, tariffData, section());
          renderTariffApplicability(doc, tariffData, section());
          renderTariffExplanationAndDisclaimer(doc, tariffData, section());
        }
        renderProductRiskAndAi(doc, productData, section());
        renderProductMatchesTable(doc, productData, section());
      }

      doc.end();
    } catch (error) {
      reject(error);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = finalName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

export const screeningTicketEntityPDF = async (
  data: IEntityTicketReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const entityName =
    data.entity?.primary || data.aiSums?.[0]?.entity_name || '';
  const topMatches = decorateAndRank(data.entityDocs, entityName);
  const topOwnership = decorateAndRank(data.ownershipDocs, entityName);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE Entity Screening Report — ${data.ticketName || ''}`,
          Author: 'BITE Entity Screening',
          Subject: 'Entity Screening Saved Work Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, { headerRight: 'Entity Screening Report' });

      renderCover(doc, data, logo);
      renderRiskAndAi(doc, data);
      renderMatchesTable(
        doc,
        '02',
        'Top 25 Matches',
        'The highest-confidence entity matches from this screen, ranked by match score against the screened entity name.',
        topMatches,
        false,
      );
      renderMatchesTable(
        doc,
        '03',
        'Top 25 Ownership Matches',
        'The highest-confidence beneficial ownership / PEP matches from this screen, ranked by match score against the screened entity name.',
        topOwnership,
        true,
      );

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

export const screeningTicketBeneficialOwnershipPDF = async (
  data: IBeneficialOwnershipTicketReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE Beneficial Ownership Report — ${
            data.ticketName || ''
          }`,
          Author: 'BITE Beneficial Ownership',
          Subject: 'Beneficial Ownership Saved Work Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · BENEFICIAL OWNERSHIP',
        headerRight: 'Beneficial Ownership Report',
        footerFirstPage: 'Beneficial Ownership Report',
      });

      renderBeneficialOwnershipCover(doc, data, logo);
      renderBeneficialOwnershipOverview(doc, data);
      renderBeneficialOwnershipOwners(doc, data);
      const hasOwnershipTree = Boolean(
        data.ownershipGraph && !data.ownershipGraph.isEmpty,
      );
      renderBeneficialOwnershipFindings(doc, data, '03');
      renderBeneficialOwnershipEntitiesAndSources(doc, data, '04');
      if (hasOwnershipTree) {
        renderBeneficialOwnershipTree(doc, data, '05');
      }

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

export interface IEntityRiskReportPdfData {
  report: EntityRiskReport;
  preparedBy: string;
}

const riskToneColor = (risk: string | null | undefined): string => {
  const lower = String(risk || '').toLowerCase();
  if (lower === 'high') return COLOR.riskHigh;
  if (lower === 'medium') return COLOR.riskMedium;
  if (lower === 'low') return COLOR.riskLow;
  return COLOR.textMuted;
};

const riskCheckStatusText = (check: EntityRiskReport['checks'][number]): string =>
  check.status === 'answered'
    ? check.risk || 'No verdict'
    : check.status === 'failed'
    ? 'Did not answer'
    : 'Not run';

function renderEntityRiskCover(
  doc: any,
  data: IEntityRiskReportPdfData,
  logo: ArrayBuffer | string | null,
): void {
  const { report } = data;
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* ignore logo failures */
    }
  }

  const headlineY = 220;
  doc
    .font(FONT_REGULAR)
    .fontSize(13)
    .fillColor(COLOR.textSecondary)
    .text('Export and Transshipment Risk', PAGE_MARGIN, headlineY);
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text('Risk Report', PAGE_MARGIN, headlineY + 22, {
      width: doc.page.width - 2 * PAGE_MARGIN,
    });

  const metaY = headlineY + 22 + 46 + 44;
  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 150;
  const valueW = doc.page.width - valueX - PAGE_MARGIN;
  const checked = report.checkedAt ? new Date(report.checkedAt) : null;
  const meta: Array<[string, string, string?]> = [
    ['ENTITY', cleanText(report.entityName) || '-'],
  ];
  if (report.country) meta.push(['COUNTRY', cleanText(report.country)]);
  meta.push([
    'OVERALL RISK',
    report.overallRisk ? `${report.overallRisk}` : 'No verdict',
    riskToneColor(report.overallRisk),
  ]);
  meta.push([
    'CHECKS',
    `${report.counts.answered} of ${report.counts.checks} answered`,
  ]);
  if (checked && !Number.isNaN(checked.getTime())) {
    meta.push(['CHECKED', fmtDateTimeLong(checked)]);
  }
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push([
    'PREPARED BY',
    cleanText(data.preparedBy) || 'BITE Entity Screening',
  ]);

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, metaY - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, metaY - 6)
    .stroke();

  let y = metaY;
  meta.forEach(([label, value, color]) => {
    const measured = doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .heightOfString(value, { width: valueW, lineGap: 2 });
    const rowH = Math.max(26, Math.min(measured, 40) + 14);
    doc
      .font(FONT_BOLD)
      .fontSize(8)
      .fillColor(COLOR.textSecondary)
      .text(label, labelX, y, { width: 120, characterSpacing: 0.6 });
    doc
      .font(color ? FONT_BOLD : FONT_REGULAR)
      .fontSize(10)
      .fillColor(color || COLOR.textPrimary)
      .text(value, valueX, y - 1, {
        width: valueW,
        height: rowH - 8,
        ellipsis: true,
      });
    y += rowH;
  });

  const noteY = y + 34;
  doc
    .roundedRect(PAGE_MARGIN, noteY, doc.page.width - 2 * PAGE_MARGIN, 78, 5)
    .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
  doc
    .font(FONT_BOLD)
    .fontSize(10)
    .fillColor(COLOR.primary)
    .text('REPORT SCOPE', PAGE_MARGIN + 16, noteY + 14, {
      characterSpacing: 0.6,
    });
  doc
    .font(FONT_REGULAR)
    .fontSize(10)
    .fillColor(COLOR.textSecondary)
    .text(
      'This report summarizes the latest Export and Transshipment Risk run on this record: export control and diversion, adverse media, financial standing and personnel, each a live search of public sources, with the combined verdict, every finding, the network of checks, findings and sources, and the sources themselves.',
      PAGE_MARGIN + 16,
      noteY + 34,
      { width: doc.page.width - 2 * PAGE_MARGIN - 32, lineGap: 2 },
    );
}

function renderEntityRiskOverview(doc: any, data: IEntityRiskReportPdfData): void {
  const { report } = data;
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '01', 'Risk Overview');

  renderKpiCards(doc, [
    {
      value: `${report.counts.answered}/${report.counts.checks}`,
      label: 'CHECKS ANSWERED',
    },
    {
      value: fmtCount(report.counts.findings),
      label: 'KEY FINDINGS',
      valueColor: riskToneColor(report.overallRisk),
    },
    {
      value: fmtCount(report.counts.highFindings),
      label: 'HIGH SEVERITY',
      valueColor: report.counts.highFindings ? COLOR.riskHigh : undefined,
    },
    { value: fmtCount(report.counts.sources), label: 'SOURCES' },
  ]);

  renderPaginatedLabeledBlock(
    doc,
    'OVERALL ASSESSMENT',
    cleanText(
      `${report.overallRisk ? `${report.overallRisk} risk.` : 'No verdict.'} ${
        report.verdictNote
      }`,
    ),
  );

  if (doc.y > doc.page.height - 160) {
    doc.addPage();
    doc.y = 70;
  }
  doc
    .font(FONT_BOLD)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text('Checks', PAGE_MARGIN, doc.y);
  doc.moveDown(0.5);
  renderTable(
    doc,
    [
      { header: 'Check', width: 1.6 },
      { header: 'Verdict', width: 1.1 },
      { header: 'Findings', width: 0.9, align: 'right', noWrap: true },
      { header: 'Summary', width: 4.2 },
    ],
    report.checks.map((check) => [
      `${check.label}\n${check.note}`,
      riskCheckStatusText(check),
      check.status === 'answered' ? fmtCount(check.findingIds.length) : '—',
      cleanText(
        [check.riskDriver, check.summary].filter(Boolean).join('. ') ||
          (check.status === 'answered'
            ? 'The agent returned no summary.'
            : 'This check has no answer on this run.'),
      ),
    ]),
    (rowIndex, colIndex) =>
      colIndex === 1
        ? riskToneColor(
            report.checks[rowIndex]?.status === 'answered'
              ? report.checks[rowIndex]?.risk
              : null,
          )
        : undefined,
  );
}

function renderEntityRiskFindings(doc: any, data: IEntityRiskReportPdfData): void {
  const { report } = data;
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '02', 'Key Findings');

  if (!report.findings.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text(
        'No findings: every check that answered came back clean for this run.',
      );
    return;
  }

  const contentWidth = doc.page.width - 2 * PAGE_MARGIN - 28;
  const FINDING_SOURCE_MAX_H = 24;
  report.findings.forEach((finding, index) => {
    const title = `${index + 1}. ${finding.agentLabel}${
      finding.category ? ` · ${cleanText(finding.category)}` : ''
    }`;
    const severity = (finding.severity || 'Not scored').toUpperCase();
    const body = cleanText(finding.claim);
    const sourceLine = [
      cleanText(finding.sourceName),
      cleanText(finding.date),
      cleanText(finding.url),
    ]
      .filter(Boolean)
      .join(' · ');
    const titleHeight = doc
      .font(FONT_BOLD)
      .fontSize(11)
      .heightOfString(title, { width: contentWidth });
    // A card never outgrows a fresh page: a long source line stops at two
    // lines and a long claim is cut to the room left, both with an ellipsis.
    const sourceHeight = sourceLine
      ? Math.min(
          FINDING_SOURCE_MAX_H,
          doc
            .font(FONT_REGULAR)
            .fontSize(8.5)
            .heightOfString(sourceLine, { width: contentWidth }),
        )
      : 0;
    const frame =
      12 + titleHeight + 13 + (sourceLine ? sourceHeight + 8 : 0) + 22;
    const bodyHeight = Math.min(
      doc.page.height - 70 - 60 - frame,
      doc
        .font(FONT_REGULAR)
        .fontSize(9.5)
        .heightOfString(body, { width: contentWidth, lineGap: 2 }),
    );
    const cardHeight = Math.max(70, frame + bodyHeight);
    if (doc.y + cardHeight > doc.page.height - 60) {
      doc.addPage();
      doc.y = 70;
    }
    const cardY = doc.y;
    doc
      .roundedRect(PAGE_MARGIN, cardY, doc.page.width - 2 * PAGE_MARGIN, cardHeight, 5)
      .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
    doc.save();
    doc
      .rect(PAGE_MARGIN, cardY, 3, cardHeight)
      .fill(riskToneColor(finding.severity));
    doc.restore();
    doc
      .font(FONT_BOLD)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(title, PAGE_MARGIN + 14, cardY + 12, { width: contentWidth });
    doc
      .font(FONT_BOLD)
      .fontSize(8)
      .fillColor(riskToneColor(finding.severity))
      .text(severity, PAGE_MARGIN + 14, doc.y + 3, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(9.5)
      .fillColor(COLOR.textSecondary)
      .text(body, PAGE_MARGIN + 14, doc.y + 7, {
        width: contentWidth,
        height: bodyHeight,
        lineGap: 2,
        ellipsis: true,
      });
    if (sourceLine) {
      doc
        .font(FONT_REGULAR)
        .fontSize(8.5)
        .fillColor(COLOR.primary)
        .text(sourceLine, PAGE_MARGIN + 14, doc.y + 6, {
          width: contentWidth,
          height: sourceHeight,
          ellipsis: true,
        });
    }
    doc.y = cardY + cardHeight + 12;
  });
}

/**
 * The Network intelligence view on paper: the entity, each check under it,
 * and each check's findings with the source behind them, as an indented
 * hierarchy like the Beneficial Ownership tree.
 */
function renderEntityRiskNetwork(doc: any, data: IEntityRiskReportPdfData): void {
  const { report } = data;
  const pageBottom = () => doc.page.height - 62;
  const startPage = (continued = false) => {
    doc.addPage();
    doc.y = 70;
    renderSectionHeader(
      doc,
      '03',
      continued ? 'Network Intelligence (continued)' : 'Network Intelligence',
    );
  };
  startPage();

  doc
    .font(FONT_REGULAR)
    .fontSize(9.5)
    .fillColor(COLOR.textSecondary)
    .text(
      'How the checks connect this entity to what was found and where: each check under the entity, each finding under its check, and the source it rests on.',
      PAGE_MARGIN,
      doc.y,
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );
  doc.moveDown(0.8);

  // The entity at the root.
  const width = doc.page.width - 2 * PAGE_MARGIN;
  const subjectY = doc.y;
  const subjectColor = riskToneColor(report.overallRisk);
  doc
    .roundedRect(PAGE_MARGIN, subjectY, width, 46, 6)
    .fillAndStroke(COLOR.cardBg, COLOR.primary);
  doc
    .font(FONT_BOLD)
    .fontSize(7.5)
    .fillColor(COLOR.primary)
    .text(
      `SUBJECT${report.country ? ` · ${cleanText(report.country).toUpperCase()}` : ''}`,
      PAGE_MARGIN + 14,
      subjectY + 9,
      { width: width - 150, characterSpacing: 0.5, lineBreak: false },
    );
  doc
    .font(FONT_BOLD)
    .fontSize(7.5)
    .fillColor(subjectColor)
    .text(
      (report.overallRisk ? `${report.overallRisk} risk` : 'No verdict').toUpperCase(),
      PAGE_MARGIN + width - 134,
      subjectY + 9,
      { width: 120, align: 'right', characterSpacing: 0.4, lineBreak: false },
    );
  doc
    .font(FONT_BOLD)
    .fontSize(12)
    .fillColor(COLOR.textPrimary)
    .text(cleanText(report.entityName) || 'This entity', PAGE_MARGIN + 14, subjectY + 22, {
      width: width - 28,
      height: 16,
      ellipsis: true,
    });
  doc.y = subjectY + 58;

  const railX = PAGE_MARGIN + 12;
  const checkX = PAGE_MARGIN + 26;
  const checkW = doc.page.width - PAGE_MARGIN - checkX;
  const findingX = checkX + 26;
  const findingW = doc.page.width - PAGE_MARGIN - findingX;
  const findingById = new Map(report.findings.map((finding) => [finding.id, finding]));

  report.checks.forEach((check) => {
    if (doc.y + 70 > pageBottom()) startPage(true);
    const tone =
      check.status === 'answered' ? riskToneColor(check.risk) : COLOR.textMuted;
    const y = doc.y;
    // Rail from the entity to this check.
    doc
      .strokeColor(COLOR.cardBorder)
      .lineWidth(1)
      .moveTo(railX, y - 12)
      .lineTo(railX, y + 14)
      .lineTo(checkX, y + 14)
      .stroke();
    doc
      .roundedRect(checkX, y, checkW, 28, 4)
      .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
    doc.save();
    doc.rect(checkX, y, 3, 28).fill(tone);
    doc.restore();
    doc
      .font(FONT_BOLD)
      .fontSize(9.5)
      .fillColor(COLOR.textPrimary)
      .text(`${check.label}`, checkX + 12, y + 9, {
        width: checkW - 190,
        lineBreak: false,
      });
    doc
      .font(FONT_BOLD)
      .fontSize(8)
      .fillColor(tone)
      .text(
        `${riskCheckStatusText(check).toUpperCase()}${
          check.status === 'answered'
            ? ` · ${check.findingIds.length} FINDING${check.findingIds.length === 1 ? '' : 'S'}`
            : ''
        }`,
        checkX + checkW - 180,
        y + 10,
        { width: 168, align: 'right', characterSpacing: 0.4, lineBreak: false },
      );
    doc.y = y + 36;

    check.findingIds.forEach((id) => {
      const finding = findingById.get(id);
      if (!finding) return;
      const claim = cleanText(finding.claim);
      const sourceText = [
        cleanText(finding.sourceName),
        finding.url ? cleanText(finding.url) : '',
      ]
        .filter(Boolean)
        .join(' · ');
      const claimHeight = Math.min(
        42,
        doc
          .font(FONT_REGULAR)
          .fontSize(9)
          .heightOfString(claim, { width: findingW - 24, lineGap: 1 }),
      );
      const rowH = 12 + claimHeight + (sourceText ? 13 : 0) + 8;
      if (doc.y + rowH > pageBottom()) startPage(true);
      const fy = doc.y;
      const sev = riskToneColor(finding.severity);
      doc
        .strokeColor(COLOR.cardBorder)
        .lineWidth(1)
        .moveTo(checkX + 12, fy - 8)
        .lineTo(checkX + 12, fy + 10)
        .lineTo(findingX, fy + 10)
        .stroke();
      doc.save();
      doc.circle(findingX + 5, fy + 10, 3.2).fill(sev);
      doc.restore();
      doc
        .font(FONT_BOLD)
        .fontSize(7.5)
        .fillColor(sev)
        .text((finding.severity || 'Not scored').toUpperCase(), findingX + 14, fy + 6, {
          characterSpacing: 0.4,
          lineBreak: false,
        });
      doc
        .font(FONT_REGULAR)
        .fontSize(9)
        .fillColor(COLOR.textPrimary)
        .text(claim, findingX + 14, fy + 17, {
          width: findingW - 24,
          height: claimHeight,
          lineGap: 1,
          ellipsis: true,
        });
      if (sourceText) {
        doc
          .font(FONT_REGULAR)
          .fontSize(8)
          .fillColor(COLOR.primary)
          .text(sourceText, findingX + 14, fy + 17 + claimHeight + 2, {
            width: findingW - 24,
            lineBreak: false,
            ellipsis: true,
          });
      }
      doc.y = fy + rowH;
    });
    doc.moveDown(0.4);
  });
}

function renderEntityRiskSources(doc: any, data: IEntityRiskReportPdfData): void {
  const { report } = data;
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '04', 'Sources');
  if (!report.sources.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text('No sources were saved with this run.');
    return;
  }
  const labels = new Map(report.checks.map((check) => [check.agent, check.label]));
  renderTable(
    doc,
    [
      { header: '#', width: 0.5, align: 'right', noWrap: true },
      { header: 'Source', width: 1.8 },
      { header: 'Cited by', width: 2 },
      { header: 'URL', width: 3 },
    ],
    report.sources.map((source, index) => [
      index + 1,
      cleanText(source.title),
      `${source.findingIds.length} finding${source.findingIds.length === 1 ? '' : 's'} · ${source.agents
        .map((agent) => labels.get(agent) || agent)
        .join(', ')}`,
      cleanText(source.url),
    ]),
  );
}

export const screeningTicketEntityRiskPDF = async (
  data: IEntityRiskReportPdfData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE Export and Transshipment Risk Report — ${
            data.report.entityName || ''
          }`,
          Author: 'BITE Entity Screening',
          Subject: 'Export and Transshipment Risk Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('error', reject);
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · EXPORT AND TRANSSHIPMENT RISK',
        headerRight: 'Risk Report',
        footerFirstPage: 'Export and Transshipment Risk Report',
      });

      renderEntityRiskCover(doc, data, logo);
      renderEntityRiskOverview(doc, data);
      renderEntityRiskFindings(doc, data);
      renderEntityRiskNetwork(doc, data);
      renderEntityRiskSources(doc, data);

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

export const screeningTicketTariffPDF = async (
  data: ITariffTicketReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE Tariff Report — ${data.ticketName || ''}`,
          Author: 'BITE Tariff Checker',
          Subject: 'Tariff Saved Work Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · TARIFF CHECKER',
        headerRight: 'Tariff Report',
        footerFirstPage: 'Tariff Report',
      });

      renderTariffCover(doc, data, logo);
      renderTariffSavedInputsPage(doc, data, '01', 'Saved Inputs', true);
      renderTariffReadableResults(doc, data);
      renderTariffApplicability(doc, data);
      renderTariffExplanationAndDisclaimer(doc, data);

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

export const classificationTicketReportPDF = async (
  data: IClassificationTicketReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE Classification Report — ${data.ticketName || ''}`,
          Author: 'BITE Classification',
          Subject: 'Classification Saved Work Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · CLASSIFICATION',
        headerRight: 'Classification Report',
        footerFirstPage: 'Classification Report',
      });

      renderClassificationCover(doc, data, logo);
      renderClassificationFinalReport(doc, data, '01', true);
      renderClassificationReferences(doc, data);

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

export const screeningTicketProductPDF = async (
  data: IProductTicketReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE Product Screening Report — ${data.ticketName || ''}`,
          Author: 'BITE Product Screening',
          Subject: 'Product Screening Saved Work Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · PRODUCT SCREENING',
        headerRight: 'Product Screening Report',
        footerFirstPage: 'Product Screening Report',
      });

      renderProductCover(doc, data, logo);
      renderProductRiskAndAi(doc, data);
      renderProductMatchesTable(doc, data);

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

// ============================================================================
// Import Hub product report — styled after the entity screening report above
// (branded cover, chrome header/footer, numbered sections, KPI cards, tables)
// instead of the html2canvas screenshot the Import Hub page used previously.
// ============================================================================

function renderImportProductCover(
  doc: any,
  data: IImportProductTicketReportData,
  logo: ArrayBuffer | string | null,
): void {
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* ignore logo failures */
    }
  }

  const titleY = 230;
  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  // "Product Compliance Report" is long enough to wrap to two lines at
  // 38pt bold — measure it instead of assuming a single line, so the
  // wrapped second line doesn't collide with the metadata ledger below.
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('Product Compliance Report', {
      width: titleWidth,
      lineGap: 2,
    });
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text('Product Compliance Report', PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const metaY = titleY + Math.max(titleHeight, 46) + 40;
  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 130;
  const valueW = doc.page.width - PAGE_MARGIN - valueX;

  const meta: Array<[string, string]> = [
    ['PRODUCT', cleanText(data.productName) || '—'],
  ];
  if (data.sku) meta.push(['SKU', cleanText(data.sku)]);
  if (data.htsCode) meta.push(['HTS CODE', cleanText(data.htsCode)]);
  if (data.countryOfOrigin)
    meta.push(['COUNTRY OF ORIGIN', cleanText(data.countryOfOrigin)]);
  if (data.databaseName) meta.push(['DATABASE', cleanText(data.databaseName)]);
  if (data.declaredValue)
    meta.push(['DECLARED VALUE', cleanText(data.declaredValue)]);
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push(['PREPARED BY', cleanText(data.preparedBy) || 'BITE Import Hub']);

  const blurb =
    'A record of the compliance score, system flags, classification, duties ' +
    'and tariffs, program codes, and cross rulings for this imported product.';
  const blurbWidth = doc.page.width - 2 * PAGE_MARGIN;
  const blurbHeight = doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .heightOfString(blurb, { width: blurbWidth, lineGap: 2 });

  // The two-line title leaves little room: long product and database names
  // could push the closing paragraph past the bottom margin, and PDFKit would
  // spill it onto a stray page. Lower the cap on the tallest rows until the
  // ledger and paragraph fit; the capped values ellipsize.
  const rowHeights = meta.map((pair) => {
    const measuredHeight = doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .heightOfString(pair[1], { width: valueW, lineGap: 2 });
    return Math.max(26, Math.min(measuredHeight, 44) + 14);
  });
  const ledgerSpace =
    doc.page.height - doc.page.margins.bottom - metaY - 36 - blurbHeight;
  const ledgerHeight = (cap: number) =>
    rowHeights.reduce((sum, h) => sum + Math.min(h, cap), 0);
  let rowCap = Math.max(...rowHeights);
  while (rowCap > 26 && ledgerHeight(rowCap) > ledgerSpace) rowCap -= 1;

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, metaY - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, metaY - 6)
    .stroke();

  let y = metaY;
  meta.forEach((pair, index) => {
    const rowH = Math.min(rowHeights[index], rowCap);

    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text(pair[0], labelX, y + 6, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(pair[1], valueX, y + 5, {
        width: valueW,
        height: rowH - 10,
        ellipsis: true,
      });
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(labelX, y + rowH - 4)
      .lineTo(doc.page.width - PAGE_MARGIN, y + rowH - 4)
      .stroke();
    y += rowH;
  });

  const accentY = y + 18;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(2.5)
    .moveTo(PAGE_MARGIN, accentY)
    .lineTo(PAGE_MARGIN + 60, accentY)
    .stroke();
  doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text(blurb, PAGE_MARGIN, accentY + 18, { width: blurbWidth, lineGap: 2 });
}

function renderImportComplianceOverview(
  doc: any,
  data: IImportProductTicketReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '01', 'Compliance Overview');

  const activeFlags = data.systemFlags.filter((f) => !f.cleared);
  const scoreLabel = data.compliance.assessed
    ? `${data.compliance.score}/100`
    : 'N/A';
  const scoreColor = !data.compliance.assessed
    ? COLOR.textSecondary
    : data.compliance.score >= 90
    ? COLOR.riskLow
    : data.compliance.score >= 70
    ? COLOR.riskMedium
    : COLOR.riskHigh;
  const riskLabel =
    data.risk.status === 'available'
      ? data.risk.level || 'N/A'
      : 'Not Evaluated';

  renderKpiCards(doc, [
    { value: scoreLabel, label: 'COMPLIANCE SCORE', valueColor: scoreColor },
    {
      value: fmtCount(activeFlags.length),
      label: 'ACTIVE SYSTEM FLAGS',
      valueColor: activeFlags.length ? COLOR.riskMedium : COLOR.riskLow,
    },
    {
      value: riskLabel,
      label: 'AI RISK EVALUATION',
      valueColor:
        data.risk.status === 'available'
          ? riskColor(riskLabel)
          : COLOR.textSecondary,
    },
  ]);

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('SCORE DEDUCTIONS', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.3);
  if (data.compliance.deductions.length) {
    renderTable(
      doc,
      [
        { header: 'Category', width: 2.2 },
        { header: 'Reason', width: 3 },
        { header: 'Points', width: 1, align: 'right', noWrap: true },
      ],
      data.compliance.deductions.map((d) => [
        d.category,
        d.reason,
        String(d.points),
      ]),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text('No deductions applied.', PAGE_MARGIN, doc.y);
    doc.moveDown(0.8);
  }

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('SYSTEM FLAGS', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.3);
  if (data.systemFlags.length) {
    renderTable(
      doc,
      [
        { header: 'Flag', width: 2.4 },
        { header: 'Detail', width: 3 },
        { header: 'Severity', width: 1.2, noWrap: true },
        { header: 'Status', width: 1, noWrap: true },
      ],
      data.systemFlags.map((f) => [
        f.label,
        f.detail || '—',
        f.severity,
        f.cleared ? 'Cleared' : 'Active',
      ]),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text('No system flags on this product.', PAGE_MARGIN, doc.y);
    doc.moveDown(0.8);
  }

  if (data.risk.status === 'available' && data.risk.narrative) {
    renderLabeledBlock(
      doc,
      data.risk.confidence
        ? `AI RISK SUMMARY (${data.risk.confidence} CONFIDENCE)`
        : 'AI RISK SUMMARY',
      cleanText(data.risk.narrative),
    );
    if (data.risk.keyFactors?.length) {
      doc
        .font(FONT_BOLD)
        .fontSize(9)
        .fillColor(COLOR.textSecondary)
        .text('KEY FACTORS', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
      doc.moveDown(0.3);
      renderOrderedList(doc, data.risk.keyFactors.slice(0, 10));
    }
  } else {
    // Keep a failed evaluation distinguishable from a missing one — this is a
    // compliance record, and "none on file" would hide the failure.
    const riskSummary =
      data.risk.status === 'error'
        ? 'The AI risk evaluation failed and no result is available. Run Analyze Product again before relying on this report.'
        : data.risk.status === 'available'
        ? 'An AI risk evaluation is available, but no narrative summary was saved with this product.'
        : 'No AI risk evaluation is on file. Use Analyze Product to run the product screening.';
    renderLabeledBlock(doc, 'AI RISK SUMMARY', riskSummary);
  }
}

function renderImportClassification(
  doc: any,
  data: IImportProductTicketReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '02', 'Classification');

  const c = data.classification;
  renderTable(
    doc,
    [
      { header: 'Field', width: 1.4 },
      { header: 'Value', width: 3 },
    ],
    [
      ['Name', c.name || '—'],
      ['SKU', c.sku || '—'],
      ['Hierarchy Type', c.hierarchyType || '—'],
      ['Category', c.category || '—'],
      ['Manufacturer', c.manufacturer || '—'],
      ['Vendor', c.vendor || '—'],
      ['HTS Code', c.htsCode || '—'],
      ['Country of Origin', c.countryOfOrigin || '—'],
      ['Unit of Measure', c.unitOfMeasure || '—'],
      ['Database', c.database || '—'],
    ],
  );

  if (c.description) {
    renderLabeledBlock(doc, 'DESCRIPTION', cleanText(c.description));
  }
  if (c.traces) {
    renderLabeledBlock(doc, 'TRACES', cleanText(c.traces));
  }
  if (c.specifications) {
    renderLabeledBlock(doc, 'SPECIFICATIONS', cleanText(c.specifications));
  }

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('ADDITIONAL HTS CODES', PAGE_MARGIN, doc.y, {
      characterSpacing: 0.6,
    });
  doc.moveDown(0.3);
  if (c.additionalHtsCodes.length) {
    renderTable(
      doc,
      [
        { header: 'HTS Code', width: 1.6, noWrap: true },
        { header: 'Country of Origin', width: 1.6, noWrap: true },
      ],
      c.additionalHtsCodes.map((h) => [
        h.htsCode || '—',
        h.countryOfOrigin || '—',
      ]),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text('No additional HTS codes.', PAGE_MARGIN, doc.y);
    doc.moveDown(0.8);
  }

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('ADDITIONAL COLUMNS', PAGE_MARGIN, doc.y, {
      characterSpacing: 0.6,
    });
  doc.moveDown(0.3);
  if (c.additionalColumns.length) {
    renderTable(
      doc,
      [
        { header: 'Column', width: 1.6 },
        { header: 'Value', width: 2.8 },
      ],
      c.additionalColumns.map((col) => [col.key, col.value || '—']),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No custom columns were imported with this product.',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(0.8);
  }
}

// Moves a sub-section label to the next page when too little room is left
// for its content, so the label isn't stranded at the bottom of a page.
function keepSubsectionLabelWithContent(doc: any): void {
  if (doc.y > doc.page.height - 120) {
    doc.addPage();
    doc.y = 70;
  }
}

function renderImportTariffSection(
  doc: any,
  data: IImportProductTicketReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '03', 'Duties & Tariffs');

  const t = data.tariff;

  keepSubsectionLabelWithContent(doc);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('TARIFF SPECIFIC INPUTS', PAGE_MARGIN, doc.y, {
      characterSpacing: 0.6,
    });
  doc.moveDown(0.3);
  if (t.materialInputs.length) {
    renderTable(
      doc,
      [
        { header: 'Field', width: 1.6 },
        { header: 'Value', width: 2 },
      ],
      t.materialInputs.map((m) => [m.label, m.value || '—']),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text('No tariff-specific inputs on file.', PAGE_MARGIN, doc.y);
    doc.moveDown(0.8);
  }

  keepSubsectionLabelWithContent(doc);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('RISK FLAGS', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.3);
  if (t.riskFlags.length) {
    renderTable(
      doc,
      [
        { header: 'Flag', width: 1.6 },
        { header: 'Detail', width: 3.4 },
      ],
      t.riskFlags.map((f) => [f.label, f.detail || '—']),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text('No AD/CVD, forced labor, or PGA risk flags on file.', PAGE_MARGIN, doc.y);
    doc.moveDown(0.8);
  }

  const costRows: Array<[string, string]> = [];
  if (t.declaredValue) costRows.push(['Declared Value', t.declaredValue]);
  if (t.baseDutyRate) costRows.push(['Base Duty Rate', t.baseDutyRate]);
  if (t.baseDutyCost) costRows.push(['Base Duty Cost', t.baseDutyCost]);
  if (t.additionalDutyRate)
    costRows.push(['Additional Duty Rate', t.additionalDutyRate]);
  if (t.additionalDutyCost)
    costRows.push(['Additional Duty Cost', t.additionalDutyCost]);
  if (t.totalDutyCost) costRows.push(['Total Duty Cost', t.totalDutyCost]);
  if (t.totalCost) costRows.push(['Total Landed Cost', t.totalCost]);

  keepSubsectionLabelWithContent(doc);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('COST SUMMARY', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.3);
  if (costRows.length) {
    renderTable(
      doc,
      [
        { header: 'Item', width: 2 },
        { header: 'Amount', width: 1.4, align: 'right', noWrap: true },
      ],
      costRows,
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No cost summary is available for this product.',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(0.8);
  }

  keepSubsectionLabelWithContent(doc);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('DUTY BREAKDOWN', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.3);
  if (t.dutyLines.length) {
    renderTable(
      doc,
      [
        { header: 'HTS Sub-code', width: 1.4, noWrap: true },
        { header: 'Line Item', width: 2.6 },
        { header: 'Rate', width: 1, align: 'right', noWrap: true },
        { header: 'Cost', width: 1.2, align: 'right', noWrap: true },
      ],
      t.dutyLines.map((l) => [l.subCode || '—', l.label, l.ratePct, l.cost]),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text('No duty line items on file.', PAGE_MARGIN, doc.y);
    doc.moveDown(0.8);
  }

  keepSubsectionLabelWithContent(doc);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('FREE TRADE AGREEMENTS', PAGE_MARGIN, doc.y, {
      characterSpacing: 0.6,
    });
  doc.moveDown(0.3);
  if (t.ftas.length) {
    renderTable(
      doc,
      [
        { header: 'Program', width: 2.4 },
        { header: 'Status', width: 1.2, noWrap: true },
        { header: 'Note', width: 2.4 },
      ],
      t.ftas.map((f) => [f.program, f.status, f.note || '—']),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text('No applicable free trade agreements.', PAGE_MARGIN, doc.y);
    doc.moveDown(0.8);
  }

  keepSubsectionLabelWithContent(doc);
  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('POTENTIAL EXCLUSIONS', PAGE_MARGIN, doc.y, {
      characterSpacing: 0.6,
    });
  doc.moveDown(0.3);
  if (t.exclusions.length) {
    renderTable(
      doc,
      [
        { header: 'Sub-code', width: 1.2, noWrap: true },
        { header: 'Exclusion', width: 2.6 },
        { header: 'Rate', width: 1, align: 'right', noWrap: true },
        { header: 'Applicability', width: 1.4, noWrap: true },
      ],
      t.exclusions.map((e) => [
        e.subCode || '—',
        e.label,
        e.ratePct,
        e.applicability,
      ]),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text('No exclusions applicable to this product.', PAGE_MARGIN, doc.y);
    doc.moveDown(0.8);
  }
}

function renderImportProgramCodes(
  doc: any,
  data: IImportProductTicketReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '04', 'Program Codes');

  const p = data.programCodes;

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('APPLIED PGA FLAGS', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.3);
  if (p.appliedFlagCodes.length) {
    renderPaginatedBodyText(doc, p.appliedFlagCodes.join(', '));
    doc.moveDown(0.4);
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text('No PGA flags applied yet.', PAGE_MARGIN, doc.y);
    doc.moveDown(0.8);
  }

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('SCREENING LENS SUMMARY', PAGE_MARGIN, doc.y, {
      characterSpacing: 0.6,
    });
  doc.moveDown(0.3);
  if (p.lensSummary.length) {
    renderTable(
      doc,
      [
        { header: 'Lens', width: 2.4 },
        { header: 'Total Matches', width: 1.4, align: 'right', noWrap: true },
        { header: 'New', width: 1, align: 'right', noWrap: true },
      ],
      p.lensSummary.map((l) => [
        l.name,
        fmtCount(l.total),
        fmtCount(l.newCount),
      ]),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No screening-lens matches for this HTS code.',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(0.8);
  }

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text(
      `TOP ${Math.min(p.matches.length, MAX_ROWS)} SCREENING MATCHES`,
      PAGE_MARGIN,
      doc.y,
      { characterSpacing: 0.6 },
    );
  doc.moveDown(0.3);
  if (p.matches.length) {
    renderTable(
      doc,
      [
        { header: 'Lens', width: 1.2 },
        { header: 'Entity / Record', width: 2.4 },
        { header: 'Type', width: 1.2, noWrap: true },
        { header: 'Country', width: 1.2, noWrap: true },
        { header: 'List', width: 2 },
        { header: 'Match', width: 0.8, align: 'right', noWrap: true },
      ],
      p.matches
        .slice(0, MAX_ROWS)
        .map((m) => [m.lens, m.entity, m.type, m.country, m.list, m.score]),
    );
  } else {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No screening matches were saved with this product.',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(0.8);
  }
}

function renderImportCrossRulings(
  doc: any,
  data: IImportProductTicketReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '05', 'Cross Rulings');

  if (!data.crossRulings.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No CROSS rulings loaded yet for this product.',
        PAGE_MARGIN,
        doc.y,
      );
    return;
  }

  renderTable(
    doc,
    [
      { header: '#', width: 0.6, align: 'right', noWrap: true },
      { header: 'Ruling', width: 3.4 },
      { header: 'Source', width: 2 },
    ],
    data.crossRulings.map((r, i) => [i + 1, r.title, r.url || '—']),
  );
}

function renderImportChangelog(
  doc: any,
  data: IImportProductTicketReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '06', 'Duty & Tariff Changelog');

  if (!data.changelog.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textPrimary)
      .text(
        'No duty or tariff changes recorded. Historical cost snapshots will ' +
          'appear here when this product changes.',
        PAGE_MARGIN,
        doc.y,
        { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
      );
    return;
  }

  renderTable(
    doc,
    [
      { header: 'Date', width: 1.2, noWrap: true },
      { header: 'Change', width: 2 },
      { header: 'Previous', width: 1.4 },
      { header: 'Current', width: 1.4 },
    ],
    data.changelog.map((c) => [
      c.date,
      c.heading,
      c.previousValue || '—',
      c.currentValue || '—',
    ]),
  );
}

export const importProductTicketPDF = async (
  data: IImportProductTicketReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE Import Hub Product Report — ${data.productName || ''}`,
          Author: 'BITE Import Hub',
          Subject: 'Import Hub Product Compliance Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('error', reject);
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · IMPORT HUB',
        headerRight: 'Product Compliance Report',
        footerFirstPage: 'Product Compliance Report',
      });

      renderImportProductCover(doc, data, logo);
      renderImportComplianceOverview(doc, data);
      renderImportClassification(doc, data);
      renderImportTariffSection(doc, data);
      renderImportProgramCodes(doc, data);
      renderImportCrossRulings(doc, data);
      renderImportChangelog(doc, data);

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

// ============================================================================

// Design Assistant saved ticket — PDF builder.
//
// Cover modelled on renderUnifiedCover (the Export Workflow/"Export Unified" report,
// the actual sibling tool): the verdict-strip fields (regime/primary control/destination/
// licence required) go straight into the cover meta strip, closed with the tool's own real
// disclaimer via renderFirstPageDisclaimer, the way ECCN's cover does.
//
// Sections modelled on the Beneficial Ownership report instead: this ticket's content is a
// mix of distinct shapes (a spec table, an order-of-review narrative, a checklist, a
// constraints table with quoted text, ranked options, a ladder, documents/trail) rather than
// one repeating shape like Export Workflow's per-stage Field/Saved-Result table, so each
// section is purpose-built per content type, same as renderBeneficialOwnershipOverview/
// Owners/etc. — reusing the same shared primitives every docType already uses below.
//
// SECTION ORDER FOLLOWS THE GUIDED FLOW (the four named stages): Overview, then Product Specs &
// Use, Classification and Specification Map. The fourth stage, the Final Design Summary, is not a
// section: this report IS the saved summary, so its summary and recommended next step lead the
// Overview, and what the report adds (caveats) follows with any analysis that ran. Licensing,
// design changes, design options and the regime ladder are not stages: they appear only when a
// check/analysis ran or the report produced them, never as an empty headed block. Sections are
// numbered as they are emitted, so a report that stopped early has no gaps in its numbering.
//
// DesignTicketPayload (imported as a type above) is reused directly as this report's data
// shape rather than adding a parallel interface + buildXReportData() mapper: unlike every
// other docType, it's already exactly the flattened shape the ticket itself renders from.
// ============================================================================

// The PDF font (Open Sans) has no arrow glyph, so "removable → fixed" printed a missing-glyph box.
// Spelled out here rather than in the shared cleanText, which every other report also uses.
const designClean = (value: any): string => cleanText(value).replace(/\s*→\s*/g, ' to ');

const designLicenceVerdict = (v: boolean | null): string =>
  v === true ? 'Yes' : v === false ? 'No' : 'Uncertain';

function renderExportDesignCover(
  doc: any,
  data: DesignTicketPayload,
  logo: ArrayBuffer | string | null,
): void {
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* logo is optional */
    }
  }

  // Higher than the other covers (220): this one carries up to eleven rows, and anything lower
  // pushes the closing paragraph past the page foot, onto an otherwise empty second page.
  const titleY = 160;
  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('BITE Design Assistant Report', {
      width: titleWidth,
      lineGap: 2,
    });
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text('BITE Design Assistant Report', PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 150;
  const valueW = doc.page.width - PAGE_MARGIN - valueX;
  const meta: Array<[string, string]> = [
    ['TICKET', designClean(data.ticketName) || '—'],
    // The control and what it is controlled for lead; the regime is the label after them. (EAR99 has
    // no code of its own, so the control would just repeat the regime.)
    ...(data.classified &&
    designClean(data.primaryControl) &&
    designClean(data.primaryControl) !== designClean(data.regime)
      ? ([
          [data.regime === 'ITAR' ? 'USML CATEGORY' : 'ECCN', designClean(data.primaryControl)],
          ...((data.reasonsForControl || []).length
            ? [['CONTROLLED FOR', (data.reasonsForControl || []).join(', ')]]
            : []),
        ] as Array<[string, string]>)
      : []),
    ['REGIME', data.classified ? designClean(data.regime) || '—' : 'Not classified yet'],
    ...(data.intendedUse
      ? ([['INTENDED USE', designClean(data.intendedUse)]] as Array<[string, string]>)
      : []),
    // Only when a licensing check ran: a destination is optional, and an "Uncertain" licence
    // verdict over a check nobody asked for reads as an inconclusive result.
    ...(data.licensingRan
      ? ([
          ['DESTINATION', designClean(data.destination) || '—'],
          ['LICENCE REQUIRED', designLicenceVerdict(data.licenceRequired)],
        ] as Array<[string, string]>)
      : []),
    ['STATUS', designClean(data.status) || '—'],
    ['FREQUENCY', designClean(data.frequency) || '—'],
    ['GENERATED', fmtDateTimeLong(new Date())],
    ['PREPARED BY', designClean(data.preparedBy) || 'BITE Design Assistant'],
  ];

  let y = titleY + Math.max(titleHeight, 46) + 40;
  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, y - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, y - 6)
    .stroke();

  meta.forEach(([label, value]) => {
    const measuredHeight = doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .heightOfString(value, { width: valueW, lineGap: 2 });
    const rowH = Math.max(24, Math.min(measuredHeight, 44) + 12);

    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text(label, labelX, y + 6, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(value, valueX, y + 5, {
        width: valueW,
        height: rowH - 10,
        ellipsis: true,
      });
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(labelX, y + rowH - 4)
      .lineTo(doc.page.width - PAGE_MARGIN, y + rowH - 4)
      .stroke();
    y += rowH;
  });

  const accentY = y + 16;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(2.5)
    .moveTo(PAGE_MARGIN, accentY)
    .lineTo(PAGE_MARGIN + 60, accentY)
    .stroke();
  const intro =
    'A record of the saved design assessment: specs and use, classification, which specs drive each control, and — where generated — the Final Design Summary.';
  const introWidth = doc.page.width - 2 * PAGE_MARGIN;
  const introHeight = doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .heightOfString(intro, { width: introWidth, lineGap: 2 });
  // The intro is a courtesy, not content: drop it rather than let it spill onto a second page
  // (the disclaimer sits in the last ~110pt of this one).
  const introFits = accentY + 18 + introHeight <= doc.page.height - 112;
  if (introFits) {
    doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(intro, PAGE_MARGIN, accentY + 18, { width: introWidth, lineGap: 2 });
  }

  renderFirstPageDisclaimer(
    doc,
    DESIGN_ASSESSMENT_DISCLAIMER,
    undefined,
    introFits ? accentY + 18 + introHeight : accentY,
  );
}

// ---- Layout helpers for this report only. The shared renderKpiCards / renderLabeledBlock stay as they
// are for the other reports; these cover what the Design Assistant's content needs.

/** KPI cards whose text always fits: the value shrinks to stay on one line (a code such as
 *  "6A003.b.4.b" wrapped onto the label below it at the fixed 18pt) and the cards grow to fit a
 *  label that wraps ("PRODUCT SPECS & USE"), all sharing one height. */
function renderDesignKpiCards(doc: any, cards: Array<{ value: string; label: string }>): void {
  const startY = doc.y;
  const usable = doc.page.width - 2 * PAGE_MARGIN;
  const gap = 10;
  const w = (usable - gap * (cards.length - 1)) / cards.length;
  const inner = w - 20;
  const labelTop = 40;

  const labelH = Math.max(
    ...cards.map((c) =>
      doc.font(FONT_BOLD).fontSize(8).heightOfString(c.label, { width: inner, characterSpacing: 0.6 }),
    ),
  );
  const h = Math.max(64, labelTop + labelH + 12);

  cards.forEach((c, i) => {
    const x = PAGE_MARGIN + i * (w + gap);
    doc.save();
    doc.roundedRect(x, startY, w, h, 4).fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
    doc.rect(x, startY, w, 3).fill(COLOR.primary);
    doc.restore();

    let size = 18;
    doc.font(FONT_BOLD);
    while (size > 10 && doc.fontSize(size).widthOfString(c.value) > inner) size -= 0.5;
    doc
      .fillColor(COLOR.primary)
      .font(FONT_BOLD)
      .fontSize(size)
      .text(c.value, x + 10, startY + 12 + (18 - size) / 2, {
        width: inner,
        lineBreak: false,
        ellipsis: true,
      });
    doc
      .fillColor(COLOR.textSecondary)
      .font(FONT_BOLD)
      .fontSize(8)
      .text(c.label, x + 10, startY + labelTop, { width: inner, characterSpacing: 0.6 });
  });

  doc.y = startY + h + 18;
}

/** A section follows the previous one on the same page when there is room, and only starts a new
 *  page when there is not — each section used to take a fresh page, leaving most of pages 2-5 blank. */
const DESIGN_SECTION_MIN_ROOM = 240;
function startDesignSection(doc: any): void {
  if (doc.y > doc.page.height - DESIGN_SECTION_MIN_ROOM) {
    doc.addPage();
    doc.y = 70;
  } else {
    doc.moveDown(1.4);
  }
}

function renderDesignSubheading(doc: any, text: string): void {
  if (doc.y > doc.page.height - 140) {
    doc.addPage();
    doc.y = 70;
  }
  doc.moveDown(0.4);
  doc.font(FONT_BOLD).fontSize(11.5).fillColor(COLOR.textPrimary).text(text, PAGE_MARGIN, doc.y);
  doc.moveDown(0.4);
}

/** A named entry (a design change, an option) in sentence case, with its description beneath. Codes
 *  and names are shown as written, not upper-cased as a small-caps label would. */
function renderDesignItem(doc: any, title: string, body: string): void {
  if (doc.y > doc.page.height - 120) {
    doc.addPage();
    doc.y = 70;
  }
  doc
    .font(FONT_BOLD)
    .fontSize(10)
    .fillColor(COLOR.textPrimary)
    .text(title, PAGE_MARGIN, doc.y, { width: doc.page.width - 2 * PAGE_MARGIN });
  doc.moveDown(0.15);
  renderPaginatedBodyText(doc, body);
  doc.moveDown(0.5);
}

function renderExportDesignOverview(
  doc: any,
  data: DesignTicketPayload,
  num: string,
): void {
  // Single caller (right after the cover), so always safe to continue on a
  // page the cover's disclaimer already spilled onto rather than force a
  // fresh, near-empty one.
  continueOrAddPage(doc);
  renderSectionHeader(doc, num, 'Overview');

  // One card per stage of the guided flow, plus confidence — and only for what exists: a clean
  // result with no binding controls to map, or no stated confidence, gets no card rather than a
  // "none" one.
  renderDesignKpiCards(doc, [
    { value: fmtCount(data.specs.length), label: 'PRODUCT SPECS & USE' },
    {
      // The control when there is one (the regime is in the cover and the sections below).
      value: !data.classified
        ? 'Not run yet'
        : designClean(data.primaryControl) && designClean(data.primaryControl) !== designClean(data.regime)
        ? designClean(data.primaryControl)
        : designClean(data.regime) || '—',
      label: 'CLASSIFICATION',
    },
    ...(data.constraints.length
      ? [{ value: fmtCount(data.constraints.length), label: 'SPECIFICATION MAP' }]
      : []),
    ...(designClean(data.confidence) && designClean(data.confidence) !== 'Not stated'
      ? [{ value: designClean(data.confidence), label: 'CONFIDENCE' }]
      : []),
  ]);

  if (data.executiveSummary.length) {
    renderPaginatedLabeledBlock(doc, 'SUMMARY', data.executiveSummary.join('\n\n'));
  }
  if (designClean(data.recommendedNextStep)) {
    renderPaginatedLabeledBlock(
      doc,
      'RECOMMENDED NEXT STEP',
      designClean(data.recommendedNextStep),
    );
  }
  if (designClean(data.licensingImplication)) {
    renderPaginatedLabeledBlock(
      doc,
      'WHAT THIS MEANS FOR LICENSING',
      designClean(data.licensingImplication),
    );
  }
}

function renderExportDesignSpecsAndUse(
  doc: any,
  data: DesignTicketPayload,
  num: string,
): void {
  startDesignSection(doc);
  renderSectionHeader(doc, num, 'Product Specs & Use');

  const hasDestination = Boolean(designClean(data.destination)) && designClean(data.destination) !== '—';
  const rows: string[][] = [
    data.intendedUse
      ? ['Intended use', designClean(data.intendedUse), 'stated by you']
      : ['Intended use', 'Not disclosed', 'none given'],
    ...(hasDestination
      ? [['Destination country', designClean(data.destination), 'optional — used for licensing advice']]
      : []),
    ...data.specs.map((spec) => [
      designClean(spec.label),
      designClean(spec.value),
      designClean(spec.source) || '—',
    ]),
  ];
  if (rows.length) {
    renderTable(
      doc,
      [
        { header: 'Parameter', width: 1.6 },
        { header: 'Value', width: 2.2 },
        { header: 'Source', width: 1.6 },
      ],
      rows,
    );
    doc.moveDown(0.6);
  } else {
    renderLabeledBlock(
      doc,
      'PRODUCT SPECS & USE',
      'No specs were recorded for this assessment.',
    );
  }
}

function renderExportDesignClassification(
  doc: any,
  data: DesignTicketPayload,
  num: string,
): void {
  startDesignSection(doc);
  renderSectionHeader(doc, num, 'Classification');

  data.orderOfReview.forEach((step) => {
    renderPaginatedLabeledBlock(
      doc,
      `${step.title.toUpperCase()}${step.tag ? ` (${step.tag.toUpperCase()})` : ''}`,
      step.body,
    );
  });

  const unavailableText = `You told the tool you can't supply ${data.unavailableSpecs.join(
    ', ',
  )} — recorded so it never asks again.`;
  // Saved before a classification was run: no determination, verification or assumptions to report.
  // Say so, rather than print "no open items" or a verification verdict over nothing.
  if (!data.classified) {
    renderLabeledBlock(
      doc,
      'NOT CLASSIFIED YET',
      'This product has not been classified yet. Continue the assessment to classify it, then save it again.',
    );
    if (data.unavailableSpecs.length) {
      renderLabeledBlock(doc, 'DECLARED UNAVAILABLE', unavailableText);
    }
    return;
  }

  if (data.openItems.length) {
    renderLabeledBlock(doc, 'OPEN ITEMS', 'Specs the classification assumed. Resolve, or knowingly accept.');
    renderOrderedList(
      doc,
      data.openItems.map(
        (item) =>
          `${designClean(item.missingSpec)} — Assumed: ${designClean(
            item.assumed,
          )}. Impact: ${designClean(item.impact)}${
            item.couldRaise ? ' (could raise control)' : ''
          }`,
      ),
    );
  } else {
    renderLabeledBlock(
      doc,
      'OPEN ITEMS',
      'No open items — every control-relevant spec was supplied.',
    );
  }
  if (data.unavailableSpecs.length) {
    renderLabeledBlock(doc, 'DECLARED UNAVAILABLE', unavailableText);
  }

  // Verifies the classification itself, so it lives here — and survives a clean result with no
  // binding controls (which has no Specification Map section at all).
  renderPaginatedLabeledBlock(
    doc,
    !data.verifierRan
      ? 'NOT INDEPENDENTLY VERIFIED'
      : data.verifierSupported
      ? 'INDEPENDENTLY VERIFIED'
      : 'VERIFICATION RAISED ISSUES',
    data.verifierNote,
  );
}

function renderExportDesignSpecificationMap(
  doc: any,
  data: DesignTicketPayload,
  num: string,
): void {
  startDesignSection(doc);
  renderSectionHeader(doc, num, 'Specification Map');

  renderTable(
    doc,
    [
      { header: 'Control', width: 1.4 },
      { header: 'Your spec', width: 1.8 },
      { header: 'What would decontrol it', width: 2.4 },
    ],
    data.constraints.map((c) => [
      designClean(c.citation),
      designClean(c.matchedSpec),
      designClean(c.decontrol),
    ]),
  );
  doc.moveDown(0.6);
  data.constraints.forEach((c) => {
    renderPaginatedLabeledBlock(
      doc,
      `QUOTED FROM ${c.citation}`,
      c.controlText,
    );
  });
}

/** True when there is anything past the Specification Map to show: the report's caveats, or an
 *  analysis that was asked for or generated. */
const hasExportDesignExtras = (data: DesignTicketPayload): boolean =>
  data.reportCaveats.length > 0 ||
  data.licensingRan ||
  Boolean(data.designChanges) ||
  data.options.length > 0 ||
  data.ladder.length > 0;

function renderExportDesignSummaryAndExtras(
  doc: any,
  data: DesignTicketPayload,
  num: string,
): void {
  startDesignSection(doc);
  const hasCaveats = data.reportCaveats.length > 0;
  const hasAnalysis =
    data.licensingRan ||
    Boolean(data.designChanges) ||
    data.options.length > 0 ||
    data.ladder.length > 0;
  renderSectionHeader(
    doc,
    num,
    hasCaveats && hasAnalysis
      ? 'Caveats & Further Analysis'
      : hasCaveats
      ? 'Caveats'
      : 'Further Analysis',
  );

  // Each analysis is its own group under a sub-heading (they used to run together as a flat stack
  // of small-caps labels), with the same few building blocks throughout.
  if (hasCaveats) {
    renderDesignSubheading(doc, 'Caveats');
    renderPaginatedBodyText(doc, 'Gaps and assumptions the summary could not settle.');
    doc.moveDown(0.4);
    renderOrderedList(doc, data.reportCaveats.map((c) => designClean(c)));
  }

  if (data.licensingRan) {
    const destination = designClean(data.destination);
    renderDesignSubheading(
      doc,
      `Licensing${destination && destination !== '—' ? ` — ${destination}` : ''}`,
    );
    const verdictSentence =
      data.licenceRequired === true
        ? 'A licence is required.'
        : data.licenceRequired === false
        ? 'No licence appears required on the inputs checked.'
        : 'Whether a licence is required is uncertain.';
    renderPaginatedBodyText(
      doc,
      [verdictSentence, designClean(data.licensingSummary)].filter(Boolean).join(' '),
    );
    doc.moveDown(0.5);

    // The payload carries the reasons as consecutive rows (the first labelled "Basis", the rest
    // blank, for the on-screen ticket) followed by the embargo / §126.1 / exceptions checks.
    const isBasis = (b: { label: string }): boolean => {
      const label = designClean(b.label);
      return !label || /^basis$/i.test(label);
    };
    const basis = data.licensingBasis.filter(isBasis);
    const checks = data.licensingBasis.filter((b) => !isBasis(b));
    if (basis.length) {
      renderLabeledBlock(doc, 'BASIS', 'Why the answer is what it is.');
      renderOrderedList(doc, basis.map((b) => designClean(b.value)));
    }
    if (checks.length) {
      doc.moveDown(0.3);
      renderTable(
        doc,
        [
          { header: 'Check', width: 1.4 },
          { header: 'Result', width: 3.2 },
        ],
        checks.map((c) => [designClean(c.label), designClean(c.value)]),
      );
      doc.moveDown(0.6);
    }
  }

  const changes = data.designChanges;
  if (changes) {
    renderDesignSubheading(
      doc,
      `Design changes${changes.target ? ` — to ${designClean(changes.target)}` : ''}`,
    );
    renderPaginatedBodyText(
      doc,
      [
        changes.reachable ? 'Reachable by a design change.' : 'Not reachable by a design change.',
        designClean(changes.summary),
      ]
        .filter(Boolean)
        .join(' '),
    );
    doc.moveDown(0.5);
    changes.changes.forEach((ch) => {
      renderDesignItem(
        doc,
        designClean(ch.description),
        ch.details.map((d) => designClean(d)).join(' · ') || '—',
      );
    });
    if (changes.resultingRegime) {
      renderLabeledBlock(doc, 'RESULT', `The changed product would be ${designClean(changes.resultingRegime)}.`);
    }
    if (changes.cost) renderPaginatedLabeledBlock(doc, 'WHAT IT COSTS', designClean(changes.cost));
    if (changes.residual.length) {
      renderPaginatedLabeledBlock(
        doc,
        'STILL BINDING AFTERWARDS',
        changes.residual.map((r) => designClean(r)).join(' · '),
      );
    }
  }

  // Report option text (changeSummary/feasibility/tradeoff) already ends in its own
  // punctuation more often than not — don't blindly append a second period on top of it.
  const withPeriod = (s: string): string => (/[.!?]$/.test(s) ? s : `${s}.`);

  if (data.options.length) {
    renderDesignSubheading(doc, 'Design options');
    data.options.forEach((o) => {
      const bodyParts = [
        withPeriod(`Reaches ${designClean(o.targetRegime)}`),
        withPeriod(`Change: ${designClean(o.changeSummary)}`),
      ];
      if (o.feasibility) bodyParts.push(withPeriod(`Feasibility: ${designClean(o.feasibility)}`));
      if (o.tradeoff) bodyParts.push(withPeriod(`Trade-off: ${designClean(o.tradeoff)}`));
      renderDesignItem(
        doc,
        `${designClean(o.label)}${o.recommended ? ' (recommended)' : ''}`,
        bodyParts.join(' '),
      );
    });
  }
  if (designClean(data.optionsNote)) {
    renderLabeledBlock(doc, 'NOTE', designClean(data.optionsNote));
  }

  if (data.ladder.length) {
    renderDesignSubheading(doc, 'Regime ladder');
    renderTable(
      doc,
      [
        { header: 'Regime', width: 2.4 },
        { header: 'Status', width: 1.1 },
        { header: 'What it means', width: 3 },
      ],
      data.ladder.map((rung) => [
        designClean(rung.regime),
        rung.isCurrent ? 'Current' : rung.reachable === false ? 'Not reachable' : 'Reachable',
        designClean(rung.note) || '—',
      ]),
    );
    doc.moveDown(0.6);
  }
}

function renderExportDesignReference(
  doc: any,
  data: DesignTicketPayload,
  num: string,
): void {
  startDesignSection(doc);
  renderSectionHeader(doc, num, 'Documents & Trail');

  if (data.documents.length) {
    renderTable(
      doc,
      [
        { header: 'Document', width: 2 },
        { header: 'Details', width: 3 },
      ],
      data.documents.map((d) => [designClean(d.name), designClean(d.meta)]),
    );
    doc.moveDown(0.6);
  }

  if (data.trail.length) {
    renderTable(
      doc,
      [
        { header: 'Time', width: 1 },
        { header: 'Note', width: 4 },
      ],
      data.trail.map((t) => [designClean(t.time), designClean(t.text)]),
    );
    doc.moveDown(0.6);
  }

  if (designClean(data.supersededNote)) {
    renderLabeledBlock(doc, 'SUPERSEDED', designClean(data.supersededNote));
  }
  if (designClean(data.regulationEdition)) {
    renderLabeledBlock(doc, 'REGULATION EDITION', designClean(data.regulationEdition));
  }
}

// ---- The Conversation section: the saved chat, as the ticket's Conversation panel shows it
// (ExportDesignTicket.tsx ConversationPanel). Always the last section, so nothing before it moves.

type DesignChatBlock = { kind: 'gap' | 'heading' | 'text' | 'bullet' | 'subbullet'; text: string; marker?: string };

/** Characters Open Sans has no glyph for print as a box: an arrow reads as "to" (as designClean does), and
 *  the emoji and symbols the assistant uses as markers (the ladder's pin and stop sign, ticks) are dropped. */
const designChatGlyphSafe = (input: string): string =>
  input
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/[ \t]*→[ \t]*/g, ' to ')
    .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '')
    .replace(/[←-⇿☀-➿]️?/g, '')
    .replace(/️/g, '');

/** Inline markdown as plain text: links, bold, italics, code and backslash escapes. */
const designChatInline = (input: string): string =>
  input
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(^|[\s(])\*(?!\s)([^*]+?)\*(?=[\s).,;:!?]|$)/g, '$1$2')
    .replace(/(^|[\s(])_(?!\s)([^_]+?)_(?=[\s).,;:!?]|$)/g, '$1$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\\([\\`*_{}[\]()#+\-.!"'|>~])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();

/** An assistant message (markdown) as blocks the PDF can draw: headings and lines that are wholly bold
 *  stay bold, lists keep their markers, a table row reads as its cells, rules and table rulings go. */
const designChatBlocks = (markdown: string): DesignChatBlock[] => {
  const blocks: DesignChatBlock[] = [];
  const pushGap = (): void => {
    if (blocks.length && blocks[blocks.length - 1].kind !== 'gap') blocks.push({ kind: 'gap', text: '' });
  };
  designChatGlyphSafe(markdown)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .forEach((raw) => {
      const indented = /^( {2,}|\t)\s*([-*+]|\d+[.)])\s+/.test(raw);
      const line = raw.trim().replace(/^>\s?/, '');
      // A blank line or a horizontal rule ends a paragraph; a table's ruling row has nothing to print.
      if (!line || /^([-*_])(\s*\1){2,}$/.test(line)) {
        pushGap();
        return;
      }
      if (/^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?$/.test(line)) return;

      const heading = line.match(/^#{1,6}\s+(.*)$/);
      const item = line.match(/^([-*+]|\d+[.)])\s+(.*)$/);
      const wholeBold = line.match(/^\*\*(.+)\*\*:?$/);
      if (heading) {
        pushGap();
        blocks.push({ kind: 'heading', text: designChatInline(heading[1]) });
      } else if (/^\|.*\|$/.test(line)) {
        const cells = line
          .slice(1, -1)
          .split('|')
          .map((cell) => designChatInline(cell))
          .filter(Boolean);
        if (cells.length) blocks.push({ kind: 'text', text: cells.join(' · ') });
      } else if (item) {
        const ordered = /^\d/.test(item[1]);
        blocks.push({
          kind: indented ? 'subbullet' : 'bullet',
          marker: ordered ? item[1].replace(')', '.') : indented ? '–' : '•',
          text: designChatInline(item[2]),
        });
      } else {
        blocks.push({
          kind: wholeBold && !/\*\*/.test(wholeBold[1]) ? 'heading' : 'text',
          text: designChatInline(line),
        });
      }
    });
  while (blocks.length && blocks[blocks.length - 1].kind === 'gap') blocks.pop();
  return blocks;
};

/** "Oct 27, 14:05" in the reader's own time zone, as the ticket's Conversation panel prints it. */
const designChatTime = (ts: string): string => {
  const date = new Date(ts);
  if (!ts || Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
};

const DESIGN_CHAT_PAGE_BOTTOM = 58;
const DESIGN_CHAT_INDENT = 12;

/** Starts a new page when fewer than `needed` points are left above the footer. */
function ensureDesignChatRoom(doc: any, needed: number): void {
  if (doc.y + needed > doc.page.height - DESIGN_CHAT_PAGE_BOTTOM) {
    doc.addPage();
    doc.y = 70;
  }
}

function renderDesignChatBody(doc: any, blocks: DesignChatBlock[], x: number, width: number): void {
  blocks.forEach((block, index) => {
    if (block.kind === 'gap') {
      doc.moveDown(0.4);
      return;
    }
    if (block.kind === 'heading') {
      doc.font(FONT_BOLD).fontSize(10);
      const height = doc.heightOfString(block.text, { width: width, lineGap: 2 });
      ensureDesignChatRoom(doc, Math.min(height, 60) + 14);
      doc
        .font(FONT_BOLD)
        .fontSize(10)
        .fillColor(COLOR.textPrimary)
        .text(block.text, x, doc.y, { width: width, lineGap: 2 });
      doc.moveDown(0.15);
      return;
    }
    if (block.kind === 'text') {
      renderPaginatedBodyText(doc, block.text, { x: x, width: width });
      return;
    }
    const markerX = block.kind === 'subbullet' ? x + 14 : x;
    const textX = markerX + 14;
    doc.font(FONT_REGULAR).fontSize(10);
    ensureDesignChatRoom(doc, doc.heightOfString('Ag', { width: width }) + 4);
    const y = doc.y;
    doc
      .font(block.marker === '•' || block.marker === '–' ? FONT_REGULAR : FONT_BOLD)
      .fontSize(10)
      .fillColor(COLOR.primary)
      .text(block.marker || '•', markerX, y, { width: 14, lineBreak: false });
    doc.y = y;
    renderPaginatedBodyText(doc, block.text, { x: textX, width: x + width - textX });
    const next = blocks[index + 1];
    if (next && (next.kind === 'bullet' || next.kind === 'subbullet')) doc.moveDown(0.15);
  });
}

/** The page index PDFKit is writing to (pages are buffered, so earlier ones can be drawn on again). */
const designChatPageIndex = (doc: any): number => {
  const range = doc.bufferedPageRange();
  return range.start + range.count - 1;
};

/** The ticket marks the user's messages with a blue bar down their left edge; draw it on every page the
 *  message ran onto. */
function drawDesignChatUserBar(doc: any, from: { page: number; y: number }, toY: number): void {
  const last = designChatPageIndex(doc);
  const savedX = doc.x;
  const savedY = doc.y;
  for (let page = from.page; page <= last; page++) {
    doc.switchToPage(page);
    const top = page === from.page ? from.y : 64;
    const bottom = page === last ? toY : doc.page.height - DESIGN_CHAT_PAGE_BOTTOM;
    if (bottom > top) {
      doc.save();
      doc.rect(PAGE_MARGIN, top, 3, bottom - top).fill(COLOR.primary);
      doc.restore();
    }
  }
  doc.switchToPage(last);
  doc.x = savedX;
  doc.y = savedY;
}

function renderExportDesignConversation(
  doc: any,
  data: DesignTicketPayload,
  num: string,
): void {
  startDesignSection(doc);
  renderSectionHeader(doc, num, 'Conversation');
  const count = data.conversation.length;
  renderPaginatedBodyText(
    doc,
    `What was asked, and what the assistant answered (${count} ${count === 1 ? 'message' : 'messages'}).`,
    { color: COLOR.textSecondary },
  );
  doc.moveDown(0.8);

  const x = PAGE_MARGIN + DESIGN_CHAT_INDENT;
  const width = doc.page.width - PAGE_MARGIN - x;

  data.conversation.forEach((message, index) => {
    if (index > 0) {
      ensureDesignChatRoom(doc, 70);
      doc
        .strokeColor(COLOR.divider)
        .lineWidth(0.5)
        .moveTo(PAGE_MARGIN, doc.y)
        .lineTo(doc.page.width - PAGE_MARGIN, doc.y)
        .stroke();
      doc.moveDown(0.8);
    }
    // The name, its time and the first lines of the message stay together.
    ensureDesignChatRoom(doc, 56);
    const start = { page: designChatPageIndex(doc), y: doc.y - 2 };

    const name = message.sender === 'user' ? 'You' : 'Design Assistant';
    const time = designChatTime(message.ts);
    const metaY = doc.y;
    doc
      .font(FONT_BOLD)
      .fontSize(9.5)
      .fillColor(COLOR.textPrimary)
      .text(name, x, metaY, { width: width, lineBreak: false });
    if (time) {
      doc
        .font(FONT_REGULAR)
        .fontSize(8.5)
        .fillColor(COLOR.textMuted)
        .text(time, x, metaY + 1, { width: width, align: 'right', lineBreak: false });
    }
    doc.y = metaY + 15;

    const files = (message.files || []).map((file) => designChatGlyphSafe(cleanText(file))).filter(Boolean);
    if (files.length) {
      renderPaginatedBodyText(doc, `Attached: ${files.join(' · ')}`, {
        x: x,
        width: width,
        fontSize: 9,
        color: COLOR.textSecondary,
      });
      doc.moveDown(0.3);
    }

    if (message.isReport) {
      renderPaginatedBodyText(doc, 'Final Design Summary, shown in full above.', {
        x: x,
        width: width,
        color: COLOR.textSecondary,
      });
    } else if (message.sender === 'user') {
      // Shown as typed, like the ticket (which does not read the user's text as markdown).
      designChatGlyphSafe(message.text)
        .replace(/\r\n?/g, '\n')
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)
        .forEach((paragraph, paragraphIndex) => {
          if (paragraphIndex > 0) doc.moveDown(0.4);
          renderPaginatedBodyText(doc, paragraph, { x: x, width: width });
        });
    } else {
      renderDesignChatBody(doc, designChatBlocks(message.text), x, width);
    }

    if (message.sender === 'user') drawDesignChatUserBar(doc, start, doc.y + 2);
    doc.moveDown(0.8);
  });
}

export const exportDesignTicketReportPDF = async (
  data: DesignTicketPayload,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE Design Assistant Report — ${data.ticketName || ''}`,
          Author: 'BITE Design Assistant',
          Subject: 'Design Assessment Saved Work Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('error', reject);
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · DESIGN ASSISTANT',
        headerRight: 'Design Assessment Report',
        footerFirstPage: 'Design Assessment Report',
      });

      renderExportDesignCover(doc, data, logo);
      // Numbered as emitted, so an assessment that stopped early has no gap in its numbering.
      let section = 0;
      const nextSection = (): string => String(++section).padStart(2, '0');
      renderExportDesignOverview(doc, data, nextSection());
      renderExportDesignSpecsAndUse(doc, data, nextSection());
      renderExportDesignClassification(doc, data, nextSection());
      // Only what exists: no Specification Map when nothing was mapped, no summary/analysis
      // section when none was generated or asked for, no reference section when it'd be empty.
      if (data.constraints.length) {
        renderExportDesignSpecificationMap(doc, data, nextSection());
      }
      if (hasExportDesignExtras(data)) {
        renderExportDesignSummaryAndExtras(doc, data, nextSection());
      }
      if (
        data.documents.length ||
        data.trail.length ||
        designClean(data.supersededNote) ||
        designClean(data.regulationEdition)
      ) {
        renderExportDesignReference(doc, data, nextSection());
      }
      // The saved chat comes last (S12 R-11, the one agreed addition to the frozen PDF).
      if (data.conversation.length) {
        renderExportDesignConversation(doc, data, nextSection());
      }

      doc.end();
    } catch (error) {
      reject(error);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = finalName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

// ============================================================================
// Business Verification report
// ----------------------------------------------------------------------------
// Downloaded when a Verification Run finishes. Same family as the saved-work
// tickets (cover, chrome, KPI cards, blue-header tables); sections: Verification
// Summary, Check Details (one block per check with what it read and the
// evidence it returned), and Record Details.
// ============================================================================

export type BusinessVerificationTone =
  | 'passed'
  | 'failed'
  | 'review'
  | 'input'
  | 'unavailable'
  | 'not-run'
  | 'running'
  | 'locked';

export interface IBusinessVerificationReportCheck {
  label: string;
  tone: BusinessVerificationTone;
  /** "Passed", "Needs review", "Not updated", ... */
  statusLabel: string;
  description: string;
  /** "What was found", "What closes it" or "Why it did not run". */
  nextTitle: string;
  next: string;
  reads: Array<{ label: string; value: string }>;
  evidence: Array<{ label: string; value: string }>;
  /** "Oct 2, 2026 · 11:14 AM", or empty when the check has not run. */
  checkedAt: string;
  /** "Oct 2, 2026", for the summary table. */
  checkedOn: string;
}

export interface IAesTicketReportData {
  run: AesRunDetails;
  lines: AesLine[];
  preparedBy?: string;
}

export interface IBusinessVerificationReportData {
  entityName: string;
  databaseName?: string;
  country?: string;
  status?: string;
  lastVerified?: string;
  preparedBy?: string;
  figure: string;
  figureTone: BusinessVerificationTone;
  tail: string;
  summary: string;
  counts: { passed: number; attention: number; failed: number };
  /** What the run that produced this report did, when it is known. */
  runNotes?: string[];
  checks: IBusinessVerificationReportCheck[];
  fields: Array<{ label: string; value: string; usedBy: string }>;
}

// The saved-work amber reads as a fill; as small text on white it needs a
// deeper shade to stay legible in print.
const BV_AMBER_TEXT = '#B54708';

const bvToneColor = (tone: BusinessVerificationTone): string => {
  if (tone === 'passed') return COLOR.riskLow;
  if (tone === 'failed') return COLOR.riskHigh;
  if (tone === 'review' || tone === 'input') return BV_AMBER_TEXT;
  if (tone === 'running') return COLOR.primary;
  return COLOR.textMuted;
};

function renderBusinessVerificationCover(
  doc: any,
  data: IBusinessVerificationReportData,
  logo: ArrayBuffer | string | null,
): void {
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* ignore logo failures */
    }
  }

  const title = 'Verification Report';
  const titleY = 220;
  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString(title, { width: titleWidth, lineGap: 2 });
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text(title, PAGE_MARGIN, titleY, { width: titleWidth, lineGap: 2 });

  const metaY = titleY + Math.max(titleHeight, 46) + 44;
  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 150;
  const valueW = doc.page.width - valueX - PAGE_MARGIN;
  const meta: Array<[string, string, string?]> = [
    ['ENTITY', cleanText(data.entityName) || '-'],
  ];
  if (data.databaseName) meta.push(['DATABASE', cleanText(data.databaseName)]);
  if (data.country) meta.push(['COUNTRY', cleanText(data.country)]);
  if (data.status) meta.push(['ENTITY STATUS', cleanText(data.status)]);
  meta.push([
    'RESULT',
    // "3 open of 7 checks · 4 passed", "7 of 7 checks passed", "2 failed · 4 passed"
    cleanText(data.tail).startsWith('of ') ||
    cleanText(data.tail).startsWith('checks ')
      ? `${cleanText(data.figure)} ${cleanText(data.tail)}`
      : `${cleanText(data.figure)} · ${cleanText(data.tail)}`,
    bvToneColor(data.figureTone),
  ]);
  meta.push([
    'LAST VERIFIED',
    cleanText(data.lastVerified) || 'Not yet verified',
  ]);
  meta.push(['GENERATED', fmtDateTimeLong(new Date())]);
  meta.push([
    'PREPARED BY',
    cleanText(data.preparedBy) || 'BITE Business Verification',
  ]);

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, metaY - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, metaY - 6)
    .stroke();

  let y = metaY;
  meta.forEach(([label, value, color]) => {
    const measuredHeight = doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .heightOfString(value, { width: valueW, lineGap: 2 });
    const rowH = Math.max(26, Math.min(measuredHeight, 40) + 14);

    doc
      .font(FONT_BOLD)
      .fontSize(8)
      .fillColor(COLOR.textSecondary)
      .text(label, labelX, y, { width: 140, characterSpacing: 0.6 });
    doc
      .font(color ? FONT_BOLD : FONT_REGULAR)
      .fontSize(10)
      .fillColor(color || COLOR.textPrimary)
      .text(value, valueX, y - 1, {
        width: valueW,
        height: rowH - 8,
        ellipsis: true,
      });
    y += rowH;
  });

  const noteY = y + 30;
  const noteW = doc.page.width - 2 * PAGE_MARGIN;
  const scope = `This report records the business verification of ${
    cleanText(data.entityName) || 'this entity'
  }: the result of each of the seven checks, the evidence each one returned, and the record details they were run against.`;
  const scopeH = doc
    .font(FONT_REGULAR)
    .fontSize(10)
    .heightOfString(scope, { width: noteW - 32, lineGap: 2 });
  doc
    .roundedRect(PAGE_MARGIN, noteY, noteW, scopeH + 50, 5)
    .fillAndStroke(COLOR.cardBg, COLOR.cardBorder);
  doc
    .font(FONT_BOLD)
    .fontSize(10)
    .fillColor(COLOR.primary)
    .text('REPORT SCOPE', PAGE_MARGIN + 16, noteY + 14, {
      characterSpacing: 0.6,
    });
  doc
    .font(FONT_REGULAR)
    .fontSize(10)
    .fillColor(COLOR.textSecondary)
    .text(scope, PAGE_MARGIN + 16, noteY + 34, {
      width: noteW - 32,
      lineGap: 2,
    });
}

function renderBusinessVerificationSummary(
  doc: any,
  data: IBusinessVerificationReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '01', 'Verification Summary');

  renderKpiCards(doc, [
    {
      value: cleanText(data.figure),
      label: 'RESULT',
      valueColor: bvToneColor(data.figureTone),
    },
    {
      value: String(data.counts.passed),
      label: 'PASSED',
      valueColor: COLOR.riskLow,
    },
    {
      value: String(data.counts.attention),
      label: 'NEEDS ATTENTION',
      valueColor: data.counts.attention ? BV_AMBER_TEXT : COLOR.textMuted,
    },
    {
      value: String(data.counts.failed),
      label: 'FAILED',
      valueColor: data.counts.failed ? COLOR.riskHigh : COLOR.textMuted,
    },
  ]);

  if (data.summary) {
    renderPaginatedLabeledBlock(doc, 'SUMMARY', cleanText(data.summary));
  }
  if (data.runNotes && data.runNotes.length) {
    renderPaginatedLabeledBlock(
      doc,
      'THIS RUN',
      data.runNotes.map(cleanText).join('\n'),
    );
  }

  doc
    .font(FONT_BOLD)
    .fontSize(9)
    .fillColor(COLOR.textSecondary)
    .text('THE SEVEN CHECKS', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
  doc.moveDown(0.4);
  renderTable(
    doc,
    [
      { header: 'Check', width: 1.4 },
      { header: 'Result', width: 1 },
      { header: 'Finding', width: 3 },
      { header: 'Last checked', width: 1 },
    ],
    data.checks.map((check) => [
      cleanText(check.label),
      cleanText(check.statusLabel),
      cleanText(check.description),
      cleanText(check.checkedOn) || '-',
    ]),
    (rowIndex, colIndex) =>
      colIndex === 1 ? bvToneColor(data.checks[rowIndex].tone) : undefined,
  );
}

function renderBusinessVerificationCheckDetails(
  doc: any,
  data: IBusinessVerificationReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '02', 'Check Details');

  const usable = doc.page.width - 2 * PAGE_MARGIN;
  const pageBottom = doc.page.height - 60;
  const tableHeight = (rows: number) => (rows ? 22 + rows * 24 + 30 : 0);
  data.checks.forEach((check, index) => {
    // Keep a check on one page when it fits on one, so a table is not split
    // from its heading; a check taller than a page flows as normal.
    const textHeight = doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .heightOfString(`${cleanText(check.description)}
${cleanText(check.next)}`, {
        width: usable,
        lineGap: 2,
      });
    const estimate =
      90 +
      textHeight +
      tableHeight(check.reads.length) +
      tableHeight(check.evidence.length);
    const fitsOnAPage = estimate < pageBottom - 70;
    if (
      (fitsOnAPage && doc.y + estimate > pageBottom) ||
      doc.y > doc.page.height - 200
    ) {
      doc.addPage();
      doc.y = 70;
    }
    if (index > 0 && doc.y > 80) {
      doc
        .strokeColor(COLOR.divider)
        .lineWidth(0.6)
        .moveTo(PAGE_MARGIN, doc.y)
        .lineTo(PAGE_MARGIN + usable, doc.y)
        .stroke();
      doc.y += 14;
    }

    const headY = doc.y;
    const status = cleanText(check.statusLabel).toUpperCase();
    const statusW = doc
      .font(FONT_BOLD)
      .fontSize(8.5)
      .widthOfString(status, { characterSpacing: 0.6 });
    doc
      .font(FONT_BOLD)
      .fontSize(8.5)
      .fillColor(bvToneColor(check.tone))
      .text(status, PAGE_MARGIN + usable - statusW, headY + 3, {
        characterSpacing: 0.6,
        lineBreak: false,
      });
    doc
      .font(FONT_BOLD)
      .fontSize(12.5)
      .fillColor(COLOR.textPrimary)
      .text(cleanText(check.label), PAGE_MARGIN, headY, {
        width: usable - statusW - 16,
      });
    doc.moveDown(0.15);
    renderPaginatedBodyText(doc, cleanText(check.description), {
      fontSize: 10,
      color: COLOR.textSecondary,
    });
    doc.moveDown(0.5);

    renderPaginatedLabeledBlock(
      doc,
      cleanText(check.nextTitle).toUpperCase(),
      cleanText(check.next),
    );

    if (check.reads.length) {
      doc
        .font(FONT_BOLD)
        .fontSize(9)
        .fillColor(COLOR.textSecondary)
        .text('READ FROM THIS RECORD', PAGE_MARGIN, doc.y, {
          characterSpacing: 0.6,
        });
      doc.moveDown(0.3);
      renderTable(
        doc,
        [
          { header: 'Field', width: 1.3 },
          { header: 'Value on the record', width: 3 },
        ],
        check.reads.map((read) => [
          cleanText(read.label),
          cleanText(read.value),
        ]),
        (rowIndex, colIndex) =>
          colIndex === 1 && check.reads[rowIndex].value === 'Not set'
            ? COLOR.textMuted
            : undefined,
      );
      doc.moveDown(0.4);
    }

    if (check.evidence.length) {
      doc
        .font(FONT_BOLD)
        .fontSize(9)
        .fillColor(COLOR.textSecondary)
        .text('EVIDENCE', PAGE_MARGIN, doc.y, { characterSpacing: 0.6 });
      doc.moveDown(0.3);
      renderTable(
        doc,
        [
          { header: 'Measure', width: 1.3 },
          { header: 'Returned', width: 3 },
        ],
        check.evidence.map((item) => [
          cleanText(item.label),
          cleanText(item.value),
        ]),
      );
      doc.moveDown(0.4);
    }

    doc
      .font(FONT_REGULAR)
      .fontSize(8.5)
      .fillColor(COLOR.textMuted)
      .text(
        check.checkedAt
          ? `Last checked ${cleanText(check.checkedAt)}`
          : 'Not checked yet',
        PAGE_MARGIN,
        doc.y,
      );
    doc.moveDown(1);
  });
}

function renderBusinessVerificationRecord(
  doc: any,
  data: IBusinessVerificationReportData,
): void {
  doc.addPage();
  doc.y = 70;
  renderSectionHeader(doc, '03', 'Record Details');
  renderPaginatedBodyText(
    doc,
    'Every check runs against these fields on the record. A field that is not set blocks the checks that need it.',
    { fontSize: 10, color: COLOR.textSecondary },
  );
  doc.moveDown(0.8);
  renderTable(
    doc,
    [
      { header: 'Field', width: 1.4 },
      { header: 'Value', width: 2.6 },
      { header: 'Used by', width: 1.4 },
    ],
    data.fields.map((field) => [
      cleanText(field.label),
      cleanText(field.value),
      cleanText(field.usedBy),
    ]),
    (rowIndex, colIndex) =>
      colIndex === 1 && data.fields[rowIndex].value === 'Not set'
        ? COLOR.textMuted
        : undefined,
  );
}

export const businessVerificationReportPDF = async (
  data: IBusinessVerificationReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE Business Verification Report — ${data.entityName || ''}`,
          Author: 'BITE Business Verification',
          Subject: 'Business Verification Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('error', reject);
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · BUSINESS VERIFICATION',
        headerRight: 'Verification Report',
        footerFirstPage: 'Business Verification Report',
      });

      renderBusinessVerificationCover(doc, data, logo);
      renderBusinessVerificationSummary(doc, data);
      renderBusinessVerificationCheckDetails(doc, data);
      renderBusinessVerificationRecord(doc, data);

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};

// ---------------------------------------------------------------------------
// AES Analyzer findings report
// ---------------------------------------------------------------------------

const aesSeverityColor = (severity: AesFinding['severity']): string => {
  if (severity === 'critical' || severity === 'high') return COLOR.riskHigh;
  if (severity === 'medium') return COLOR.riskMedium;
  return COLOR.textSecondary;
};

// Inlined rather than importing aesFindingSignature from api/AesAnalyzerAPI:
// that module pulls in axios (and transitively keycloak-js) at runtime, both
// ESM-only packages jest's default CRA transform can't parse, which broke
// every other test that imports this file directly. Must stay in sync with
// aesFindingSignature's `${f.rule}|${f.message}` formula.
const aesFindingSignature = (f: { rule: string; message: string }): string =>
  `${f.rule}|${f.message}`;

// Mirrors AesRunDetailsPage.tsx's activeFindingsOf: every finding is shown,
// annotated as cleared rather than dropped, so a printed audit never implies
// fewer findings were raised than the run actually produced.
const aesLineFindingsText = (line: AesLine): string => {
  const clearedSignatures = new Set(
    (line.cleared_flags || []).map((c) => c.signature),
  );
  const items = (line.findings || []).map((f) => {
    const cleared =
      !!f.autoCleared || clearedSignatures.has(aesFindingSignature(f));
    return `${f.severity.toUpperCase()} · ${cleanText(f.flag)}${
      cleared ? ' (cleared)' : ''
    }`;
  });
  return items.length ? items.join('\n') : '—';
};

function renderAesCover(
  doc: any,
  data: IAesTicketReportData,
  logo: ArrayBuffer | string | null,
): void {
  doc.save();
  doc.rect(0, 0, 18, doc.page.height).fill(COLOR.primary);
  doc.restore();

  if (logo) {
    try {
      doc.image(logo, PAGE_MARGIN, 48, { width: 150 });
    } catch {
      /* ignore logo failures */
    }
  }

  const titleY = 230;
  const titleWidth = doc.page.width - 2 * PAGE_MARGIN;
  const titleHeight = doc
    .font(FONT_BOLD)
    .fontSize(38)
    .heightOfString('BITE AES Analyzer Report', {
      width: titleWidth,
      lineGap: 2,
    });
  doc
    .font(FONT_BOLD)
    .fontSize(38)
    .fillColor(COLOR.textPrimary)
    .text('BITE AES Analyzer Report', PAGE_MARGIN, titleY, {
      width: titleWidth,
      lineGap: 2,
    });

  const { run } = data;
  const metaY = titleY + Math.max(titleHeight, 46) + 40;
  const labelX = PAGE_MARGIN;
  const valueX = PAGE_MARGIN + 130;
  const valueW = doc.page.width - PAGE_MARGIN - valueX;
  const minRowH = 26;
  const meta: Array<[string, string]> = [
    ['RUN', cleanText(run.name) || '—'],
    ['FILE', cleanText(run.original_file_name) || '—'],
    ['GENERATED', fmtDateTimeLong(new Date())],
    ['PREPARED BY', cleanText(data.preparedBy) || 'BITE AES Analyzer'],
  ];

  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.6)
    .moveTo(labelX, metaY - 6)
    .lineTo(doc.page.width - PAGE_MARGIN, metaY - 6)
    .stroke();

  let y = metaY;
  meta.forEach((pair) => {
    const valueHeight = doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .heightOfString(pair[1], { width: valueW, lineGap: 2 });
    const rowH = Math.max(minRowH, valueHeight + 14);
    doc
      .font(FONT_BOLD)
      .fontSize(9)
      .fillColor(COLOR.textSecondary)
      .text(pair[0], labelX, y + 6, { characterSpacing: 0.6 });
    doc
      .font(FONT_REGULAR)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text(pair[1], valueX, y + 5, { width: valueW, lineGap: 2 });
    doc
      .strokeColor(COLOR.divider)
      .lineWidth(0.4)
      .moveTo(labelX, y + rowH - 4)
      .lineTo(doc.page.width - PAGE_MARGIN, y + rowH - 4)
      .stroke();
    y += rowH;
  });

  const accentY = y + 18;
  doc
    .strokeColor(COLOR.primary)
    .lineWidth(2.5)
    .moveTo(PAGE_MARGIN, accentY)
    .lineTo(PAGE_MARGIN + 60, accentY)
    .stroke();
  doc
    .font(FONT_REGULAR)
    .fontSize(11)
    .fillColor(COLOR.textPrimary)
    .text(
      'A record of the commodity lines, flagged findings, and coverage for this AES export-compliance audit.',
      PAGE_MARGIN,
      accentY + 18,
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );

  renderFirstPageDisclaimer(doc, AES_DISCLAIMER, undefined, doc.y);
}

function renderAesSummary(doc: any, data: IAesTicketReportData): void {
  // Right after the cover: continue on a page its disclaimer moved to.
  continueOrAddPage(doc);
  renderSectionHeader(doc, '01', 'Audit Summary');

  const { run } = data;
  renderKpiCards(doc, [
    { value: fmtCount(run.line_count), label: 'COMMODITY LINES' },
    {
      value: fmtCount(run.flagged_line_count),
      label: 'FLAGGED LINES',
      valueColor: COLOR.riskMedium,
    },
    {
      value: fmtCount(run.critical_count),
      label: 'CRITICAL LINES',
      valueColor: COLOR.riskHigh,
    },
    { value: formatCompactMoney(run.total_value), label: 'TOTAL VALUE' },
  ]);

  const notes = [...(run.upload_notes || []), ...(run.audit_notes || [])];
  if (notes.length) {
    doc
      .font(FONT_BOLD)
      .fontSize(11)
      .fillColor(COLOR.textPrimary)
      .text('About this file', PAGE_MARGIN, doc.y);
    doc.moveDown(0.3);
    renderOrderedList(doc, notes.map(cleanText));
  }

  const coverageGaps = (run.coverage || []).filter(
    (c) => c.status !== 'evaluated',
  );
  if (coverageGaps.length) {
    const ruleById = Object.fromEntries(
      (run.rules || []).map((r) => [r.id, r]),
    );
    doc.moveDown(0.3);
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text(
        `${coverageGaps.length} of ${run.coverage.length} checks could not fully run on this file:`,
        PAGE_MARGIN,
        doc.y,
        { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
      );
    doc.moveDown(0.3);
    renderOrderedList(
      doc,
      coverageGaps.map((c) => {
        const flag = cleanText(ruleById[c.rule]?.flag) || 'Check';
        const status = c.status === 'partial' ? 'partial' : 'not run';
        const reason = c.reason ? ` — ${cleanText(c.reason)}` : '';
        return `${flag} (${status})${reason}`;
      }),
    );
  }
}

// Critical first, then High/Medium/Low/Informational, clean lines (no
// findings, max_severity null) last. `Array.prototype.sort` is stable, and
// the lines already arrive in row_number order (fetchAllLinesForReport sorts
// by row_number), so lines sharing a severity keep their upload order.
const AES_SEVERITY_SORT_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  informational: 4,
};

const sortAesLinesBySeverity = (lines: AesLine[]): AesLine[] =>
  [...lines].sort((a, b) => {
    const rankA = a.max_severity ? AES_SEVERITY_SORT_RANK[a.max_severity] ?? 5 : 5;
    const rankB = b.max_severity ? AES_SEVERITY_SORT_RANK[b.max_severity] ?? 5 : 5;
    return rankA - rankB;
  });

function renderAesLinesTable(doc: any, data: IAesTicketReportData): void {
  // Landscape: 9 columns (including the free-text Consignee/Findings ones)
  // don't fit a portrait page without truncating headers.
  doc.addPage({ size: 'LETTER', layout: 'landscape' });
  doc.y = 70;
  renderSectionHeader(doc, '02', 'Commodity Lines');

  doc
    .font(FONT_REGULAR)
    .fontSize(10)
    .fillColor(COLOR.textPrimary)
    .text(
      'Every uploaded commodity line with its findings, most severe first.',
      { width: doc.page.width - 2 * PAGE_MARGIN, lineGap: 2 },
    );
  doc.moveDown(0.8);

  if (!data.lines.length) {
    doc
      .font(FONT_REGULAR)
      .fontSize(10)
      .fillColor(COLOR.textSecondary)
      .text('No lines were saved with this run.', PAGE_MARGIN, doc.y);
    return;
  }

  const sortedLines = sortAesLinesBySeverity(data.lines);

  // Column widths are sized so every header label fits on one line at the
  // header font (9pt bold): drawHeaderRow's text() call still wraps to a
  // second line when the label is wider than its column despite
  // `lineBreak: false` (pdfkit wraps on an overflowing width regardless of
  // that option), and the fixed 22px header row height then lets that
  // second line bleed into the first data row instead of showing it —
  // which is what made "Row" render as "Ro" and "ECCN / USML" render as
  // just "ECCN /". Verified against the actual font metrics: every header
  // below now has several points of margin to spare in its column.
  const cols: Col[] = [
    { header: 'Row', width: 0.65, align: 'right', noWrap: true },
    { header: 'Shipment', width: 1 },
    { header: 'Consignee', width: 1.2 },
    { header: 'Dest.', width: 0.7, noWrap: true },
    { header: 'License', width: 1 },
    { header: 'ECCN / USML', width: 1.3 },
    // Fixed-format codes/amounts: noWrap so they never split mid-number, and
    // wide enough on their own that normal values never need the ellipsis
    // fallback either.
    { header: 'Schedule B', width: 1.3, noWrap: true },
    { header: 'Value', width: 1.1, align: 'right', noWrap: true },
    { header: 'Findings', width: 1.85 },
  ];

  const rows = sortedLines.map((line) => {
    const eccnUsml =
      [
        line.eccn,
        line.usml_category ? `USML ${usmlDisplay(line.usml_category)}` : null,
      ]
        .filter(Boolean)
        .join(' / ') || '—';
    const license =
      [line.license_type, line.license_number].filter(Boolean).join(' / ') ||
      '—';
    return [
      line.row_number,
      cleanText(line.shipment_reference || line.itn) || '—',
      cleanText(line.consignee_name) || '—',
      cleanText(line.destination_country) || '—',
      license,
      eccnUsml,
      cleanText(line.hts) || '—',
      formatReportMoney(line.line_value),
      aesLineFindingsText(line),
    ];
  });

  renderTable(
    doc,
    cols,
    rows,
    (rowIndex, colIndex) => {
      if (colIndex !== 8) return undefined;
      const severity = sortedLines[rowIndex]?.max_severity;
      return severity ? aesSeverityColor(severity) : undefined;
    },
    { size: 'LETTER', layout: 'landscape' },
  );
}

export const aesAnalyzerReportPDF = async (
  data: IAesTicketReportData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLogoBuffer(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'LETTER',
        margins: {
          top: PAGE_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: 50,
        },
        info: {
          Title: `BITE AES Analyzer Report — ${data.run.name || ''}`,
          Author: 'BITE AES Analyzer',
          Subject: 'AES Analyzer Findings Report',
        },
        bufferPages: true,
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('error', reject);
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      registerFonts(doc, regularFont, boldFont);
      attachChrome(doc, {
        header: 'BITE · AES ANALYZER',
        footerFirstPage: 'AES Analyzer Report',
      });

      renderAesCover(doc, data, logo);
      renderAesSummary(doc, data);
      renderAesLinesTable(doc, data);

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};
